import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase, isConfigured } from '../lib/supabase';
import { db } from '../lib/db';
import { normalizePermissions, initialsFromName } from '../lib/permissions';
import { teamAlias, teamAvatarEmoji } from '../lib/teamDisplay';
import { onlyOperationalRows, soloOperacionReal, DEMO_IDS } from '../lib/operationalData';
import { recoverReal, separarGastos, ventasReales } from '../features/finanzas/lib/finanzasCanonicas';
import { updatePorId, borrarPorId, SIN_FILAS_CODE } from '../lib/updateVerificado';
import { defaultApplicationsForPackage, applicationById, calculateProjectTimeline } from '../lib/packages';
import { precioCanonicoONull, registrarCatalogoCanonico } from '../lib/precioDePaquete';
import { cupoCanonicoONull } from '../lib/cupoDePaquete';
import { advanceProductionStatus, resolveProductionStatus } from '../lib/productionStatus';
import { APPROVAL_ACT_STATEMENT } from '../features/client-portal/lib/approvalActStatement';
import { useAuth } from './AuthContext';
import { archiveStorage, isFinancialArchiveDocument, isLegalArchiveDocument, isSensitiveArchiveDocument, normalizeArchiveConfig } from '../lib/archiveStorage';
import { createInternalNotificationPayload, isNotificationPending, normalizeNotification } from '../lib/internalNotifications';
import { inheritedMarketingAttribution, mergeMissingMarketingAttribution } from '../lib/marketingAttributionFields';
import { filterLandingAssetPayload, normalizeLandingAsset } from '../lib/landingAssets';
import { normalizeStudioNewsRow, buildStudioNewsPayload, seedStudioNewsItems, removeStudioNewsObjects } from '../lib/studioNews';
import { normalizePortalAsset } from '../lib/portalAssets';
import { mergeBriefSubmission } from '../lib/briefResponses';
import { dispatchCriticalCommunication } from '../lib/communications';
import { claveDeAvisoDeAvance, decidirAvisoDeAvance, PLANTILLA_AVISO_DE_AVANCE } from '../lib/avisoDeAvance';
import { PORTAL_URL } from '../lib/appUrls';
import { diagnosticoDeCotizacionDelCobro, AVISO_COTIZACION_NO_VISIBLE, avanzarCotizacionPorCobro, avisoDeAvanceDeCotizacion } from '../lib/cobroCotizacion';
import { getCrmPhaseIndex, getCrmTransitionTrigger } from '../lib/crmStateMachine';
import { portalErrorMessage } from '../features/client-portal/lib/portalErrorCopy';
import { buildCrmTransitionPlan, callAdvanceClientPhaseRpc } from '../lib/crmTransitions';
import { CONTRACT_STALE_ADMIN_MESSAGE, isStaleContractLegalContent } from '../lib/contractContentIntegrity';
import { DEFAULT_ACCENT_COLOR, getAccentColorTokens } from '../lib/themeAccent';
import { precioDeLaFila, precioDeExtraParaPersistir } from '../lib/precioDeAplicacion.js';

const BRANDKIT_ASSET_COLUMNS = new Set(['id', 'cliente_id', 'proyecto_id', 'asset_type', 'title', 'description', 'storage_bucket', 'storage_path', 'mime_type', 'file_size', 'status', 'uploaded_by', 'published_at', 'access_grant_count', 'last_access_granted_at', 'delivered_at', 'created_at', 'updated_at']);
const filterBrandkitAssetPayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key]) => BRANDKIT_ASSET_COLUMNS.has(key)));
const PROJECT_UPDATE_COLUMNS = new Set(['id', 'proyecto_id', 'tipo', 'titulo', 'descripcion', 'archivo_url', 'attachment_bucket', 'attachment_path', 'attachment_name', 'attachment_type', 'attachment_size', 'reviewer_id', 'file_type', 'version_label', 'application_id', 'status', 'feedback', 'organized_feedback', 'created_by', 'reviewed_by', 'reviewed_at', 'published_to_client', 'published_at', 'published_by', 'presentation_version', 'client_visible_status', 'client_decision_status', 'client_decision_at', 'is_demo', 'created_at', 'updated_at']);
const filterProjectUpdatePayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => PROJECT_UPDATE_COLUMNS.has(key) && value !== undefined));
const INGRESO_COLUMNS = new Set(['id', 'estudio_id', 'proyecto_id', 'cliente_id', 'cobro_id', 'cliente_nombre', 'pais', 'mes', 'tipo_servicio', 'origen_ingreso', 'ingreso_bruto', 'incluye_iva', 'iva_monto', 'primer_pago_pct', 'primer_pago_monto', 'segundo_pago_monto', 'cobros_registrados', 'estado_cobro', 'retencion_irf_pct', 'retencion_irf_monto', 'retencion_iva_pct', 'retencion_iva_monto', 'comision_tarjeta', 'canal_cobro', 'ingreso_neto', 'marketing_source_id', 'marketing_campaign_id', 'marketing_attribution_confidence', 'marketing_attribution_notes', 'created_at', 'updated_at', 'created_by']);
const filterIngresoPayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => INGRESO_COLUMNS.has(key) && value !== undefined));
const SUSCRIPCION_COLUMNS = new Set(['id', 'estudio_id', 'herramienta', 'categoria', 'costo_usd', 'ciclo', 'costo_mensual_equiv', 'entidad_pagadora', 'estado', 'fecha_renovacion', 'notas', 'created_at', 'updated_at', 'created_by']);
const filterSuscripcionPayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => SUSCRIPCION_COLUMNS.has(key) && value !== undefined));
const GASTO_COLUMNS = new Set(['id', 'nombre', 'cat', 'monto', 'fecha', 'status', 'via', 'responsable', 'cliente_id', 'tipo_egreso', 'deuda_id', 'suscripcion_id', 'capital_pago', 'interes_pago', 'created_at', 'updated_at']);
const filterGastoPayload = (payload = {}) => {
  const normalized = { ...payload };
  if (normalized.clienteId && !normalized.cliente_id) normalized.cliente_id = normalized.clienteId;
  delete normalized.clienteId;
  if (normalized.status === 'paid') normalized.status = 'pagado';
  if (normalized.status === 'pending') normalized.status = 'pendiente';
  if (normalized.status === 'recurring') normalized.status = 'recurrente';
  if (normalized.id && !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(normalized.id))) {
    delete normalized.id;
  }
  return Object.fromEntries(Object.entries(normalized).filter(([key, value]) => GASTO_COLUMNS.has(key) && value !== undefined));
};
const GASTO_FIJO_COLUMNS = new Set(['id', 'estudio_id', 'concepto', 'categoria', 'entidad', 'monto_mensual', 'costo_hora', 'activo', 'notas', 'created_at', 'updated_at', 'created_by']);
const filterGastoFijoPayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => GASTO_FIJO_COLUMNS.has(key) && value !== undefined));
const FLUJO_CONFIG_COLUMNS = new Set(['id', 'estudio_id', 'efectivo_inicial_usa', 'efectivo_inicial_ecuador', 'mes_inicio', 'activo', 'created_at', 'updated_at', 'created_by']);
const filterFlujoConfigPayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => FLUJO_CONFIG_COLUMNS.has(key) && value !== undefined));
const MARKETING_SOURCE_TYPES = new Set(['organic', 'paid', 'referral', 'direct', 'partner', 'event', 'social', 'website', 'whatsapp', 'email', 'other']);
const MARKETING_CAMPAIGN_TYPES = new Set(['awareness', 'lead_generation', 'conversion', 'retargeting', 'content', 'referral', 'organic']);
const MARKETING_CAMPAIGN_STATUSES = new Set(['draft', 'active', 'paused', 'completed', 'archived']);
const MARKETING_SOURCE_COLUMNS = new Set(['id', 'estudio_id', 'name', 'slug', 'type', 'platform', 'description', 'active', 'created_at', 'updated_at', 'created_by']);
const MARKETING_CAMPAIGN_COLUMNS = new Set(['id', 'estudio_id', 'source_id', 'name', 'slug', 'platform', 'campaign_type', 'status', 'start_date', 'end_date', 'budget', 'currency', 'meta_campaign_id', 'external_campaign_id', 'notes', 'created_at', 'updated_at', 'created_by']);
const filterMarketingSourcePayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => MARKETING_SOURCE_COLUMNS.has(key) && value !== undefined));
const filterMarketingCampaignPayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => MARKETING_CAMPAIGN_COLUMNS.has(key) && value !== undefined));
const SUPPORT_TICKET_COLUMNS = new Set(['id', 'cliente_id', 'proyecto_id', 'created_by', 'assigned_to', 'title', 'description', 'category', 'priority', 'status', 'source', 'resolution_summary', 'resolved_at', 'closed_at', 'created_at', 'updated_at']);
const SUPPORT_TICKET_MESSAGE_COLUMNS = new Set(['id', 'ticket_id', 'author_id', 'author_role', 'message', 'is_internal', 'attachment_bucket', 'attachment_path', 'attachment_name', 'attachment_type', 'attachment_size', 'created_at']);
const filterSupportTicketPayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => SUPPORT_TICKET_COLUMNS.has(key) && value !== undefined));
const filterSupportTicketMessagePayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => SUPPORT_TICKET_MESSAGE_COLUMNS.has(key) && value !== undefined));
const CLIENT_SATISFACTION_SURVEY_COLUMNS = new Set(['id', 'cliente_id', 'proyecto_id', 'score', 'nps_score', 'feedback', 'would_recommend', 'status', 'sent_at', 'completed_at', 'created_at', 'updated_at']);
const TESTIMONIAL_REQUEST_COLUMNS = new Set(['id', 'cliente_id', 'proyecto_id', 'status', 'testimonial_text', 'public_name', 'permission_to_publish', 'requested_at', 'received_at', 'approved_at', 'published_at', 'created_at', 'updated_at']);
const filterClientSatisfactionSurveyPayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => CLIENT_SATISFACTION_SURVEY_COLUMNS.has(key) && value !== undefined));
const filterTestimonialRequestPayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => TESTIMONIAL_REQUEST_COLUMNS.has(key) && value !== undefined));
const CONTRACT_PARTICIPANT_COLUMNS = new Set(['id', 'contrato_id', 'cliente_id', 'actor_type', 'portal_member_id', 'name', 'email', 'role', 'required', 'sign_order', 'status', 'created_at', 'updated_at']);
const BRIEF_PARTICIPATION_COLUMNS = new Set(['id', 'brief_id', 'cliente_id', 'quote_id', 'proyecto_id', 'actor_type', 'portal_member_id', 'name', 'email', 'role', 'required', 'status', 'completed_at', 'last_activity_at', 'internal_note', 'created_at', 'updated_at']);
const filterContractParticipantPayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => CONTRACT_PARTICIPANT_COLUMNS.has(key) && value !== undefined));
const filterBriefParticipationPayload = (payload = {}) => Object.fromEntries(Object.entries(payload).filter(([key, value]) => BRIEF_PARTICIPATION_COLUMNS.has(key) && value !== undefined));
const LEAD_COLUMNS = new Set(['id', 'nombre', 'email', 'brand', 'status', 'dias', 'created_at', 'is_demo', 'meta_lead_id', 'meta_form_id', 'meta_campaign_name', 'meta_adset_name', 'meta_ad_name', 'source_payload', 'synced_from', 'synced_at', 'source_id', 'campaign_id', 'marketing_source_id', 'marketing_campaign_id', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'attribution_confidence', 'attribution_notes']);
const LEAD_PAYLOAD_ONLY_FIELDS = ['telefono', 'fuente', 'tipo', 'presupuesto', 'notas'];
const nullableUuid = value => {
  const text = String(value || '').trim();
  return text ? text : null;
};

function parseObjectPayload(value) {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
      return {};
    }
  }
  return {};
}

function logLeadMarketingSchemaAudit(input = {}, context = 'lead_write') {
  const hasSourceField = Object.prototype.hasOwnProperty.call(input, 'marketing_source_id');
  const hasCampaignField = Object.prototype.hasOwnProperty.call(input, 'marketing_campaign_id');
  if (context !== 'lead_create' && !hasSourceField && !hasCampaignField) return;
  if (!hasSourceField || !hasCampaignField || input.marketing_source_id == null || input.marketing_campaign_id == null) {
    console.warn('[AppContext.leads] Auditoria de atribucion de marketing', {
      context,
      has_marketing_source_id_field: hasSourceField,
      has_marketing_campaign_id_field: hasCampaignField,
      marketing_source_id_is_null: input.marketing_source_id == null,
      marketing_campaign_id_is_null: input.marketing_campaign_id == null,
      checked_at: new Date().toISOString(),
    });
  }
}

function normalizeLeadDbPayload(input = {}, { legacyOnly = false } = {}) {
  const row = { ...input };
  const sourcePayload = parseObjectPayload(row.source_payload);

  LEAD_PAYLOAD_ONLY_FIELDS.forEach(field => {
    if (row[field] !== undefined && row[field] !== null && row[field] !== '') {
      sourcePayload[field] = row[field];
    }
    delete row[field];
  });

  if (row.marketing_source_id && !row.source_id) row.source_id = row.marketing_source_id;
  if (row.marketing_campaign_id && !row.campaign_id) row.campaign_id = row.marketing_campaign_id;
  if (row.source_id && !row.marketing_source_id) row.marketing_source_id = row.source_id;
  if (row.campaign_id && !row.marketing_campaign_id) row.marketing_campaign_id = row.campaign_id;

  if (legacyOnly) {
    delete row.marketing_source_id;
    delete row.marketing_campaign_id;
  }

  if (Object.keys(sourcePayload).length) row.source_payload = sourcePayload;

  return Object.fromEntries(Object.entries(row).filter(([key, value]) => LEAD_COLUMNS.has(key) && value !== undefined));
}

function isMissingLeadMarketingSchemaColumn(error) {
  const message = String(error?.message || error || '');
  return /marketing_source_id|marketing_campaign_id/i.test(message)
    && /(schema cache|column|could not find|not found)/i.test(message);
}

const getRowId = row => (row?.id == null ? '' : String(row.id));
const getCreatedTime = row => {
  const time = new Date(row?.created_at || 0).getTime();
  return Number.isFinite(time) ? time : 0;
};
const CONTRACT_SIGNING_DEBUG_STORAGE_KEY = 'feria_contract_signing_debug';
function contractSigningDiagnosticsEnabled() {
  const isDev = process.env.NODE_ENV !== 'production';
  if (isDev) return true;
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search || '');
    return params.has('codex_qa') ||
      params.get('contract_signing_debug') === '1' ||
      window.localStorage.getItem(CONTRACT_SIGNING_DEBUG_STORAGE_KEY) === 'true';
  } catch (_) {
    return false;
  }
}
function logContractSigningDiagnostic(event, details = {}) {
  if (!contractSigningDiagnosticsEnabled()) return;
  const safeDetails = Object.fromEntries(
    Object.entries(details || {}).filter(([, value]) => value !== undefined)
  );
  console.info('[contract-signing-diagnostics]', event, safeDetails);
}
const sortNewestFirst = rows => [...(rows || [])].sort((a, b) => getCreatedTime(b) - getCreatedTime(a));
const sortOldestFirst = rows => [...(rows || [])].sort((a, b) => getCreatedTime(a) - getCreatedTime(b));
const upsertRowById = (rows = [], next, { position = 'end', sort = null } = {}) => {
  if (!next?.id) return rows || [];
  const nextId = getRowId(next);
  const current = rows || [];
  const exists = current.some(row => getRowId(row) === nextId);
  const merged = exists
    ? current.map(row => (getRowId(row) === nextId ? { ...row, ...next } : row))
    : position === 'start'
      ? [next, ...current]
      : [...current, next];
  return typeof sort === 'function' ? sort(merged) : merged;
};
const removeRowById = (rows = [], id) => {
  if (!id) return rows || [];
  const targetId = String(id);
  return (rows || []).filter(row => getRowId(row) !== targetId);
};

function calculateIngresoLocally(row = {}) {
  const ingresoBruto = normalizeMoney(row.ingreso_bruto, 0);
  const primerPagoPct = normalizeMoney(row.primer_pago_pct, 60);
  const comisionTarjeta = normalizeMoney(row.comision_tarjeta, 0);
  const origenIngreso = row.origen_ingreso === 'internacional' ? 'internacional' : 'nacional';
  const primerPagoMonto = Math.round(ingresoBruto * (primerPagoPct / 100) * 100) / 100;
  const segundoPagoMonto = Math.round((ingresoBruto - primerPagoMonto) * 100) / 100;

  if (origenIngreso === 'internacional') {
    return {
      ...row,
      origen_ingreso: origenIngreso,
      ingreso_bruto: ingresoBruto,
      incluye_iva: false,
      iva_monto: 0,
      primer_pago_pct: primerPagoPct,
      primer_pago_monto: primerPagoMonto,
      segundo_pago_monto: segundoPagoMonto,
      retencion_irf_pct: 0,
      retencion_irf_monto: 0,
      retencion_iva_pct: 0,
      retencion_iva_monto: 0,
      comision_tarjeta: comisionTarjeta,
      ingreso_neto: Math.round((ingresoBruto - comisionTarjeta) * 100) / 100,
    };
  }

  const ivaMonto = row.incluye_iva ? Math.round(ingresoBruto * 0.15 * 100) / 100 : 0;
  const retencionIrfPct = normalizeMoney(row.retencion_irf_pct, 0);
  const retencionIvaPct = normalizeMoney(row.retencion_iva_pct, 0);
  const retencionIrfMonto = Math.round(ingresoBruto * retencionIrfPct * 100) / 100;
  const retencionIvaMonto = Math.round(ivaMonto * retencionIvaPct * 100) / 100;

  return {
    ...row,
    origen_ingreso: origenIngreso,
    ingreso_bruto: ingresoBruto,
    iva_monto: ivaMonto,
    primer_pago_pct: primerPagoPct,
    primer_pago_monto: primerPagoMonto,
    segundo_pago_monto: segundoPagoMonto,
    retencion_irf_pct: retencionIrfPct,
    retencion_irf_monto: retencionIrfMonto,
    retencion_iva_pct: retencionIvaPct,
    retencion_iva_monto: retencionIvaMonto,
    comision_tarjeta: comisionTarjeta,
    ingreso_neto: Math.round((ingresoBruto - retencionIrfMonto - retencionIvaMonto - comisionTarjeta) * 100) / 100,
  };
}

function calculateSuscripcionLocally(row = {}) {
  const costo = normalizeMoney(row.costo_usd, 0);
  const ciclo = row.ciclo === 'anual' ? 'anual' : 'mensual';
  return {
    ...row,
    herramienta: String(row.herramienta || '').trim(),
    categoria: row.categoria || '',
    costo_usd: costo,
    ciclo,
    costo_mensual_equiv: Math.round((ciclo === 'anual' ? costo / 12 : costo) * 100) / 100,
    entidad_pagadora: row.entidad_pagadora === 'llc_usa' ? 'llc_usa' : 'ecuador',
    estado: ['activo', 'cancelado', 'pendiente', 'pausado'].includes(row.estado) ? row.estado : 'activo',
    fecha_renovacion: row.fecha_renovacion || null,
    notas: row.notas || '',
  };
}

function buildSubscriptionExpensePayload(suscripcion = {}, existing = {}) {
  const monthly = normalizeMoney(suscripcion.costo_mensual_equiv, normalizeMoney(suscripcion.costo_usd, 0));
  const tool = String(suscripcion.herramienta || existing.nombre || 'Suscripcion digital').trim();
  return {
    ...existing,
    nombre: tool,
    cat: 'Software / Suscripciones',
    monto: monthly,
    fecha: existing.fecha || new Date().toISOString().slice(0, 10),
    status: 'recurrente',
    via: existing.via || (suscripcion.entidad_pagadora === 'llc_usa' ? 'LLC USA' : 'Ecuador'),
    tipo_egreso: 'suscripcion',
    suscripcion_id: suscripcion.id,
    updated_at: new Date().toISOString(),
  };
}

function findSubscriptionExpense(rows = [], suscripcion = {}) {
  if (!suscripcion?.id) return null;
  const linked = rows.find(row => String(row.suscripcion_id || '') === String(suscripcion.id));
  if (linked) return linked;

  const tool = normalizeFinanceText(suscripcion.herramienta);
  if (!tool) return null;
  const candidates = rows.filter(row => {
    const category = normalizeFinanceText(`${row.cat || ''} ${row.tipo_egreso || ''}`);
    const name = normalizeFinanceText(row.nombre);
    const looksLikeSubscription = category.includes('suscripcion') || category.includes('software') || category.includes('herramienta');
    const nameMatches = name === tool || (tool.length >= 4 && name.includes(tool)) || (name.length >= 4 && tool.includes(name));
    return looksLikeSubscription && nameMatches && !row.suscripcion_id;
  });
  return candidates.length === 1 ? candidates[0] : null;
}

function shouldMirrorSubscriptionToExpense(suscripcion = {}) {
  const status = normalizeFinanceText(suscripcion.estado || 'activo');
  const amount = normalizeMoney(suscripcion.costo_mensual_equiv, normalizeMoney(suscripcion.costo_usd, 0));
  return Boolean(suscripcion.id && amount > 0 && !['cancelado', 'pausado'].includes(status));
}

function calculateGastoFijoLocally(row = {}) {
  const monto = normalizeMoney(row.monto_mensual, 0);
  return {
    ...row,
    concepto: String(row.concepto || '').trim(),
    categoria: row.categoria || '',
    entidad: row.entidad === 'usa' ? 'usa' : 'ecuador',
    monto_mensual: monto,
    costo_hora: Math.round((monto / 240) * 100) / 100,
    activo: row.activo !== false,
    notas: row.notas || '',
  };
}

function calculateFlujoConfigLocally(row = {}) {
  const nowMonth = new Date().toISOString().slice(0, 7) + '-01';
  const month = row.mes_inicio ? `${String(row.mes_inicio).slice(0, 7)}-01` : nowMonth;
  return {
    ...row,
    efectivo_inicial_usa: normalizeMoney(row.efectivo_inicial_usa, 0),
    efectivo_inicial_ecuador: normalizeMoney(row.efectivo_inicial_ecuador, 0),
    mes_inicio: month,
    activo: row.activo !== false,
  };
}

function slugifyMarketingCatalog(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function calculateMarketingSourceLocally(row = {}) {
  const name = String(row.name || '').trim();
  const type = MARKETING_SOURCE_TYPES.has(row.type) ? row.type : 'other';
  return {
    ...row,
    name,
    slug: row.slug || slugifyMarketingCatalog(name),
    type,
    platform: row.platform || '',
    description: row.description || '',
    active: row.active !== false,
  };
}

function calculateMarketingCampaignLocally(row = {}) {
  const name = String(row.name || '').trim();
  const campaignType = row.campaign_type && MARKETING_CAMPAIGN_TYPES.has(row.campaign_type) ? row.campaign_type : null;
  const status = MARKETING_CAMPAIGN_STATUSES.has(row.status) ? row.status : 'active';
  return {
    ...row,
    source_id: nullableUuid(row.source_id),
    name,
    slug: row.slug || slugifyMarketingCatalog(name),
    platform: row.platform || '',
    campaign_type: campaignType,
    status,
    start_date: row.start_date || null,
    end_date: row.end_date || null,
    budget: normalizeMoney(row.budget, 0),
    currency: String(row.currency || 'USD').trim().toUpperCase() || 'USD',
    meta_campaign_id: row.meta_campaign_id || '',
    external_campaign_id: row.external_campaign_id || '',
    notes: row.notes || '',
  };
}

const AppContext = createContext(null);
const STUDIO_BRAND_STORAGE_KEY = 'feria_studio_brand_assets';

function readStoredStudioBrandAssets() {
  if (typeof window === 'undefined') return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STUDIO_BRAND_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStoredStudioBrandAssets(assets = {}) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STUDIO_BRAND_STORAGE_KEY, JSON.stringify(assets || {}));
  } catch {
    // Storage cache is best-effort; DB remains the source of truth.
  }
}

function firstText(...values) {
  return values.find(value => typeof value === 'string' && value.trim()) || '';
}

function applyAccentColorCss(color) {
  const value = firstText(color);
  if (!value || typeof document === 'undefined') return value;
  const r = document.documentElement;
  // UNA sola derivacion. Antes esta funcion calculaba sus propias mezclas y
  // `getAccentColorTokens` calculaba otras distintas para los mismos nombres:
  // `--accent-hover` aclaraba aqui con blanco y oscurecia alli con negro. Dos
  // formulas para un token es una autoridad partida en dos.
  const t = getAccentColorTokens(value);
  r.style.setProperty('--gold', value);
  r.style.setProperty('--accent', value);
  r.style.setProperty('--accent-hover', t.accentHover);
  r.style.setProperty('--accent-strong', t.accentHover);
  r.style.setProperty('--accent-contrast', t.accentContrast);
  r.style.setProperty('--accent-ink', t.accentInk);
  r.style.setProperty('--gold-faint', t.accentFaint);
  r.style.setProperty('--accent-faint', t.accentFaint);
  r.style.setProperty('--accent-soft', t.accentSoft);
  r.style.setProperty('--accent-border', t.accentBorder);
  r.style.setProperty('--accent-focus', t.accentFocus);
  r.style.setProperty('--accent-glow', t.accentShadow);
  // ATENCION: "sin leer" no es un error. Sigue al acento, pero por el derivado
  // que si se ve sobre superficie clara (el acento crudo daba 1,91:1).
  r.style.setProperty('--accent-attention', t.accentAttention);
  r.style.setProperty('--accent-attention-ink', t.accentAttentionInk);
  // Rol --cta: el CTA principal del sistema DEJA de ser un rosa fijo y pasa a
  // ser el acento configurado. Era la tercera autoridad de color en paralelo.
  r.style.setProperty('--cta', value);
  r.style.setProperty('--cta-ink', t.accentContrast);
  r.style.setProperty('--cta-hover', t.accentHover);
  return value;
}

function writeStoredAccentColor(color) {
  const value = firstText(color);
  if (!value || typeof window === 'undefined') return value;
  try {
    window.localStorage.setItem('feria_accent_color', value);
  } catch {
    // Best-effort cache; public.studio remains the source of truth.
  }
  return value;
}

function studioBrandAssetsFromRow(studio = {}, fallback = {}) {
  return {
    displayName: firstText(studio.display_name, studio.nombre_visible, studio.name, studio.nombre, fallback.displayName),
    fullLight: firstText(studio.logo_light_url, studio.logo_full_light_url, studio.logo_full_url, studio.logo_url, fallback.fullLight),
    fullDark: firstText(studio.logo_dark_url, studio.logo_full_dark_url, studio.logo_full_url, studio.logo_url, fallback.fullDark),
    isoLight: firstText(studio.isotype_light_url, studio.isotipo_light_url, studio.logo_compact_light_url, studio.isotype_url, studio.isotipo_url, studio.logo_compact_url, fallback.isoLight),
    isoDark: firstText(studio.isotype_dark_url, studio.isotipo_dark_url, studio.logo_compact_dark_url, studio.isotype_url, studio.isotipo_url, studio.logo_compact_url, fallback.isoDark),
    accentColor: firstText(studio.accent_color, studio.color_principal, fallback.accentColor, fallback.accent_color),
    // Persona de atención del chat del portal (persistencia BD). Alimenta el prefill del
    // formulario de Admin; el chat la lee vía resolvePortalStudioBrand, no desde aquí.
    chatAgentName: firstText(studio.chat_agent_name, fallback.chatAgentName),
    chatAgentRole: firstText(studio.chat_agent_role, fallback.chatAgentRole),
    chatAgentPhoto: firstText(studio.chat_agent_photo_url, fallback.chatAgentPhoto),
  };
}

function normalizeStudioFromRow(studio = {}, previous = {}) {
  const brandAssets = studioBrandAssetsFromRow(studio, {});
  return {
    ...previous,
    name: firstText(studio.name, studio.nombre, studio.studio_name, previous.name),
    display_name: firstText(studio.display_name, studio.nombre_visible, previous.display_name),
    logo_url: firstText(studio.logo_url, studio.logoUrl, studio.logo, previous.logo_url),
    logo_full_url: firstText(studio.logo_full_url, studio.full_logo_url, previous.logo_full_url),
    logo_full_light_url: firstText(studio.logo_full_light_url, studio.full_logo_light_url, brandAssets.fullLight, previous.logo_full_light_url),
    logo_full_dark_url: firstText(studio.logo_full_dark_url, studio.full_logo_dark_url, brandAssets.fullDark, previous.logo_full_dark_url),
    logo_dark_url: firstText(studio.logo_dark_url, previous.logo_dark_url),
    logo_light_url: firstText(studio.logo_light_url, previous.logo_light_url),
    isotype_url: firstText(studio.isotype_url, studio.isotipo_url, studio.logo_compact_url, previous.isotype_url),
    isotype_light_url: firstText(studio.isotype_light_url, studio.isotipo_light_url, studio.logo_compact_light_url, brandAssets.isoLight, previous.isotype_light_url),
    isotype_dark_url: firstText(studio.isotype_dark_url, studio.isotipo_dark_url, studio.logo_compact_dark_url, brandAssets.isoDark, previous.isotype_dark_url),
    accent_color: firstText(studio.accent_color, studio.color_principal, previous.accent_color),
    // Persona del chat: se propaga en crudo para que el resolver del portal la lea desde
    // data.studio (este normalizador es un whitelist; sin estas líneas no llegaría).
    chat_agent_name: firstText(studio.chat_agent_name, previous.chat_agent_name),
    chat_agent_role: firstText(studio.chat_agent_role, previous.chat_agent_role),
    chat_agent_photo_url: firstText(studio.chat_agent_photo_url, previous.chat_agent_photo_url),
    portal_show_news: typeof studio.portal_show_news === 'boolean' ? studio.portal_show_news : Boolean(previous.portal_show_news),
    archive_storage_enabled: typeof studio.archive_storage_enabled === 'boolean' ? studio.archive_storage_enabled : previous.archive_storage_enabled !== false,
    archive_storage_provider: firstText(studio.archive_storage_provider, previous.archive_storage_provider, 'supabase'),
    archive_storage_bucket: firstText(studio.archive_storage_bucket, previous.archive_storage_bucket, 'feria-archive'),
    archive_storage_base_path: firstText(studio.archive_storage_base_path, previous.archive_storage_base_path, 'internal-client-archive/'),
    archive_storage_mode: firstText(studio.archive_storage_mode, previous.archive_storage_mode, 'direct'),
    archive_storage_endpoint: firstText(studio.archive_storage_endpoint, previous.archive_storage_endpoint),
    archive_storage_region: firstText(studio.archive_storage_region, previous.archive_storage_region),
    archive_storage_access_key_ref: firstText(studio.archive_storage_access_key_ref, previous.archive_storage_access_key_ref),
    archive_storage_secret_key_ref: firstText(studio.archive_storage_secret_key_ref, previous.archive_storage_secret_key_ref),
  };
}

function studioPayloadFromBrandAssets(assets = {}) {
  const clean = value => {
    const text = typeof value === 'string' ? value.trim() : '';
    return text || null;
  };
  const payload = {
    display_name: clean(assets.displayName),
    logo_url: clean(assets.fullDark || assets.fullLight),
    logo_full_url: clean(assets.fullDark || assets.fullLight),
    logo_dark_url: clean(assets.fullDark),
    logo_light_url: clean(assets.fullLight),
    logo_full_light_url: clean(assets.fullLight),
    logo_full_dark_url: clean(assets.fullDark),
    isotype_url: clean(assets.isoDark || assets.isoLight),
    isotype_light_url: clean(assets.isoLight),
    isotype_dark_url: clean(assets.isoDark),
    chat_agent_name: clean(assets.chatAgentName),
    chat_agent_role: clean(assets.chatAgentRole),
    chat_agent_photo_url: clean(assets.chatAgentPhoto),
    updated_at: new Date().toISOString(),
  };
  if (Object.prototype.hasOwnProperty.call(assets, 'accentColor') || Object.prototype.hasOwnProperty.call(assets, 'accent_color')) {
    payload.accent_color = clean(assets.accentColor || assets.accent_color);
  }
  return payload;
}

function applyTheme(theme) {
  const r = document.documentElement;
  r.setAttribute('data-theme', theme === 'light' ? 'light' : 'dark');
  if (theme === 'light') {
    // Light D17 (Fase B): familia perla #f5f5f5 y tintas neutras
    r.style.setProperty('--dark',      '#ffffff'); // Sidebar
    r.style.setProperty('--s1',        '#f5f5f5'); // Main content bg (perla)
    r.style.setProperty('--s2',        '#ffffff'); // Cards
    r.style.setProperty('--s3',        '#efefef'); // Hover states
    r.style.setProperty('--s4',        '#e5e5e5'); // Elements/Scrollbars
    r.style.setProperty('--border-s',  'rgba(0,0,0,0.06)');
    r.style.setProperty('--border-m',  'rgba(0,0,0,0.1)');
    r.style.setProperty('--border',    'rgba(0,0,0,0.15)');
    r.style.setProperty('--text',      '#141414');
    r.style.setProperty('--text-m',    '#555555');
    r.style.setProperty('--text-d',    '#8d8d8d');
    r.style.setProperty('--gold-faint','color-mix(in srgb, var(--accent) 8%, transparent)');
    r.style.setProperty('--accent-faint','color-mix(in srgb, var(--accent) 9%, transparent)');
    r.style.setProperty('--accent-glow','color-mix(in srgb, var(--accent) 14%, transparent)');
  } else {
    // Dark D17 (Fase B): base #121212 y tarjetas #1c1c1c
    r.style.setProperty('--dark',      '#121212');
    r.style.setProperty('--s1',        '#161616');
    r.style.setProperty('--s2',        '#1c1c1c');
    r.style.setProperty('--s3',        '#232323');
    r.style.setProperty('--s4',        '#2e2e2e');
    r.style.setProperty('--border-s',  'rgba(255,255,255,0.05)');
    r.style.setProperty('--border-m',  'rgba(255,255,255,0.08)');
    r.style.setProperty('--border',    'rgba(255,255,255,0.12)');
    r.style.setProperty('--text',      '#ededed');
    r.style.setProperty('--text-m',    '#a8a8a8');
    r.style.setProperty('--text-d',    '#717171');
    r.style.setProperty('--gold-faint','color-mix(in srgb, var(--accent) 10%, transparent)');
    r.style.setProperty('--accent-faint','color-mix(in srgb, var(--accent) 11%, transparent)');
    r.style.setProperty('--accent-glow','color-mix(in srgb, var(--accent) 18%, transparent)');
  }
}

// En producción REACT_APP_USE_SEED debe ser undefined (no 'true')
// En desarrollo local puedes poner REACT_APP_USE_SEED=true en .env para usar datos de ejemplo
const USE_SEED = process.env.NODE_ENV === 'test' || process.env.REACT_APP_USE_SEED === 'true';

async function edgeFunctionErrorMessage(error, fallback = 'No se pudo completar la operacion.') {
  const response = error?.context || error?.response;
  if (response && typeof response.clone === 'function') {
    try {
      const text = await response.clone().text();
      if (text) {
        try {
          const json = JSON.parse(text);
          return json?.error || json?.message || json?.details || fallback;
        } catch {
          return text.slice(0, 500);
        }
      }
    } catch (_) {
      // Keep the SDK message if the body cannot be read.
    }
  }
  const message = error?.message || fallback;
  return /non-2xx status code/i.test(message) ? fallback : message;
}

async function edgeFunctionErrorPayload(error) {
  const response = error?.context || error?.response;
  if (response && typeof response.clone === 'function') {
    try {
      const text = await response.clone().text();
      if (!text) return null;
      return JSON.parse(text);
    } catch (_) {
      return null;
    }
  }
  return null;
}

function normalizeTeamMember(m = {}) {
  const brandList = Array.isArray(m.brands) ? m.brands : (m.brand ? [m.brand] : ['feria', 'bl']);
  const roleId = m.perms || m.role_id || m.access_role || 'creativo';
  const alias = teamAlias(m);
  const avatar_emoji = teamAvatarEmoji(m);
  const formalName = m.name || m.nombre || m.email || 'Nuevo miembro';
  const displayName = alias || m.displayName || m.display_name || formalName;
  return {
    ...m,
    name: formalName,
    alias,
    displayName,
    avatar_emoji,
    avatarEmoji: avatar_emoji,
    initials: m.initials || initialsFromName(displayName, m.email),
    perms: roleId,
    role: m.role || m.cargo || 'Colaborador',
    brands: brandList.length ? brandList : ['feria'],
    permissions: normalizePermissions(roleId, m.permissions),
    bg: m.bg || (m.color || '#5B9BD5') + '22',
  };
}

const TEAM_DB_FIELDS = [
  'id',
  'name',
  'initials',
  'role',
  'email',
  'color',
  'bg',
  'perms',
  'brand',
  'brands',
  'permissions',
  'is_active',
  'is_demo',
  'alias',
  'avatar_emoji',
  'created_at',
];

function pickTeamDbPayload(member = {}) {
  const normalized = { ...member };
  if (normalized.avatarEmoji && !normalized.avatar_emoji) normalized.avatar_emoji = normalized.avatarEmoji;
  if (Array.isArray(normalized.brands)) normalized.brand = normalized.brands[0] || normalized.brand || 'feria';
  return TEAM_DB_FIELDS.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(normalized, key) && normalized[key] !== undefined) {
      acc[key] = normalized[key];
    }
    return acc;
  }, {});
}

function hideDemo(rows = [], demoMode = false) {
  return onlyOperationalRows(rows, demoMode);
}

const DEUDA_DB_FIELDS = [
  'id',
  'nombre',
  'monto',
  'fecha',
  'status',
  'responsable',
  'cliente_id',
  'notas',
  'is_demo',
  'created_at',
  'updated_at',
  'tipo',
  'deuda_original',
  'saldo_actual',
  'cuota_mensual',
  'tasa_interes',
  'liquidacion_estimada',
  'pagos',
  'concepto',
  'acreedor',
  'cuota',
  'fecha_pago',
  'vence',
  'estado',
  'proxima_fecha_pago',
  'fecha_vencimiento',
  'due_date',
];

function pickDeudaDbPayload(payload = {}) {
  return DEUDA_DB_FIELDS.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key) && payload[key] !== undefined) {
      acc[key] = payload[key];
    }
    return acc;
  }, {});
}

const SUELDO_PROGRAMADO_DB_FIELDS = [
  'id',
  'persona',
  'cargo',
  'valor',
  'frecuencia',
  'fecha_pago',
  'estado',
  'notas',
  'is_demo',
  'created_at',
  'updated_at',
];

function pickSueldoProgramadoDbPayload(payload = {}) {
  return SUELDO_PROGRAMADO_DB_FIELDS.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key) && payload[key] !== undefined) {
      acc[key] = payload[key];
    }
    return acc;
  }, {});
}

const CLIENTE_DB_FIELDS = [
  'id',
  'nombre',
  'email',
  'telefono',
  'empresa',
  'tipo',
  'servicio',
  'monto',
  'notas',
  'stage',
  'brand',
  'color',
  'creativo_id',
  'created_at',
  'updated_at',
  'service_type',
  'is_demo',
  'whatsapp',
  'portal_user_id',
  'portal_access_status',
  'portal_access_created_at',
  'portal_access_sent_at',
  'portal_access_whatsapp_sent_at',
  'portal_password_must_change',
  'archived_at',
  'archived_by',
  'archive_reason',
  'original_lead_id',
  'acquisition_source_id',
  'acquisition_campaign_id',
  'acquisition_confidence',
  'acquisition_notes',
];

function pickClienteDbPayload(payload = {}) {
  const picked = CLIENTE_DB_FIELDS.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key) && payload[key] !== undefined) {
      acc[key] = payload[key];
    }
    return acc;
  }, {});

  if (picked.id && !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(picked.id))) {
    delete picked.id;
  }

  if (typeof picked.email === 'string') picked.email = picked.email.trim();
  if (typeof picked.nombre === 'string') picked.nombre = picked.nombre.trim();
  if (typeof picked.telefono === 'string') picked.telefono = picked.telefono.trim();
  if (typeof picked.whatsapp === 'string') picked.whatsapp = picked.whatsapp.trim();

  return picked;
}

function isMissingClienteSchemaColumn(error, column) {
  const message = String(error?.message || error || '');
  return message.includes('schema cache') && message.includes(`'${column}'`) && message.includes('column');
}

function withoutClienteDbField(payload = {}, field) {
  const clean = { ...payload };
  delete clean[field];
  return clean;
}

function normalizeClienteRow(row = {}) {
  return { ...row, creativo: row.creativo_id ?? row.creativo };
}

const PROYECTO_DB_FIELDS = new Set([
  'id',
  'cliente_id',
  'nombre',
  'estado',
  'fase',
  'pct_interno',
  'pct_cliente',
  'dias_entrega',
  'dias_ejecucion',
  'brand',
  'creativo_id',
  'fecha_inicio',
  'fecha_entrega',
  'prioridad',
  'created_at',
  'updated_at',
  'is_demo',
  'client_due_date',
  'internal_due_date',
  'package_name',
  'package_app_limit',
  'package_price',
  'service_type',
  'assigned_reviewer_id',
  'production_status',
  'designer_assigned_at',
  'designer_assignment_notes',
  'designer_assignment_status',
  'brief_responses',
  'brief_completed_at',
  'production_started_at',
  'project_started_at',
  'client_portal_status',
  'meeting_unlocked_at',
  'approvals_ready_at',
  'final_payment_paid_at',
  'brandkit_unlocked_at',
  'brandkit_delivery_url',
  'production_ready_at',
  'delay_owner',
  'delay_reason',
  'delay_started_at',
  'delay_resolved_at',
  'approval_act_signed_at',
  'approval_act_id',
  'delivery_act_id',
  'delivery_act_signed_at',
  'quote_id',
  'contrato_id',
  'brief_id',
  'source_client_id',
  'project_scope',
  'internal_requester_id',
  'internal_request_notes',
]);

function normalizeProjectScope(project = {}) {
  if (project.project_scope === 'studio' || project.projectScope === 'studio' || project.is_internal === true) return 'studio';
  return 'client';
}

function pickProyectoDbPayload(payload = {}) {
  return Object.entries(payload).reduce((acc, [key, value]) => {
    if (PROYECTO_DB_FIELDS.has(key) && value !== undefined) acc[key] = value;
    return acc;
  }, {});
}

function normalizeProyectoRow(row = {}) {
  const scope = normalizeProjectScope(row);
  return {
    ...row,
    clienteId: row.cliente_id,
    creativo: row.creativo_id,
    pctInterno: row.pct_interno,
    pctCliente: row.pct_cliente,
    diasEntrega: row.dias_entrega,
    diasEjecucion: row.dias_ejecucion,
    fechaEntregaCliente: row.client_due_date,
    fechaEntregaInterna: row.internal_due_date,
    paquete: row.package_name,
    aplicacionesIncluidas: row.package_app_limit,
    project_scope: scope,
    projectScope: scope,
    is_internal: scope === 'studio',
  };
}

const CONTRATO_DB_FIELDS = [
  'id',
  'cliente_id',
  'firmante',
  'firmante_doc',
  'fecha_firma',
  'hash_firma',
  'contenido',
  'status',
  'created_at',
  'updated_at',
  'is_demo',
  'studio_signature_label',
  'studio_signature_url',
  'client_signature_name',
  'client_signature_image_url',
  'client_signature_url',
  'client_signed_email',
  'client_signed_ip',
  'final_pdf_url',
  'verification_code',
  'verification_url',
  'document_type',
  'document_hash',
  'audit_hash',
  'require_identity_document',
  'identity_document_path',
  'identity_document_type',
  'identity_uploaded_at',
  'identity_consent_at',
  'locked_at',
  'sent_copy_at',
  'legal_data',
  'ready_for_signature_at',
  'portal_access_requested_at',
  'estudio_firmado',
  'service_type',
  'quote_id',
  'proyecto_id',
];

function pickContratoDbPayload(payload = {}) {
  return CONTRATO_DB_FIELDS.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key) && payload[key] !== undefined) {
      acc[key] = payload[key];
    }
    return acc;
  }, {});
}

const SEED = {
  studio: {
    name: 'Feria Design Studio',
    brands: [
      { id: 'feria', name: 'Feria Design',   color: '#5B9BD5' },
      { id: 'bl',    name: 'Brand & Legacy', color: '#D4537E' },
    ],
    // diasRestantes calculado dinámicamente — nunca hardcodeado
    meta: { objetivo: 14000, actual: 0, diasRestantes: 0 },
  },

  // Fallback VACÍO a propósito: si `data.team` no trae filas es porque la consulta a `team`
  // falló (RLS/permisos), no porque el estudio no tenga equipo. La UI degrada con dignidad
  // (rosters vacíos, "Sin asignar"); NO se inventan personas. El equipo real vive en la tabla
  // `team`; el rastro del fallo va a consola (ver el console.error del loader).
  team: [],

  clientes:      [],
  proyectos:     [],
  cobros:        [],
  ingresos:      [],
  suscripciones: [],
  gastosFijos:   [],
  flujoConfig:   [],
  gastos:        [],
  leads:         [],
  notificaciones:[],
  clientResponsibilities: [],
  cotizacionesEnviadas: [],
  contratos:      [],
  contractParticipants: [],
  contractSignatures: [],
  briefs:         [],
  briefParticipation: [],
  clientPortalMembers: [],
  reuniones:     [],
  projectUpdates:[],
  projectApplications:[],
  projectApprovalRecords:[],
  projectApprovalActs:[],
  projectAdjustmentRequests:[],
  brandkitAssets:[],
  internalClientDocuments:[],
  projectDeliveryActs:[],
  brandkitDownloadLogs:[],
  projectChatMessages:[],
  projectUpdateComments:[],
  marketingPerformance:[],
  marketingMetaInsights:[],
  marketingSources:[],
  marketingCampaigns:[],
  metaAdsSyncLogs:[],
  communicationEvents:[],
  supportTickets:[],
  supportTicketMessages:[],
  clientSatisfactionSurveys:[],
  testimonialRequests:[],
  landingAssets:[],
  studioNews:[],
  portalAssets:[],
  deudas:        [],
  sueldosProgramados: [],
};

// ── STAGE LABELS ──────────────────────────────────────────────────────────
export const STAGE_LABELS = [
  'Captación', 'Propuesta', 'Contrato', 'Cobro 60%',
  'Brief', 'Reunión', 'Producción', 'Aprobación',
  'Cobro 40%', 'Brand Kit', 'Post-venta',
];

export const KANBAN_COLS = [
  { id: 'origen',       label: 'El Origen',    color: '#8A8070' },
  { id: 'exploracion',  label: 'Exploración',  color: '#5B9BD5' },
  { id: 'refinamiento', label: 'Refinamiento', color: '#C9A96E' },
  { id: 'entrega',      label: 'Entrega',      color: '#7BC67A' },
  { id: 'completado',   label: 'Completado',   color: '#4ECDC4' },
];


function addDaysISO(days = 3) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function normalizeMoney(value, fallback = 0) {
  const cleaned = typeof value === 'string'
    ? value.replace(/[^\d.,-]/g, '').replace(/,(?=\d{3}\b)/g, '').replace(',', '.')
    : value;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeFinanceText(value = '') {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function financeSyncMarker(type, id) {
  return id ? `[feria-sync:${type}:${id}]` : '';
}

function hasFinanceSyncMarker(row = {}, type, id) {
  const marker = financeSyncMarker(type, id);
  return Boolean(marker && String(row.notas || '').includes(marker));
}

function getFinanceSyncRef(row = {}) {
  const match = String(row.notas || '').match(/\[feria-sync:(payroll|debt):([^\]]+)\]/);
  return match ? { type: match[1], id: match[2] } : null;
}

function appendFinanceSyncNote(existing = '', marker = '', label = '') {
  const current = String(existing || '').trim();
  const base = `${marker} Reflejo automatico de ${label}. No editar desde Gastos fijos; cambia la fuente original.`;
  if (!marker) return current || base;
  if (current.includes(marker)) return current;
  return current ? `${base} ${current}` : base;
}

function payrollMonthlyValue(sueldo = {}) {
  const value = normalizeMoney(sueldo.valor ?? sueldo.monto ?? sueldo.sueldo, 0);
  const frequency = normalizeFinanceText(sueldo.frecuencia || 'mensual');
  if (frequency.includes('anual')) return Math.round((value / 12) * 100) / 100;
  if (frequency.includes('quincenal')) return Math.round((value * 2) * 100) / 100;
  if (frequency.includes('semanal')) return Math.round((value * 4.33) * 100) / 100;
  return value;
}

function payrollFixedExpenseCategory(sueldo = {}) {
  const cargo = normalizeFinanceText(`${sueldo.cargo || ''} ${sueldo.persona || ''}`);
  return cargo.includes('honorario') || cargo.includes('servicio profesional') || cargo.includes('contador') || cargo.includes('raquel')
    ? 'Servicios profesionales'
    : 'Sueldo';
}

function buildPayrollFixedExpensePayload(sueldo = {}, existing = {}) {
  const persona = String(sueldo.persona || existing.concepto || 'Persona').trim();
  const amount = payrollMonthlyValue(sueldo);
  const category = payrollFixedExpenseCategory(sueldo);
  const prefix = category === 'Servicios profesionales' ? 'Honorarios' : 'Sueldo';
  const marker = financeSyncMarker('payroll', sueldo.id);
  return calculateGastoFijoLocally({
    ...existing,
    concepto: `${prefix} - ${persona}`,
    categoria: category,
    entidad: 'ecuador',
    monto_mensual: amount,
    activo: amount > 0 && sueldo.estado !== 'inactivo',
    notas: appendFinanceSyncNote(existing.notas || sueldo.notas, marker, 'Sueldos'),
  });
}

function findPayrollFixedExpense(rows = [], sueldo = {}) {
  const markerMatch = rows.find(row => hasFinanceSyncMarker(row, 'payroll', sueldo.id));
  if (markerMatch) return markerMatch;

  const persona = normalizeFinanceText(sueldo.persona);
  if (!persona) return null;
  const expectedCategory = payrollFixedExpenseCategory(sueldo);
  return rows.find(row => {
    const concept = normalizeFinanceText(row.concepto);
    const category = normalizeFinanceText(row.categoria);
    const categoryMatches = category === normalizeFinanceText(expectedCategory)
      || category.includes('sueldo')
      || category.includes('servicio profesional');
    return categoryMatches && concept.includes(persona);
  }) || null;
}

function debtMonthlyValue(deuda = {}) {
  return normalizeMoney(deuda.cuota_mensual ?? deuda.cuota ?? deuda.monthlyQuota, 0);
}

function isDebtSettled(deuda = {}) {
  const status = normalizeFinanceText(`${deuda.estado || ''} ${deuda.status || ''}`);
  const balance = normalizeMoney(deuda.saldo_actual ?? deuda.saldo ?? deuda.balance, Number.NaN);
  return status.includes('pagad') || status.includes('liquidad') || status.includes('cerrad') || balance === 0;
}

function buildDebtFixedExpensePayload(deuda = {}, existing = {}) {
  const amount = debtMonthlyValue(deuda);
  const name = String(deuda.nombre || deuda.concepto || deuda.acreedor || 'Prestamo').trim();
  const marker = financeSyncMarker('debt', deuda.id);
  return calculateGastoFijoLocally({
    ...existing,
    concepto: `Prestamo - ${name}`,
    categoria: 'Obligaciones',
    entidad: 'ecuador',
    monto_mensual: amount,
    activo: amount > 0 && !isDebtSettled(deuda),
    notas: appendFinanceSyncNote(existing.notas || deuda.notas, marker, 'Deudas'),
  });
}

function findDebtFixedExpense(rows = [], deuda = {}) {
  const markerMatch = rows.find(row => hasFinanceSyncMarker(row, 'debt', deuda.id));
  if (markerMatch) return markerMatch;

  const debtTokens = [deuda.nombre, deuda.concepto, deuda.acreedor].map(normalizeFinanceText).filter(Boolean);
  const unmarkedLoanRows = rows.filter(row => {
    const concept = normalizeFinanceText(row.concepto);
    const category = normalizeFinanceText(row.categoria);
    const hasOtherDebtMarker = Boolean(getFinanceSyncRef(row)?.type === 'debt');
    const looksLikeLoan = concept.includes('prestamo') || concept.includes('prestamos') || category.includes('obligacion');
    return looksLikeLoan && !hasOtherDebtMarker;
  });

  const tokenMatches = unmarkedLoanRows.filter(row => {
    const concept = normalizeFinanceText(row.concepto);
    return debtTokens.some(token => token && (concept.includes(token) || token.includes(concept)));
  });
  if (tokenMatches.length === 1) return tokenMatches[0];

  return debtTokens.length <= 1 && unmarkedLoanRows.length === 1 ? unmarkedLoanRows[0] : null;
}

function isLegacyDebtExpenseRow(row = {}) {
  const name = normalizeFinanceText(`${row.nombre || ''} ${row.concepto || ''} ${row.cat || ''} ${row.categoria || ''} ${row.tipo_egreso || ''}`);
  return name.includes('prestamo') || name.includes('prestamos');
}

// `CONTRACT_PACKAGE_ALIASES` y `contractPackageKey` vivian aqui para traducir nombres
// legados de contrato a claves de la tabla estatica. Con el precio fuera de esa tabla
// ya no traducen nada: la busqueda por nombre la hace `precioCanonicoDelPaquete`, que
// normaliza tildes y caja.

// ── EL PRECIO DE UN PAQUETE SALE DE `paquetes_cotizacion`, Y DE NINGUN OTRO SITIO ──
//
// Esto leia `PACKAGE_RULES[...].price`, que en `packages.js` mantiene los precios
// ANTERIORES al Pricebook V1 -1500 / 2900 / 4600 frente a los canonicos 1600 / 3000 / 4800-
// y que nada reconciliaba contra la base.
//
// Ahora resuelve contra el catalogo canonico registrado y devuelve `null` cuando no puede.
// NO devuelve 0: un contrato con monto 0 pasaria guardas que un `null` bloquea, y un precio
// viejo parece verdad.
//
// El nombre del paquete sigue siendo lo unico que este camino tiene -viene del contrato o
// del cliente-, asi que se usa para BUSCAR la fila. El precio lo da la fila.
function resolvePackagePrice(value = '') {
  return precioCanonicoONull({ packageName: value });
}

function resolveContractAmount(contractData = {}, cliente = {}) {
  const quoteSnapshot = contractData.quote_snapshot && typeof contractData.quote_snapshot === 'object'
    ? contractData.quote_snapshot
    : null;
  const quoteSelection = quoteSnapshot?.client_selection && typeof quoteSnapshot.client_selection === 'object'
    ? quoteSnapshot.client_selection
    : {};
  const quotePricing = quoteSelection?.pricing && typeof quoteSelection.pricing === 'object'
    ? quoteSelection.pricing
    : {};
  const snapshotAmount = normalizeMoney(
    quoteSnapshot?.final_total ?? quotePricing.total ?? quoteSelection.total,
    0,
  );
  if (snapshotAmount > 0) return snapshotAmount;

  const explicit = normalizeMoney(
    contractData.monto ?? contractData.total ?? contractData.valor ?? contractData.package_price ?? cliente.monto ?? cliente.valor ?? cliente.package_price,
    0,
  );
  if (explicit > 0) return explicit;
  // Ultimo recurso. Puede ser `null`: la instantanea y el monto explicito son las dos
  // autoridades buenas, y si ninguna esta, el catalogo canonico es lo que queda. Si
  // tampoco resuelve, quien llama BLOQUEA -ver las dos guardas de abajo-.
  return resolvePackagePrice(contractData.paquete || contractData.package_name || contractData.service_type || cliente.servicio || cliente.service_type || cliente.paquete);
}

function buildPortalAccess(cliente = {}) {
  const email = cliente.email || cliente.clientEmail || '';
  const passwordSeed = String(cliente.id || email || Date.now()).replace(/[^a-zA-Z0-9]/g, '').slice(-6).toUpperCase();
  return {
    portal_url: `${window.location.origin}/portal`,
    portal_email: email,
    portal_password: cliente.portal_password || `Feria-${passwordSeed || '2026'}!`,
    portal_access_status: 'activo',
    portal_credentials_sent_at: new Date().toISOString(),
  };
}

// ── PROVIDER ──────────────────────────────────────────────────────────────
export function AppProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  // ── QUIEN MIRA DECIDE QUE COLUMNAS SE PIDEN ───────────────────────────────
  //
  // `db.clientes.getAll()` pedia `select('*')`, y esa fila entera llegaba al
  // navegador del CLIENTE: las 38 columnas de `clientes`, incluidas las notas
  // internas del estudio sobre el y la confianza de atribucion de su adquisicion.
  // RLS no lo impide —gobierna que FILAS se ven, no que columnas— asi que la
  // minimizacion es una decision de la consulta.
  //
  // El personal no pierde nada: con `false` recibe lo de siempre. La lista segura
  // vive en `lib/columnasDeCliente.js`, en un solo sitio.
  const esCliente = user?.isClient === true
    || String(user?.role || user?.perms || '').toLowerCase() === 'cliente';
  const [data, setData] = useState(SEED);
  const [activeBrand, setActiveBrand] = useState('all');
  const [toast, setToast] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('feria_theme') || 'light');
  const [accentColor, setAccentColorState] = useState(() => localStorage.getItem('feria_accent_color') || DEFAULT_ACCENT_COLOR);
  const [studioLogo, setStudioLogo] = useState(null);
  const [studioBrandAssets, setStudioBrandAssets] = useState(() => readStoredStudioBrandAssets());
  const studioIdRef = React.useRef(null); // UUID real del studio en DB — usado en updateMeta
  const [dbReady, setDbReady] = useState(false);
  const [demoMode, setDemoModeState] = useState(() => localStorage.getItem('feria_demo_mode') === 'true');

  // ── CALCULAR DÍAS RESTANTES EN EL MES ─────────────────────────
  function calcDiasRestantes() {
    const hoy  = new Date();
    const fin  = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    return fin.getDate() - hoy.getDate();
  }

  const applyStudioBrandRow = useCallback((studioRow = {}, fallbackAssets = {}) => {
    const dbAssets = studioBrandAssetsFromRow(studioRow, fallbackAssets);
    setStudioBrandAssets(dbAssets);
    writeStoredStudioBrandAssets(dbAssets);
    setStudioLogo(dbAssets.fullDark || dbAssets.fullLight || studioRow.logo_url || null);
    if (dbAssets.accentColor) {
      setAccentColorState(dbAssets.accentColor);
      writeStoredAccentColor(dbAssets.accentColor);
      applyAccentColorCss(dbAssets.accentColor);
    }
    setData(prev => ({
      ...prev,
      studio: normalizeStudioFromRow(studioRow, prev.studio),
    }));
    return dbAssets;
  }, []);

  // ── CARGAR DESDE SUPABASE ─────────────────────────────────────────────
  useEffect(() => {
    if (!isConfigured) {
      // Sin Supabase: al menos calcula diasRestantes correctamente
      setData(d => ({ ...d, studio: { ...d.studio, meta: { ...d.studio.meta, diasRestantes: calcDiasRestantes() } } }));
      return;
    }
    if (authLoading) return;
    if (!user?.id) {
      let cancelled = false;
      async function loadStudioBrandOnly() {
        try {
          const [studioResult, landingAssetsResult, portalAssetsResult] = await Promise.all([
            // Bug 2 (hotfix): superficies publicas/deslogueadas leen la MARCA desde la vista publica
            // (grant a anon); la tabla `studio` esta bloqueada por RLS para anon (solo-staff).
            supabase.from('studio_public_brand').select('*').limit(1),
            supabase.from('landing_assets').select('*').eq('active', true).order('section', { ascending: true }).order('sort_order', { ascending: true }),
            supabase.from('portal_assets').select('*').eq('status', 'published').order('scope', { ascending: true }).order('entity_ref', { ascending: true }).order('asset_key', { ascending: true }).order('published_at', { ascending: false }),
          ]);
          if (cancelled) return;
          if (studioResult.error) {
            console.warn('[Feria OS] Error loading public studio branding:', studioResult.error?.message || studioResult.error);
          }
          if (landingAssetsResult.error) {
            console.warn('[Feria OS] Error loading public landing assets:', landingAssetsResult.error?.message || landingAssetsResult.error);
          }
          if (portalAssetsResult.error) {
            console.warn('[Feria OS] Error loading public portal assets:', portalAssetsResult.error?.message || portalAssetsResult.error);
          }
          const rows = studioResult.data || [];
          const studioData = rows?.[0];
          if (studioData) {
            const dbBrandAssets = studioBrandAssetsFromRow(studioData, readStoredStudioBrandAssets());
            setStudioBrandAssets(dbBrandAssets);
            writeStoredStudioBrandAssets(dbBrandAssets);
            setStudioLogo(dbBrandAssets.fullDark || dbBrandAssets.fullLight || studioData.logo_url || null);
            if (dbBrandAssets.accentColor) {
              setAccentColorState(dbBrandAssets.accentColor);
              writeStoredAccentColor(dbBrandAssets.accentColor);
              applyAccentColorCss(dbBrandAssets.accentColor);
            }
          }
          setData(prev => ({
            ...prev,
            landingAssets: landingAssetsResult.error ? prev.landingAssets || [] : (landingAssetsResult.data || []).map(normalizeLandingAsset),
            portalAssets: portalAssetsResult.error ? prev.portalAssets || [] : (portalAssetsResult.data || []).map(normalizePortalAsset),
            studio: studioData
              ? {
                  ...normalizeStudioFromRow(studioData, prev.studio),
                  meta: {
                    ...prev.studio.meta,
                    diasRestantes: calcDiasRestantes(),
                  },
                }
              : { ...prev.studio, meta: { ...prev.studio.meta, diasRestantes: calcDiasRestantes() } },
          }));
        } catch {
          // Public/login surfaces can fall back to cached branding without blocking render.
        } finally {
          if (!cancelled) setDbReady(false);
        }
      }
      loadStudioBrandOnly();
      return () => { cancelled = true; };
    }

    async function loadFromDB() {
      if (USE_SEED) {
        setDbReady(true);
        setData(d => ({ ...d, studio: { ...d.studio, meta: { ...d.studio.meta, diasRestantes: calcDiasRestantes() } } }));
        return;
      }
      try {
        const [clientesRows, cobrosRows, ingresosRows, suscripcionesRows, gastosFijosRows, flujoConfigRows, gastosRows, leadsRows, notifsRows, clientResponsibilitiesRows, proyectosRows, cotizacionesRows, contratosRows, contractParticipantsRows, contractSignaturesRows, briefsRows, briefParticipationRows, clientPortalMembersRows, studioRows, teamRows, reunionesRows, projectUpdatesRows, projectApplicationsRows, projectApprovalRows, projectApprovalActsRows, projectAdjustmentRows, brandkitAssetsRows, internalClientDocumentsRows, projectDeliveryActsRows, brandkitDownloadRows, projectChatRows, projectCommentsRows, marketingPerformanceRows, marketingMetaInsightsRows, marketingSourcesRows, marketingCampaignsRows, metaAdsSyncLogsRows, communicationEventsRows, supportTicketsRows, supportTicketMessagesRows, clientSatisfactionSurveysRows, testimonialRequestsRows, landingAssetsRows, portalAssetsRows, deudasRows, sueldosRows] = await Promise.all([
          db.clientes.getAll(esCliente),
          db.cobros.getAll(),
          supabase.from('ingresos').select('*').order('mes', { ascending: false }).order('created_at', { ascending: false }),
          supabase.from('suscripciones').select('*').order('estado', { ascending: true }).order('fecha_renovacion', { ascending: true }),
          supabase.from('gastos_fijos').select('*').order('activo', { ascending: false }).order('categoria', { ascending: true }).order('concepto', { ascending: true }),
          supabase.from('flujo_config').select('*').order('activo', { ascending: false }).order('updated_at', { ascending: false }).limit(5),
          db.gastos.getAll(),
          db.leads.getAll(),
          db.notificaciones.getAll(),
          db.clientResponsibilities.getAll(),
          db.proyectos.getAll(),
          supabase.from('cotizaciones_enviadas').select('*').order('created_at', { ascending: false }),
          supabase.from('contratos').select('*, clase_de_origen').order('created_at', { ascending: false }),
          supabase.from('contract_participants').select('*').order('sign_order', { ascending: true }).order('created_at', { ascending: true }),
          supabase.from('contract_signatures').select('*').order('signed_at', { ascending: false }),
          supabase.from('briefs').select('*, clase_de_origen').order('created_at', { ascending: false }),
          supabase.from('brief_participation').select('*').order('created_at', { ascending: true }),
          supabase.from('client_portal_members').select('*').order('created_at', { ascending: false }),
          supabase.from('studio').select('*').limit(1),
          supabase.from('team').select('*'),
          supabase.from('reuniones').select('*').order('fecha', { ascending: true }),
          supabase.from('project_updates').select('*').order('created_at', { ascending: false }),
          supabase.from('project_applications').select('*, clase_de_origen').order('sort_order', { ascending: true }),
          supabase.from('project_approval_records').select('*').order('created_at', { ascending: false }),
          supabase.from('project_approval_acts').select('*').order('created_at', { ascending: false }),
          supabase.from('project_adjustment_requests').select('*').order('created_at', { ascending: false }),
          supabase.from('brandkit_assets').select('*').order('created_at', { ascending: false }),
          supabase.from('internal_client_documents').select('*').order('created_at', { ascending: false }),
          supabase.from('project_delivery_acts').select('*').order('created_at', { ascending: false }),
          supabase.from('brandkit_download_logs').select('*').order('created_at', { ascending: false }),
          supabase.from('project_chat_messages').select('*').order('created_at', { ascending: true }),
          supabase.from('project_update_comments').select('*').order('created_at', { ascending: true }),
          supabase.from('marketing_performance').select('*').order('period_start', { ascending: false }),
          supabase.from('marketing_meta_insights').select('*').order('date', { ascending: false }).limit(1000),
          supabase.from('marketing_sources').select('*').order('active', { ascending: false }).order('name', { ascending: true }),
          supabase.from('marketing_campaigns').select('*').order('status', { ascending: true }).order('start_date', { ascending: false }),
          supabase.from('meta_ads_sync_logs').select('*').order('started_at', { ascending: false }).limit(40),
          supabase.from('communication_events').select('*').order('created_at', { ascending: false }).limit(250),
          supabase.from('support_tickets').select('*').order('updated_at', { ascending: false }),
          supabase.from('support_ticket_messages').select('*').order('created_at', { ascending: true }),
          supabase.from('client_satisfaction_surveys').select('*').order('updated_at', { ascending: false }),
          supabase.from('testimonial_requests').select('*').order('updated_at', { ascending: false }),
          supabase.from('landing_assets').select('*').order('section', { ascending: true }).order('sort_order', { ascending: true }),
          supabase.from('portal_assets').select('*').eq('status', 'published').order('scope', { ascending: true }).order('entity_ref', { ascending: true }).order('asset_key', { ascending: true }).order('published_at', { ascending: false }),
          supabase.from('deudas').select('*').order('created_at', { ascending: false }),
          supabase.from('sueldos_programados').select('*').order('fecha_pago', { ascending: true }),
        ]);

        // ── LA AUTORIDAD MONETARIA DE PAQUETE ────────────────────────────────
        //
        // Se registra aparte y no dentro del `Promise.all`: es una cache de
        // `paquetes_cotizacion` que consumen cuatro funciones de modulo -fuera de React-
        // y que NACE VACIA. Si esta lectura falla, el registro se queda vacio y todo
        // precio resuelve «sin resolver», que se niega. No hay valores por defecto.
        try {
          const catalogoCanonico = await supabase
            .from('paquetes_cotizacion')
            .select('id,nombre,family,precio,precio_nacional,precio_internacional,application_count,visible,status');
          if (catalogoCanonico.error) {
            console.error('[Feria OS] Error loading canonical package authority from Supabase', catalogoCanonico.error);
          } else {
            registrarCatalogoCanonico(catalogoCanonico.data || []);
          }
        } catch (error) {
          console.error('[Feria OS] Canonical package authority unavailable', error);
        }

        if (deudasRows.error) {
          console.error('[Feria OS] Error loading debts from Supabase', deudasRows.error);
        }
        if (ingresosRows.error) {
          console.error('[Feria OS] Error loading income registry from Supabase', ingresosRows.error);
        }
        if (suscripcionesRows.error) {
          console.error('[Feria OS] Error loading digital subscriptions from Supabase', suscripcionesRows.error);
        }
        if (gastosFijosRows.error) {
          console.error('[Feria OS] Error loading fixed expenses from Supabase', gastosFijosRows.error);
        }
        if (flujoConfigRows.error) {
          console.error('[Feria OS] Error loading cash flow config from Supabase', flujoConfigRows.error);
        }
        if (sueldosRows.error) {
          console.error('[Feria OS] Error loading scheduled payroll from Supabase', sueldosRows.error);
        }
        if (contratosRows.error) {
          console.error('[Feria OS] Error loading contracts from Supabase', contratosRows.error);
        }
        if (contractParticipantsRows.error) {
          console.error('[Feria OS] Error loading contract participants from Supabase', contractParticipantsRows.error);
        }
        if (contractSignaturesRows.error) {
          console.error('[Feria OS] Error loading contract signatures from Supabase', contractSignaturesRows.error);
        }
        if (cotizacionesRows.error) {
          console.error('[Feria OS] Error loading sent quotes from Supabase', cotizacionesRows.error);
        }
        if (briefsRows.error) {
          console.error('[Feria OS] Error loading briefs from Supabase', briefsRows.error);
        }
        if (briefParticipationRows.error) {
          console.error('[Feria OS] Error loading brief participation from Supabase', briefParticipationRows.error);
        }
        if (clientPortalMembersRows.error) {
          console.error('[Feria OS] Error loading client portal members from Supabase', clientPortalMembersRows.error);
        }
        if (projectApprovalRows.error) {
          console.error('[Feria OS] Error loading project approval records from Supabase', projectApprovalRows.error);
        }
        if (projectApprovalActsRows.error) {
          console.error('[Feria OS] Error loading project approval acts from Supabase', projectApprovalActsRows.error);
        }
        if (projectAdjustmentRows.error) {
          console.error('[Feria OS] Error loading project adjustment requests from Supabase', projectAdjustmentRows.error);
        }
        if (brandkitAssetsRows.error) {
          console.error('[Feria OS] Error loading Brand Kit assets from Supabase', brandkitAssetsRows.error);
        }
        if (internalClientDocumentsRows.error) {
          console.error('[Feria OS] Error loading internal client archive from Supabase', internalClientDocumentsRows.error);
        }
        if (projectDeliveryActsRows.error) {
          console.error('[Feria OS] Error loading project delivery acts from Supabase', projectDeliveryActsRows.error);
        }
        if (brandkitDownloadRows.error) {
          console.error('[Feria OS] Error loading Brand Kit download logs from Supabase', brandkitDownloadRows.error);
        }
        if (communicationEventsRows.error) {
          console.error('[Feria OS] Error loading communication events from Supabase', communicationEventsRows.error);
        }
        if (marketingMetaInsightsRows.error) {
          console.error('[Feria OS] Error loading Meta marketing insights from Supabase', marketingMetaInsightsRows.error);
        }
        if (marketingSourcesRows.error) {
          console.error('[Feria OS] Error loading marketing sources from Supabase', marketingSourcesRows.error);
        }
        if (marketingCampaignsRows.error) {
          console.error('[Feria OS] Error loading marketing campaigns from Supabase', marketingCampaignsRows.error);
        }
        if (supportTicketsRows.error) {
          console.error('[Feria OS] Error loading support tickets from Supabase', supportTicketsRows.error);
        }
        if (supportTicketMessagesRows.error) {
          console.error('[Feria OS] Error loading support ticket messages from Supabase', supportTicketMessagesRows.error);
        }
        if (clientSatisfactionSurveysRows.error) {
          console.error('[Feria OS] Error loading client satisfaction surveys from Supabase', clientSatisfactionSurveysRows.error);
        }
        if (testimonialRequestsRows.error) {
          console.error('[Feria OS] Error loading testimonial requests from Supabase', testimonialRequestsRows.error);
        }
        if (landingAssetsRows.error) {
          console.error('[Feria OS] Error loading landing assets from Supabase', landingAssetsRows.error);
        }
        if (portalAssetsRows.error) {
          console.error('[Feria OS] Error loading portal CDN assets from Supabase', portalAssetsRows.error);
        }

        const diasRestantes = calcDiasRestantes();
        const studioData    = studioRows.data?.[0];
        if (studioData?.id) studioIdRef.current = studioData.id;
        // Bug 2 (hotfix front-only): el cliente NO puede leer `studio` (RLS solo-staff) → studioData
        // null → sin logo. SOLO cuando la lectura de staff vuelve vacia, se lee la MARCA desde la
        // vista publica `studio_public_brand` (grant a authenticated; mismas columnas de marca). El
        // staff NO entra aqui (su from('studio') si trae fila) → fila completa intacta. La vista no
        // expone campos operativos, asi que solo alimenta la marca; el resto cae a defaults.
        let studioBrandRow = studioData;
        if (!studioBrandRow) {
          try {
            const publicBrand = await supabase.from('studio_public_brand').select('*').limit(1);
            if (!publicBrand.error) studioBrandRow = publicBrand.data?.[0] || null;
          } catch { /* sin marca publica: cae a iniciales, como hoy */ }
        }
        const dbBrandAssets = studioBrandRow ? studioBrandAssetsFromRow(studioBrandRow, readStoredStudioBrandAssets()) : readStoredStudioBrandAssets();
        if (studioBrandRow) {
          setStudioBrandAssets(dbBrandAssets);
          writeStoredStudioBrandAssets(dbBrandAssets);
          setStudioLogo(dbBrandAssets.fullDark || dbBrandAssets.fullLight || studioBrandRow.logo_url || null);
          if (dbBrandAssets.accentColor) {
            setAccentColorState(dbBrandAssets.accentColor);
            writeStoredAccentColor(dbBrandAssets.accentColor);
            applyAccentColorCss(dbBrandAssets.accentColor);
          }
        }
        const teamData      = teamRows.data || [];
        if (!teamData.length) {
          // Fallback silencioso hecho RUIDOSO para el desarrollador (criterio portalErrorMessage):
          // la UI muestra el equipo vacío con dignidad, pero 0 filas de `team` casi siempre = fallo
          // de RLS/permisos, no un estudio sin equipo. El crudo va a consola, no a la UI.
          console.error('[Feria OS] La consulta a `team` devolvió 0 filas: se usa el fallback vacío (equipo vacío). Revisar RLS/permisos del usuario.', teamRows.error || '(sin error explícito de Supabase)');
        }

        setData(prev => ({
          ...prev,
          clientes:  clientesRows.length  ? hideDemo(clientesRows, demoMode).map(normalizeClienteRow)  : prev.clientes,
          cobros:    cobrosRows.length    ? hideDemo(cobrosRows, demoMode).map(c => ({ ...c, clienteId: c.cliente_id })) : prev.cobros,
          ingresos: ingresosRows.error ? prev.ingresos || [] : hideDemo(ingresosRows.data || [], demoMode),
          suscripciones: suscripcionesRows.error ? prev.suscripciones || [] : hideDemo(suscripcionesRows.data || [], demoMode),
          gastosFijos: gastosFijosRows.error ? prev.gastosFijos || [] : hideDemo(gastosFijosRows.data || [], demoMode),
          flujoConfig: flujoConfigRows.error ? prev.flujoConfig || [] : hideDemo(flujoConfigRows.data || [], demoMode),
          gastos:    gastosRows.length    ? hideDemo(gastosRows, demoMode)    : prev.gastos,
          leads:     leadsRows.length     ? hideDemo(leadsRows, demoMode)     : prev.leads,
          proyectos: proyectosRows.length ? hideDemo(proyectosRows, demoMode).map(normalizeProyectoRow) : prev.proyectos,
          cotizacionesEnviadas: cotizacionesRows.error ? prev.cotizacionesEnviadas || [] : hideDemo(cotizacionesRows.data || [], demoMode),
          contratos: contratosRows.error ? prev.contratos || [] : hideDemo(contratosRows.data || [], demoMode).map(c => ({ ...c, clienteId: c.cliente_id })),
          contractParticipants: contractParticipantsRows.error ? prev.contractParticipants || [] : hideDemo(contractParticipantsRows.data || [], demoMode),
          contractSignatures: contractSignaturesRows.error ? prev.contractSignatures || [] : hideDemo(contractSignaturesRows.data || [], demoMode),
          briefs: briefsRows.error ? prev.briefs || [] : hideDemo(briefsRows.data || [], demoMode),
          briefParticipation: briefParticipationRows.error ? prev.briefParticipation || [] : hideDemo(briefParticipationRows.data || [], demoMode),
          clientPortalMembers: clientPortalMembersRows.error ? prev.clientPortalMembers || [] : hideDemo(clientPortalMembersRows.data || [], demoMode),
          notificaciones: notifsRows.length ? hideDemo(notifsRows, demoMode).map(normalizeNotification) : prev.notificaciones,
          clientResponsibilities: demoMode ? [] : (clientResponsibilitiesRows || []),
          reuniones: reunionesRows.data?.length ? hideDemo(reunionesRows.data, demoMode) : prev.reuniones || [],
          projectUpdates: projectUpdatesRows.data?.length ? hideDemo(projectUpdatesRows.data, demoMode) : prev.projectUpdates || [],
          projectApplications: projectApplicationsRows.data?.length ? hideDemo(projectApplicationsRows.data, demoMode) : prev.projectApplications || [],
          projectApprovalRecords: projectApprovalRows.error ? prev.projectApprovalRecords || [] : hideDemo(projectApprovalRows.data || [], demoMode),
          projectApprovalActs: projectApprovalActsRows.error ? prev.projectApprovalActs || [] : hideDemo(projectApprovalActsRows.data || [], demoMode),
          projectAdjustmentRequests: projectAdjustmentRows.error ? prev.projectAdjustmentRequests || [] : hideDemo(projectAdjustmentRows.data || [], demoMode),
          brandkitAssets: brandkitAssetsRows.error ? prev.brandkitAssets || [] : hideDemo(brandkitAssetsRows.data || [], demoMode),
          internalClientDocuments: internalClientDocumentsRows.error ? prev.internalClientDocuments || [] : hideDemo(internalClientDocumentsRows.data || [], demoMode),
          projectDeliveryActs: projectDeliveryActsRows.error ? prev.projectDeliveryActs || [] : hideDemo(projectDeliveryActsRows.data || [], demoMode),
          brandkitDownloadLogs: brandkitDownloadRows.error ? prev.brandkitDownloadLogs || [] : hideDemo(brandkitDownloadRows.data || [], demoMode),
          projectChatMessages: projectChatRows.data?.length ? hideDemo(projectChatRows.data, demoMode) : prev.projectChatMessages || [],
          projectUpdateComments: projectCommentsRows.data?.length ? hideDemo(projectCommentsRows.data, demoMode) : prev.projectUpdateComments || [],
          marketingPerformance: marketingPerformanceRows.data?.length ? hideDemo(marketingPerformanceRows.data, demoMode) : prev.marketingPerformance || [],
          marketingMetaInsights: marketingMetaInsightsRows.error ? prev.marketingMetaInsights || [] : hideDemo(marketingMetaInsightsRows.data || [], demoMode),
          marketingSources: marketingSourcesRows.error ? prev.marketingSources || [] : hideDemo(marketingSourcesRows.data || [], demoMode),
          marketingCampaigns: marketingCampaignsRows.error ? prev.marketingCampaigns || [] : hideDemo(marketingCampaignsRows.data || [], demoMode),
          metaAdsSyncLogs: metaAdsSyncLogsRows.error ? prev.metaAdsSyncLogs || [] : hideDemo(metaAdsSyncLogsRows.data || [], demoMode),
          communicationEvents: communicationEventsRows.error ? prev.communicationEvents || [] : hideDemo(communicationEventsRows.data || [], demoMode),
          supportTickets: supportTicketsRows.error ? prev.supportTickets || [] : hideDemo(supportTicketsRows.data || [], demoMode),
          supportTicketMessages: supportTicketMessagesRows.error ? prev.supportTicketMessages || [] : hideDemo(supportTicketMessagesRows.data || [], demoMode),
          clientSatisfactionSurveys: clientSatisfactionSurveysRows.error ? prev.clientSatisfactionSurveys || [] : hideDemo(clientSatisfactionSurveysRows.data || [], demoMode),
          testimonialRequests: testimonialRequestsRows.error ? prev.testimonialRequests || [] : hideDemo(testimonialRequestsRows.data || [], demoMode),
          landingAssets: landingAssetsRows.error ? prev.landingAssets || [] : hideDemo((landingAssetsRows.data || []).map(normalizeLandingAsset), demoMode),
          portalAssets: portalAssetsRows.error ? prev.portalAssets || [] : hideDemo((portalAssetsRows.data || []).map(normalizePortalAsset), demoMode),
          deudas: deudasRows.error ? prev.deudas || [] : hideDemo(deudasRows.data || [], demoMode),
          sueldosProgramados: sueldosRows.error ? prev.sueldosProgramados || [] : hideDemo(sueldosRows.data || [], demoMode),
          studio: {
            // MARCA desde studioBrandRow (staff = fila completa de `studio`; cliente = vista publica).
            // meta_objetivo (financiero) sigue leyendose de studioData: solo staff lo trae (la vista
            // no lo expone); para el cliente queda en su default, como hoy.
            ...normalizeStudioFromRow(studioBrandRow || {}, prev.studio),
            meta: {
              objetivo:     studioData?.meta_objetivo ?? prev.studio.meta.objetivo,
              actual:       0,
              diasRestantes,
            },
          },
          team: teamData.length ? hideDemo(teamData, demoMode).map(normalizeTeamMember) : prev.team.map(normalizeTeamMember),
        }));
        setDbReady(true);
      } catch (err) {
        console.warn('DB no disponible, usando datos locales', err);
        setData(d => ({ ...d, studio: { ...d.studio, meta: { ...d.studio.meta, diasRestantes: calcDiasRestantes() } } }));
      }
    }

    loadFromDB();

    // ── REALTIME ────────────────────────────────────────────────────────────
    // 🔴 UN BINDING SOBRE UNA TABLA NO PUBLICADA MATA EL CANAL ENTERO.
    //
    // Este canal declaraba 15 bindings sobre 8 tablas, pero solo 3 de esas tablas estan en la
    // publicacion `supabase_realtime`. Realtime rechaza el **conjunto completo** de
    // `postgres_changes` cuando uno solo de sus bindings apunta a una tabla no publicada:
    //
    //   "Unable to subscribe to changes with given parameters. Please check Realtime is
    //    enabled for the given connect parameters: [event: INSERT, ..., table: clientes, ...]"
    //
    // Medido contra produccion (2026-08-11): con las 8 tablas, `realtime.subscription` queda
    // en CERO filas; quitando los bindings no publicados, quedan 9 y los eventos llegan.
    //
    // ⚠️ Y `subscribe()` DEVUELVE `SUBSCRIBED` IGUAL. El error viaja por el evento `system`,
    // que nadie escuchaba: por eso el chat del portal no se actualizaba **en silencio**, y
    // durante meses parecio que "el realtime del portal no existe". Existia y estaba muerto.
    //
    // Los bindings de `clientes`, `cobros`, `notificaciones` y `client_responsibilities` se
    // retiran porque NUNCA entregaron un evento (esas 4 tablas no estan publicadas) y ademas
    // anulaban a las 3 que si lo estan. Reponerlos exige primero anadir sus tablas a la
    // publicacion — decision de infraestructura, no de este arreglo.
    const channel = supabase
      .channel('cobros-changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'project_updates' }, (payload) => {
        const inserted = payload.new || {};
        if (!inserted.id || (demoMode && inserted.is_demo)) return;
        setData(d => ({
          ...d,
          projectUpdates: upsertRowById(d.projectUpdates, inserted, { position: 'start', sort: sortNewestFirst }),
        }));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'project_updates' }, (payload) => {
        const updated = payload.new || {};
        if (!updated.id) return;
        setData(d => ({
          ...d,
          projectUpdates: demoMode && updated.is_demo
            ? removeRowById(d.projectUpdates, updated.id)
            : upsertRowById(d.projectUpdates, updated, { position: 'start', sort: sortNewestFirst }),
        }));
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'project_updates' }, (payload) => {
        const deletedId = payload.old?.id;
        if (!deletedId) return;
        setData(d => ({ ...d, projectUpdates: removeRowById(d.projectUpdates, deletedId) }));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'project_update_comments' }, (payload) => {
        const inserted = payload.new || {};
        if (!inserted.id || (demoMode && inserted.is_demo)) return;
        setData(d => ({
          ...d,
          projectUpdateComments: upsertRowById(d.projectUpdateComments, inserted, { position: 'end', sort: sortOldestFirst }),
        }));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'project_update_comments' }, (payload) => {
        const updated = payload.new || {};
        if (!updated.id) return;
        setData(d => ({
          ...d,
          projectUpdateComments: demoMode && updated.is_demo
            ? removeRowById(d.projectUpdateComments, updated.id)
            : upsertRowById(d.projectUpdateComments, updated, { position: 'end', sort: sortOldestFirst }),
        }));
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'project_update_comments' }, (payload) => {
        const deletedId = payload.old?.id;
        if (!deletedId) return;
        setData(d => ({ ...d, projectUpdateComments: removeRowById(d.projectUpdateComments, deletedId) }));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'project_chat_messages' }, (payload) => {
        const inserted = payload.new || {};
        if (!inserted.id || (demoMode && inserted.is_demo)) return;
        setData(d => ({
          ...d,
          projectChatMessages: upsertRowById(d.projectChatMessages, inserted, { position: 'end', sort: sortOldestFirst }),
        }));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'project_chat_messages' }, (payload) => {
        const updated = payload.new || {};
        if (!updated.id) return;
        setData(d => ({
          ...d,
          projectChatMessages: demoMode && updated.is_demo
            ? removeRowById(d.projectChatMessages, updated.id)
            : upsertRowById(d.projectChatMessages, updated, { position: 'end', sort: sortOldestFirst }),
        }));
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'project_chat_messages' }, (payload) => {
        const deletedId = payload.old?.id;
        if (!deletedId) return;
        setData(d => ({ ...d, projectChatMessages: removeRowById(d.projectChatMessages, deletedId) }));
      })
      // El canal ya no puede fallar en silencio: si Realtime rechaza los `postgres_changes`,
      // lo dice por aca. `subscribe()` no lo cuenta — devuelve SUBSCRIBED igual.
      .on('system', {}, (payload) => {
        if (payload?.status === 'error') {
          console.warn('[Feria OS] Realtime rechazo los cambios del canal:', payload?.message || payload);
        }
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [demoMode, authLoading, user?.id, esCliente]);

  const refreshProjectReviewData = useCallback(async (projectId = null) => {
    if (!isConfigured || USE_SEED) return { ok: false, skipped: true };
    const targetProjectId = projectId ? String(projectId) : '';
    try {
      let updatesQuery = supabase.from('project_updates').select('*').order('created_at', { ascending: false });
      let commentsQuery = supabase.from('project_update_comments').select('*').order('created_at', { ascending: true });
      let chatQuery = supabase.from('project_chat_messages').select('*').order('created_at', { ascending: true });
      // 🔴 LA DECISION DEL CLIENTE TAMBIEN SE REFRESCA.
      // Estas dos tablas se cargaban SOLO en el arranque: no estan en el canal realtime
      // (que trae project_updates, sus comentarios y el chat) ni estaban aca. Medido en
      // produccion el 2026-08-11: el cliente pidio ajustes, la fila existia en la BD, y la
      // pantalla de "Avances y revision" del staff NO la mostraba hasta un F5 completo.
      // El equipo podia estar mirando la pantalla con un pedido encima sin verlo nunca.
      let approvalsQuery = supabase.from('project_approval_records').select('*').order('created_at', { ascending: false });
      let adjustmentsQuery = supabase.from('project_adjustment_requests').select('*').order('created_at', { ascending: false });
      if (targetProjectId) {
        updatesQuery = updatesQuery.eq('proyecto_id', targetProjectId);
        commentsQuery = commentsQuery.eq('proyecto_id', targetProjectId);
        chatQuery = chatQuery.eq('proyecto_id', targetProjectId);
        approvalsQuery = approvalsQuery.eq('proyecto_id', targetProjectId);
        adjustmentsQuery = adjustmentsQuery.eq('proyecto_id', targetProjectId);
      }

      const [updatesRows, commentsRows, chatRows, approvalRows, adjustmentRows] = await Promise.all([
        updatesQuery, commentsQuery, chatQuery, approvalsQuery, adjustmentsQuery,
      ]);
      const firstError = updatesRows.error || commentsRows.error || chatRows.error || approvalRows.error || adjustmentRows.error;
      if (firstError) throw firstError;

      const nextUpdates = sortNewestFirst(hideDemo(updatesRows.data || [], demoMode));
      const nextComments = sortOldestFirst(hideDemo(commentsRows.data || [], demoMode));
      const nextChatMessages = sortOldestFirst(hideDemo(chatRows.data || [], demoMode));
      const nextApprovals = sortNewestFirst(hideDemo(approvalRows.data || [], demoMode));
      const nextAdjustments = sortNewestFirst(hideDemo(adjustmentRows.data || [], demoMode));

      setData(d => {
        if (!targetProjectId) {
          return {
            ...d,
            projectUpdates: nextUpdates,
            projectUpdateComments: nextComments,
            projectChatMessages: nextChatMessages,
            projectApprovalRecords: nextApprovals,
            projectAdjustmentRequests: nextAdjustments,
          };
        }
        // Se reemplaza SOLO lo de este proyecto (se filtra lo viejo y se concatena lo
        // nuevo), asi que refrescar no puede duplicar filas ni pisar otros proyectos.
        const isSameProject = row => String(row?.proyecto_id || row?.proyectoId || '') === targetProjectId;
        return {
          ...d,
          projectUpdates: sortNewestFirst([...(d.projectUpdates || []).filter(row => !isSameProject(row)), ...nextUpdates]),
          projectUpdateComments: sortOldestFirst([...(d.projectUpdateComments || []).filter(row => !isSameProject(row)), ...nextComments]),
          projectChatMessages: sortOldestFirst([...(d.projectChatMessages || []).filter(row => !isSameProject(row)), ...nextChatMessages]),
          projectApprovalRecords: sortNewestFirst([...(d.projectApprovalRecords || []).filter(row => !isSameProject(row)), ...nextApprovals]),
          projectAdjustmentRequests: sortNewestFirst([...(d.projectAdjustmentRequests || []).filter(row => !isSameProject(row)), ...nextAdjustments]),
        };
      });
      return { ok: true };
    } catch (error) {
      console.warn('[AppContext.refreshProjectReviewData] No se pudo refrescar avances/comentarios/chat/decisiones:', error?.message || error);
      return { ok: false, error };
    }
  }, [demoMode]);

  const toggleTheme = useCallback((t) => {
    const next = t || (theme === 'dark' ? 'light' : 'dark');
    setTheme(next);
    localStorage.setItem('feria_theme', next);
    applyTheme(next);
  }, [theme]);

  const setAccentColor = useCallback((color) => {
    const value = firstText(color);
    if (!value) return;
    setAccentColorState(value);
    writeStoredAccentColor(value);
    applyAccentColorCss(value);
  }, []);

  const setStudioBrandAsset = useCallback((key, value) => {
    if (!key) return;
    setStudioBrandAssets(prev => {
      const next = { ...(prev || {}), [key]: value || '' };
      writeStoredStudioBrandAssets(next);
      return next;
    });
  }, []);

  const refreshStudioBrandFromDB = useCallback(async () => {
    if (!isConfigured || USE_SEED) {
      return { data: null, assets: readStoredStudioBrandAssets() };
    }
    const { data: rows, error } = await supabase.from('studio').select('*').limit(1);
    if (error) throw error;
    const studioRow = rows?.[0] || null;
    if (studioRow?.id) studioIdRef.current = studioRow.id;
    const assets = studioRow ? applyStudioBrandRow(studioRow, readStoredStudioBrandAssets()) : readStoredStudioBrandAssets();
    return { data: studioRow, assets };
  }, [applyStudioBrandRow]);

  const saveStudioBrandSettings = useCallback(async (assets = {}) => {
    if (!isConfigured || USE_SEED) {
      throw new Error('Supabase no esta configurado para guardar la marca del estudio.');
    }
    const mergedAssets = { ...readStoredStudioBrandAssets(), ...(assets || {}) };
    const payload = studioPayloadFromBrandAssets(mergedAssets);
    let studioId = studioIdRef.current;
    if (!studioId) {
      const { data: rows, error: selectError } = await supabase.from('studio').select('id').limit(1);
      if (selectError) throw selectError;
      studioId = rows?.[0]?.id || null;
      if (studioId) studioIdRef.current = studioId;
    }

    const query = supabase.from('studio').update(payload);
    const { data: savedRows, error } = studioId
      ? await query.eq('id', studioId).select('*').limit(1)
      : await query.not('id', 'is', null).select('*').limit(1);
    if (error) throw error;
    const savedStudio = savedRows?.[0];
    if (!savedStudio) throw new Error('No se pudo confirmar el guardado de marca en public.studio.');
    if (savedStudio.id) studioIdRef.current = savedStudio.id;
    const savedAssets = applyStudioBrandRow(savedStudio, mergedAssets);
    return { data: savedStudio, assets: savedAssets };
  }, [applyStudioBrandRow]);

  const saveStudioPortalSettings = useCallback(async (settings = {}) => {
    const nextPortalShowNews = Boolean(settings.portal_show_news);
    const updatedAt = new Date().toISOString();

    setData(prev => ({
      ...prev,
      studio: {
        ...prev.studio,
        portal_show_news: nextPortalShowNews,
        updated_at: updatedAt,
      },
    }));

    if (!isConfigured || USE_SEED) {
      return { data: null, settings: { portal_show_news: nextPortalShowNews } };
    }

    let studioId = studioIdRef.current;
    if (!studioId) {
      const { data: rows, error: selectError } = await supabase.from('studio').select('id').limit(1);
      if (selectError) throw selectError;
      studioId = rows?.[0]?.id || null;
      if (studioId) studioIdRef.current = studioId;
    }

    const payload = { portal_show_news: nextPortalShowNews, updated_at: updatedAt };
    const query = supabase.from('studio').update(payload);
    const { data: savedRows, error } = studioId
      ? await query.eq('id', studioId).select('*').limit(1)
      : await query.not('id', 'is', null).select('*').limit(1);
    if (error) throw error;
    const savedStudio = savedRows?.[0];
    if (!savedStudio) throw new Error('No se pudo confirmar la configuracion del portal en public.studio.');
    if (savedStudio.id) studioIdRef.current = savedStudio.id;
    applyStudioBrandRow(savedStudio, readStoredStudioBrandAssets());
    return { data: savedStudio, settings: { portal_show_news: Boolean(savedStudio.portal_show_news) } };
  }, [applyStudioBrandRow]);

  const saveStudioArchiveSettings = useCallback(async (settings = {}) => {
    const normalized = normalizeArchiveConfig({ ...data.studio, ...settings });
    const payload = {
      archive_storage_enabled: normalized.enabled,
      archive_storage_provider: normalized.provider,
      archive_storage_bucket: normalized.bucket,
      archive_storage_base_path: normalized.basePath,
      archive_storage_mode: normalized.mode,
      archive_storage_endpoint: normalized.endpoint || null,
      archive_storage_region: normalized.region || null,
      archive_storage_access_key_ref: normalized.accessKeyRef || null,
      archive_storage_secret_key_ref: normalized.secretKeyRef || null,
      updated_at: new Date().toISOString(),
    };

    setData(prev => ({
      ...prev,
      studio: {
        ...prev.studio,
        ...payload,
      },
    }));

    if (!isConfigured || USE_SEED) {
      return { data: null, settings: normalized };
    }

    let studioId = studioIdRef.current;
    if (!studioId) {
      const { data: rows, error: selectError } = await supabase.from('studio').select('id').limit(1);
      if (selectError) throw selectError;
      studioId = rows?.[0]?.id || null;
      if (studioId) studioIdRef.current = studioId;
    }

    const query = supabase.from('studio').update(payload);
    const { data: savedRows, error } = studioId
      ? await query.eq('id', studioId).select('*').limit(1)
      : await query.not('id', 'is', null).select('*').limit(1);
    if (error) throw error;
    const savedStudio = savedRows?.[0];
    if (!savedStudio) throw new Error('No se pudo confirmar la configuracion de archivo interno en public.studio.');
    if (savedStudio.id) studioIdRef.current = savedStudio.id;
    applyStudioBrandRow(savedStudio, readStoredStudioBrandAssets());
    return { data: savedStudio, settings: normalizeArchiveConfig(savedStudio) };
  }, [applyStudioBrandRow, data.studio]);

  const uploadArchiveDocument = useCallback(async (payload = {}) => {
    const clienteId = payload.clienteId || payload.cliente_id;
    const proyectoId = payload.proyectoId || payload.proyecto_id || null;
    const cliente = payload.cliente || (data.clientes || []).find(item => String(item.id) === String(clienteId)) || null;
    const proyecto = payload.proyecto || (data.proyectos || []).find(item => String(item.id) === String(proyectoId)) || null;
    const saved = await archiveStorage.save({
      ...payload,
      clienteId,
      proyectoId,
      cliente,
      proyecto,
      config: data.studio,
      uploadedBy: payload.uploadedBy || user?.id || null,
    });
    setData(prev => ({
      ...prev,
      internalClientDocuments: [saved, ...(prev.internalClientDocuments || []).filter(item => String(item.id) !== String(saved.id))],
    }));
    return saved;
  }, [data.clientes, data.proyectos, data.studio, user?.id]);

  const refreshArchiveDocuments = useCallback(async (clienteId) => {
    if (!isConfigured || USE_SEED || !clienteId) return [];
    const rows = await archiveStorage.listByClient(clienteId);
    setData(prev => ({
      ...prev,
      internalClientDocuments: [
        ...rows,
        ...(prev.internalClientDocuments || []).filter(item => String(item.cliente_id || item.clienteId) !== String(clienteId)),
      ],
    }));
    return rows;
  }, []);

  const getArchiveSignedUrl = useCallback(async (documentId) => {
    const existingDocument = (data.internalClientDocuments || []).find(item => String(item.id) === String(documentId));
    const document = existingDocument || await archiveStorage.get(documentId);
    if (isSensitiveArchiveDocument(document)) {
      const role = String(user?.role || user?.perms || '').toLowerCase();
      if (!['admin', 'crm'].includes(role)) {
        throw new Error('Este documento sensible solo puede abrirlo administracion o CRM.');
      }
      const contractId = document.contract_id || document.metadata?.contract_id;
      if (document.source !== 'client_identity_uploaded' || !contractId) {
        throw new Error('Este documento sensible requiere acceso auditado y no tiene contrato asociado.');
      }
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Inicia sesion para abrir este documento sensible.');
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
      const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
      if (!supabaseUrl || !anonKey) throw new Error('Supabase no esta configurado para acceso auditado.');
      const response = await fetch(`${supabaseUrl.replace(/\/+$/, '')}/functions/v1/get-contract-identity-document`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: anonKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contract_id: contractId }),
      });
      if (!response.ok) {
        let message = 'No se pudo abrir el documento sensible.';
        try {
          const payload = await response.json();
          message = payload?.error || message;
        } catch (_) {}
        throw new Error(message);
      }
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    }
    if (isFinancialArchiveDocument(document)) {
      const role = String(user?.role || user?.perms || '').toLowerCase();
      if (!['admin', 'crm', 'finanzas'].includes(role)) {
        throw new Error('Este documento financiero solo puede abrirlo administracion, CRM o finanzas.');
      }
    }
    if (isLegalArchiveDocument(document)) {
      const role = String(user?.role || user?.perms || '').toLowerCase();
      if (!['admin', 'crm', 'finanzas'].includes(role)) {
        throw new Error('Este documento legal solo puede abrirlo administracion, CRM o finanzas.');
      }
    }
    const result = await archiveStorage.getSignedUrl(documentId);
    return result.signedUrl;
  }, [data.internalClientDocuments, user?.perms, user?.role]);

  const archiveInternalDocument = useCallback(async (documentId) => {
    const updated = await archiveStorage.deleteOrArchive(documentId);
    setData(prev => ({
      ...prev,
      internalClientDocuments: (prev.internalClientDocuments || []).map(item => String(item.id) === String(updated.id) ? updated : item),
    }));
    return updated;
  }, []);

  const archiveBrandkitAssetForInternalArchive = useCallback((asset = {}, reason = 'brandkit_asset_saved') => {
    const assetId = asset?.id;
    const bucket = asset?.storage_bucket || 'brandkit-assets';
    const storagePath = asset?.storage_path || '';
    if (!isConfigured || USE_SEED || demoMode || asset?.is_demo || !assetId || bucket !== 'brandkit-assets' || !storagePath) return;

    supabase.functions.invoke('archive-brandkit-asset', {
      body: { brandkit_asset_id: assetId },
    }).then(({ data: result, error }) => {
      if (error || result?.ok === false) {
        console.warn('[AppContext.archiveBrandkitAssetForInternalArchive] Asset Brand Kit guardado, pero no se pudo archivar automaticamente:', {
          reason,
          asset_id: assetId,
          error: result?.error || error?.message || error,
        });
        return;
      }
      const archivedDocument = result?.archive || result?.document || null;
      if (archivedDocument?.id) {
        setData(prev => ({
          ...prev,
          internalClientDocuments: [
            archivedDocument,
            ...(prev.internalClientDocuments || []).filter(item => String(item.id) !== String(archivedDocument.id)),
          ],
        }));
      }
    }).catch((archiveError) => {
      console.warn('[AppContext.archiveBrandkitAssetForInternalArchive] Asset Brand Kit guardado, pero el archivo interno fallo:', {
        reason,
        asset_id: assetId,
        error: archiveError?.message || archiveError,
      });
    });
  }, [demoMode]);

  useEffect(() => {
    applyTheme(theme);
    setAccentColor(accentColor);
  }, [theme, accentColor, setAccentColor]);

  const showToast = useCallback((msg, icon = '✦') => {
    setToast({ msg, icon, id: Date.now() });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const addNotification = useCallback(async (notification = {}) => {
    const generatedId = crypto.randomUUID?.();
    const payload = createInternalNotificationPayload(notification);
    const row = normalizeNotification({
      id: generatedId || `${Date.now()}-notification`,
      tipo: notification.tipo || 'interno',
      titulo: notification.titulo || 'Notificación interna',
      desc: notification.desc || notification.descripcion || '',
      descripcion: notification.descripcion || notification.desc || '',
      leida: notification.leida ?? false,
      ...payload,
      ...notification,
    });

    if (isConfigured && !USE_SEED && !demoMode && !row.is_demo && payload.dedupe_key) {
      try {
        const { data: existing, error: existingError } = await supabase
          .from('notificaciones')
          .select('*')
          .eq('dedupe_key', payload.dedupe_key)
          .limit(1)
          .maybeSingle();
        if (!existingError && existing?.id) {
          const normalizedExisting = normalizeNotification(existing);
          setData(d => ({
            ...d,
            notificaciones: (d.notificaciones || []).some(n => String(n.id) === String(existing.id))
              ? d.notificaciones
              : [normalizedExisting, ...(d.notificaciones || [])],
          }));
          return normalizedExisting;
        }
      } catch (error) {
        console.warn('[AppContext.addNotification] No se pudo validar dedupe de notificacion:', error?.message || error);
      }
    }

    setData(d => ({ ...d, notificaciones: [row, ...(d.notificaciones || [])] }));

    // El resultado de persistir viaja EN el row devuelto, no como un objeto distinto:
    // addNotification se usa en ~67 lugares y varios meten lo que devuelve directo en
    // la lista de notificaciones, asi que cambiarle la forma los romperia. Con esto,
    // quien quiera saber si se guardo mira `persisted`; el resto sigue igual.
    // NUNCA lanza: una notificacion interna que falla no puede tumbar la accion del usuario.
    let persisted = true;
    let persistError = null;

    if (isConfigured && !USE_SEED && !demoMode && !row.is_demo) {
      const dbRow = {
        ...(generatedId ? { id: generatedId } : {}),
        ...payload,
      };
      try {
        const { error } = await supabase.from('notificaciones').insert([dbRow]);
        if (error) {
          persisted = false;
          persistError = error?.message || String(error);
          console.error('[AppContext.addNotification] No se pudo guardar notificacion:', error);
        }
      } catch (error) {
        persisted = false;
        persistError = error?.message || String(error);
        console.error('[AppContext.addNotification] Error guardando notificacion:', error);
      }
    }

    return { ...row, persisted, persistError };
  }, [demoMode]);

  // El centro de avisos compara `recipient_user_id` contra `profiles.id`
  // (Notificaciones/index.jsx:27), pero `creativo_id`, `assigned_reviewer_id` y
  // `assigned_to` guardan `team.id`. Sin traducir, la notificacion se guarda y no le
  // llega a nadie: al revisar la BD no habia UNA sola fila con un profiles.id valido.
  // Falla ruidoso a proposito: si no resuelve exactamente un perfil, no se crea una
  // notificacion sin destinatario, que es justo el bug que esto viene a cerrar.
  const resolveProfileIdForTeamMember = useCallback(async (teamId) => {
    if (!teamId) return { ok: false, error: 'No hay destinatario asignado.' };
    const member = (data.team || []).find(m => String(m.id) === String(teamId));
    const email = String(member?.email || '').trim().toLowerCase();
    if (!email) return { ok: false, error: `El miembro ${teamId} no tiene email en team.` };
    if (!isConfigured || USE_SEED || demoMode) return { ok: true, profileId: teamId };
    const { data: rows, error } = await supabase.from('profiles').select('id').ilike('email', email);
    if (error) return { ok: false, error: error.message || 'No se pudo leer profiles.' };
    if (!rows?.length) return { ok: false, error: `No hay perfil para ${email}.` };
    if (rows.length > 1) return { ok: false, error: `Hay ${rows.length} perfiles con ${email}.` };
    return { ok: true, profileId: rows[0].id };
  }, [data.team, demoMode]);

  // Envoltorio: resuelve el destinatario y solo entonces notifica. Si no resuelve,
  // avisa en pantalla en vez de dejar la notificacion muda.
  const notifyTeamMember = useCallback(async (teamId, payload = {}, contexto = 'la notificacion') => {
    const destino = await resolveProfileIdForTeamMember(teamId);
    if (!destino.ok) {
      console.error('[AppContext.notifyTeamMember] destinatario sin resolver:', destino.error);
      showToast(`No se pudo avisar al responsable de ${contexto}: ${destino.error}`, '!');
      return { ok: false, error: destino.error };
    }
    await addNotification({ ...payload, recipient_user_id: destino.profileId });
    return { ok: true };
  }, [addNotification, resolveProfileIdForTeamMember, showToast]);

  const dispatchCrmTransitionCommunication = useCallback(({ cliente, from, to, trigger }) => {
    if (!trigger || !to) return;

    const clienteId = cliente?.id || null;
    const clienteNombre = cliente?.nombre || cliente?.empresa || 'Cliente';
    const recipientEmail = cliente?.email || '';
    const recipientPhone = cliente?.whatsapp || cliente?.telefono || '';
    const isDemo = Boolean(cliente?.is_demo || demoMode || USE_SEED);
    const eventBase = `crm_${String(trigger.phase || to.id).toLowerCase()}`;
    const payload = {
      cliente_id: clienteId,
      clienteId,
      cliente_nombre: clienteNombre,
      clienteNombre,
      brand: cliente?.brand || 'feria',
      previous_phase: from?.id || null,
      previous_phase_label: from?.label || '',
      next_phase: to.id,
      next_phase_label: to.label,
      crm_stage: getCrmPhaseIndex(to, cliente?.stage || 0),
      is_demo: isDemo,
    };

    const tasks = [];

    if (trigger.internal_notification) {
      tasks.push(addNotification({
        tipo: 'crm_phase_transition',
        titulo: `CRM: ${to.label}`,
        desc: trigger.internal_notification,
        descripcion: trigger.internal_notification,
        module: 'crm',
        route: '/crm',
        priority: 'normal',
        cliente_id: clienteId,
        entity_type: 'cliente',
        entity_id: clienteId,
        metadata: payload,
        is_demo: isDemo,
      }));
    }

    if (trigger.email_template && recipientEmail) {
      tasks.push(dispatchCriticalCommunication({
        eventKey: `${eventBase}_email`,
        channel: 'email',
        templateKey: trigger.email_template,
        recipientEmail,
        payload,
        clienteId,
        isDemo,
      }));
    }

    if (trigger.whatsapp_template && recipientPhone) {
      tasks.push(dispatchCriticalCommunication({
        eventKey: `${eventBase}_whatsapp`,
        channel: 'whatsapp',
        templateKey: trigger.whatsapp_template,
        recipientPhone,
        payload,
        clienteId,
        isDemo,
      }));
    }

    if (!tasks.length) return;

    Promise.allSettled(tasks).then(results => {
      results.forEach(result => {
        if (result.status === 'rejected') {
          console.warn('[AppContext.crmStateMachine] Comunicacion CRM fallida:', result.reason);
          return;
        }
        if (result.value?.ok === false) {
          console.warn('[AppContext.crmStateMachine] Comunicacion CRM no entregada:', result.value);
        }
      });
    });
  }, [addNotification, demoMode]);

  const advanceClientPhase = useCallback(async ({
    clienteId,
    targetStage,
    source = 'appcontext',
    actorId = user?.id || null,
    metadata = {},
    relatedEntityType = null,
    relatedEntityId = null,
    allowOverride = false,
    allowBackwards = false,
    allowCompound = false,
    silent = false,
  } = {}) => {
    const currentCliente = (data.clientes || []).find(c => String(c.id) === String(clienteId));
    if (!currentCliente) {
      const error = `Cliente ${clienteId || ''} no encontrado para avanzar fase CRM.`;
      console.warn('[AppContext.advanceClientPhase]', error);
      if (!silent) showToast('No se pudo validar la fase del cliente.', '!');
      return { ok: false, error };
    }

    const plan = buildCrmTransitionPlan(currentCliente.stage ?? 0, targetStage, {
      allowOverride,
      allowBackwards,
      allowCompound,
    });

    if (!plan.ok) {
      console.warn('[AppContext.advanceClientPhase]', plan.error);
      if (!silent) showToast('No se puede avanzar esa fase del CRM.', '!');
      return { ok: false, error: plan.error };
    }

    let remoteResult = { ok: true, data: null };
    const shouldPersist = isConfigured && !USE_SEED && !demoMode && !currentCliente?.is_demo;

    if (shouldPersist) {
      remoteResult = await callAdvanceClientPhaseRpc(supabase, {
        clienteId,
        targetStage: plan.to.index,
        source,
        actorId,
        metadata: {
          ...(metadata && typeof metadata === 'object' ? metadata : {}),
          compound: Boolean(plan.compound),
          steps: plan.steps.map(step => ({ from: step.from?.id, to: step.to?.id })),
        },
        relatedEntityType,
        relatedEntityId,
        allowOverride,
        allowBackwards,
        allowCompound,
      });

      if (!remoteResult.ok) {
        console.error('[AppContext.advanceClientPhase] RPC rechazo transicion:', remoteResult.error || remoteResult);
        if (!silent) showToast('No se pudo avanzar la fase del CRM. Intenta de nuevo.', '!');
        return remoteResult;
      }
    }

    setData(d => ({
      ...d,
      clientes: (d.clientes || []).map(c =>
        String(c.id) === String(clienteId)
          ? { ...c, stage: plan.to.index, updated_at: new Date().toISOString() }
          : c
      ),
    }));

    if (!plan.noop) {
      const communicationSteps = plan.compound ? plan.steps.slice(-1) : plan.steps;
      communicationSteps.forEach(step => {
        const trigger = getCrmTransitionTrigger(step.to?.id);
        if (!trigger) return;
        dispatchCrmTransitionCommunication({
          cliente: { ...currentCliente, stage: step.to.index },
          from: step.from,
          to: step.to,
          trigger,
        });
      });
    }

    return {
      ok: true,
      data: {
        ...(remoteResult.data || {}),
        from_stage: plan.from.index,
        to_stage: plan.to.index,
        steps: plan.steps,
        noop: Boolean(plan.noop),
      },
    };
  }, [data.clientes, demoMode, dispatchCrmTransitionCommunication, showToast, user?.id]);

  const refreshSupportTickets = useCallback(async () => {
    if (!isConfigured || USE_SEED || demoMode) return {
      tickets: data.supportTickets || [],
      messages: data.supportTicketMessages || [],
    };
    const [ticketsResult, messagesResult] = await Promise.all([
      supabase.from('support_tickets').select('*').order('updated_at', { ascending: false }),
      supabase.from('support_ticket_messages').select('*').order('created_at', { ascending: true }),
    ]);
    if (ticketsResult.error) throw ticketsResult.error;
    if (messagesResult.error) throw messagesResult.error;
    const tickets = ticketsResult.data || [];
    const messages = messagesResult.data || [];
    setData(d => ({ ...d, supportTickets: hideDemo(tickets, demoMode), supportTicketMessages: hideDemo(messages, demoMode) }));
    return { tickets, messages };
  }, [data.supportTicketMessages, data.supportTickets, demoMode]);

  /**
   * REVALIDACION ACOTADA DEL PIPELINE DE COTIZACIONES.
   *
   * El pipeline de /cotizaciones no consulta nada por su cuenta: pinta
   * `data.cotizacionesEnviadas` y compone cada señal con `buildQuoteOperationSnapshot`,
   * que ademas lee clientes, cobros, proyectos, contratos, briefs, reuniones y
   * `communicationEvents`. Con la pantalla abierta, una interaccion del cliente
   * (una vista del enlace publico, un pago, una cita) cambiaba la BD y la
   * pantalla seguia mostrando lo de antes hasta que alguien apretaba F5.
   *
   * ── POR QUE UN REFETCH ACOTADO Y NO EL LOAD COMPLETO ───────────────────────
   *
   * La carga canonica de `AppContext` son 46 consultas: marketing, sueldos,
   * gastos, equipo, assets... Repetirla cada vez que un cliente abre su
   * propuesta convertiria una pantalla abierta en una tormenta de consultas
   * para refrescar un contador. Aqui se repiten SOLO las ocho lecturas de las
   * que depende una fila del pipeline.
   *
   * ── Y POR QUE NO ES UNA SEGUNDA VERDAD ─────────────────────────────────────
   *
   * Cada consulta y cada asignacion se reproducen IDENTICAS a las del load
   * canonico: mismo `select`, mismo `order`, mismo `limit`, mismo `hideDemo`,
   * mismos normalizadores y la misma guarda que conserva `prev` cuando la
   * respuesta viene vacia o con error. No hay un segundo criterio sobre que es
   * verdad; hay la misma lectura, disparada otra vez.
   *
   * Si falla, LANZA y no toca `setData`: la pantalla se queda con el ultimo
   * snapshot bueno en vez de vaciarse. Quien llama decide si reintenta.
   */
  const refreshQuotesPipeline = useCallback(async () => {
    if (!isConfigured || USE_SEED || demoMode) return false;
    const [
      clientesRows, cobrosRows, proyectosRows, cotizacionesRows,
      contratosRows, briefsRows, reunionesRows, communicationEventsRows,
    ] = await Promise.all([
      db.clientes.getAll(esCliente),
      db.cobros.getAll(),
      db.proyectos.getAll(),
      supabase.from('cotizaciones_enviadas').select('*').order('created_at', { ascending: false }),
      supabase.from('contratos').select('*, clase_de_origen').order('created_at', { ascending: false }),
      supabase.from('briefs').select('*, clase_de_origen').order('created_at', { ascending: false }),
      supabase.from('reuniones').select('*').order('fecha', { ascending: true }),
      supabase.from('communication_events').select('*').order('created_at', { ascending: false }).limit(250),
    ]);
    setData(prev => ({
      ...prev,
      clientes:  clientesRows.length  ? hideDemo(clientesRows, demoMode).map(normalizeClienteRow)  : prev.clientes,
      cobros:    cobrosRows.length    ? hideDemo(cobrosRows, demoMode).map(c => ({ ...c, clienteId: c.cliente_id })) : prev.cobros,
      proyectos: proyectosRows.length ? hideDemo(proyectosRows, demoMode).map(normalizeProyectoRow) : prev.proyectos,
      cotizacionesEnviadas: cotizacionesRows.error ? prev.cotizacionesEnviadas || [] : hideDemo(cotizacionesRows.data || [], demoMode),
      contratos: contratosRows.error ? prev.contratos || [] : hideDemo(contratosRows.data || [], demoMode).map(c => ({ ...c, clienteId: c.cliente_id })),
      briefs: briefsRows.error ? prev.briefs || [] : hideDemo(briefsRows.data || [], demoMode),
      reuniones: reunionesRows.data?.length ? hideDemo(reunionesRows.data, demoMode) : prev.reuniones || [],
      communicationEvents: communicationEventsRows.error ? prev.communicationEvents || [] : hideDemo(communicationEventsRows.data || [], demoMode),
    }));
    return true;
  }, [demoMode, esCliente]);

  const addSupportTicket = useCallback(async (ticket = {}) => {
    const now = new Date().toISOString();
    const row = {
      id: ticket.id || crypto.randomUUID?.() || Date.now().toString(),
      cliente_id: ticket.cliente_id || ticket.clienteId || null,
      proyecto_id: ticket.proyecto_id || ticket.proyectoId || null,
      created_by: ticket.created_by || user?.id || null,
      assigned_to: ticket.assigned_to || null,
      title: String(ticket.title || '').trim(),
      description: ticket.description || '',
      category: ticket.category || 'other',
      priority: ticket.priority || 'medium',
      status: ticket.status || 'open',
      source: ticket.source || 'postventa',
      resolution_summary: ticket.resolution_summary || null,
      created_at: ticket.created_at || now,
      updated_at: now,
    };
    if (!row.title) throw new Error('Titulo del ticket requerido.');
    if (!row.cliente_id) throw new Error('Selecciona un cliente para el ticket.');

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, supportTickets: [row, ...(d.supportTickets || [])] }));
      return row;
    }

    const { data: inserted, error } = await supabase
      .from('support_tickets')
      .insert([filterSupportTicketPayload(row)])
      .select()
      .single();
    if (error) throw error;
    const saved = inserted || row;
    setData(d => ({ ...d, supportTickets: [saved, ...(d.supportTickets || [])] }));

    const createdByClient = saved.source === 'client_portal' || String(user?.role || '').toLowerCase() === 'cliente';
    if (createdByClient) {
      await addNotification({
        tipo: 'support_ticket_created',
        titulo: 'Nuevo ticket de soporte',
        descripcion: saved.title,
        module: 'postventa',
        owner: 'CRM',
        recipient_role: 'crm',
        priority: saved.priority === 'urgent' ? 'high' : 'medium',
        route: '/postventa?tab=tickets',
        entity_type: 'support_ticket',
        entity_id: saved.id,
        cliente_id: saved.cliente_id,
        proyecto_id: saved.proyecto_id,
        dedupe_key: `support_ticket_created:${saved.id}`,
      });
    }

    if (saved.assigned_to) {
      await notifyTeamMember(saved.assigned_to, {
        tipo: 'support_ticket_assigned',
        titulo: 'Ticket de soporte asignado',
        descripcion: saved.title,
        module: 'postventa',
        owner: 'Postventa',
        priority: saved.priority === 'urgent' ? 'high' : 'medium',
        route: '/postventa?tab=tickets',
        entity_type: 'support_ticket',
        entity_id: saved.id,
        cliente_id: saved.cliente_id,
        proyecto_id: saved.proyecto_id,
        dedupe_key: `support_ticket_assigned:${saved.id}:${saved.assigned_to}`,
      }, 'el ticket de soporte');
    }

    return saved;
  }, [addNotification, notifyTeamMember, demoMode, user?.id, user?.role]);

  const updateSupportTicket = useCallback(async (id, changes = {}) => {
    const current = (data.supportTickets || []).find(row => String(row.id) === String(id)) || {};
    const patch = {
      ...changes,
      updated_at: new Date().toISOString(),
    };
    delete patch.id;
    delete patch.created_at;
    delete patch.created_by;

    if (!isConfigured || USE_SEED || demoMode) {
      const saved = { ...current, ...patch };
      setData(d => ({ ...d, supportTickets: (d.supportTickets || []).map(row => String(row.id) === String(id) ? saved : row) }));
      return saved;
    }

    const { data: updated, error } = await supabase
      .from('support_tickets')
      .update(filterSupportTicketPayload(patch))
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    const saved = updated || { ...current, ...patch };
    setData(d => ({ ...d, supportTickets: (d.supportTickets || []).map(row => String(row.id) === String(id) ? saved : row) }));

    if (changes.assigned_to && String(changes.assigned_to) !== String(current.assigned_to || '')) {
      await notifyTeamMember(changes.assigned_to, {
        tipo: 'support_ticket_assigned',
        titulo: 'Ticket de soporte asignado',
        descripcion: saved.title,
        module: 'postventa',
        owner: 'Postventa',
        priority: saved.priority === 'urgent' ? 'high' : 'medium',
        route: '/postventa?tab=tickets',
        entity_type: 'support_ticket',
        entity_id: saved.id,
        cliente_id: saved.cliente_id,
        proyecto_id: saved.proyecto_id,
        dedupe_key: `support_ticket_assigned:${saved.id}:${changes.assigned_to}`,
      }, 'el ticket de soporte');
    }

    if (changes.priority === 'urgent' && current.priority !== 'urgent') {
      await addNotification({
        tipo: 'support_ticket_urgent',
        titulo: 'Ticket urgente',
        descripcion: saved.title,
        module: 'postventa',
        owner: 'CRM',
        recipient_role: 'crm',
        priority: 'high',
        route: '/postventa?tab=tickets',
        entity_type: 'support_ticket',
        entity_id: saved.id,
        cliente_id: saved.cliente_id,
        proyecto_id: saved.proyecto_id,
        dedupe_key: `support_ticket_urgent:${saved.id}`,
      });
    }

    if (changes.status === 'resolved' && current.status !== 'resolved') {
      await addNotification({
        tipo: 'support_ticket_resolved',
        titulo: 'Ticket resuelto',
        descripcion: saved.title,
        module: 'postventa',
        owner: 'CRM',
        priority: 'low',
        route: '/postventa?tab=tickets',
        entity_type: 'support_ticket',
        entity_id: saved.id,
        cliente_id: saved.cliente_id,
        proyecto_id: saved.proyecto_id,
        dedupe_key: `support_ticket_resolved:${saved.id}`,
      });
    }

    return saved;
  }, [addNotification, notifyTeamMember, data.supportTickets, demoMode]);

  const addSupportTicketMessage = useCallback(async (ticketId, message = {}) => {
    const ticket = (data.supportTickets || []).find(row => String(row.id) === String(ticketId)) || {};
    const now = new Date().toISOString();
    const row = {
      id: message.id || crypto.randomUUID?.() || Date.now().toString(),
      ticket_id: ticketId,
      author_id: message.author_id || user?.id || null,
      author_role: message.author_role || user?.role || user?.perms || null,
      message: String(message.message || '').trim(),
      is_internal: Boolean(message.is_internal),
      attachment_bucket: message.attachment_bucket || null,
      attachment_path: message.attachment_path || null,
      attachment_name: message.attachment_name || null,
      attachment_type: message.attachment_type || null,
      attachment_size: message.attachment_size || null,
      created_at: message.created_at || now,
    };
    if (!row.message && !row.attachment_path) throw new Error('Escribe un mensaje o adjunta un archivo.');

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({
        ...d,
        supportTicketMessages: [...(d.supportTicketMessages || []), row],
        supportTickets: (d.supportTickets || []).map(item => String(item.id) === String(ticketId) ? { ...item, updated_at: now } : item),
      }));
      return row;
    }

    const { data: inserted, error } = await supabase
      .from('support_ticket_messages')
      .insert([filterSupportTicketMessagePayload(row)])
      .select()
      .single();
    if (error) throw error;
    const saved = inserted || row;
    setData(d => ({
      ...d,
      supportTicketMessages: [...(d.supportTicketMessages || []), saved],
      supportTickets: (d.supportTickets || []).map(item => String(item.id) === String(ticketId) ? { ...item, updated_at: now } : item),
    }));

    const authorRole = String(row.author_role || '').toLowerCase();
    if (authorRole === 'cliente') {
      await addNotification({
        tipo: 'support_ticket_client_reply',
        titulo: 'Respuesta de cliente en soporte',
        descripcion: ticket.title || row.message,
        module: 'postventa',
        owner: 'CRM',
        recipient_role: 'crm',
        priority: ticket.priority === 'urgent' ? 'high' : 'medium',
        route: '/postventa?tab=tickets',
        entity_type: 'support_ticket',
        entity_id: ticketId,
        cliente_id: ticket.cliente_id,
        proyecto_id: ticket.proyecto_id,
        dedupe_key: `support_ticket_client_reply:${saved.id}`,
      });
    }

    return saved;
  }, [addNotification, data.supportTickets, demoMode, user?.id, user?.perms, user?.role]);

  const addClientSatisfactionSurvey = useCallback(async (survey = {}) => {
    const now = new Date().toISOString();
    const clienteId = survey.cliente_id || survey.clienteId || null;
    const proyectoId = survey.proyecto_id || survey.proyectoId || null;
    const existingOpen = (data.clientSatisfactionSurveys || []).find(row => (
      String(row.cliente_id || row.clienteId || '') === String(clienteId || '') &&
      String(row.proyecto_id || row.proyectoId || '') === String(proyectoId || '') &&
      ['pending', 'sent'].includes(String(row.status || 'pending').toLowerCase())
    ));
    if (existingOpen?.id) return existingOpen;
    const row = {
      id: survey.id || crypto.randomUUID?.() || Date.now().toString(),
      cliente_id: clienteId,
      proyecto_id: proyectoId,
      score: survey.score ?? null,
      nps_score: survey.nps_score ?? null,
      feedback: survey.feedback || null,
      would_recommend: survey.would_recommend ?? null,
      status: survey.status || 'pending',
      sent_at: survey.sent_at || null,
      completed_at: survey.completed_at || null,
      created_at: survey.created_at || now,
      updated_at: now,
    };
    if (!row.cliente_id) throw new Error('Selecciona un cliente para la encuesta.');

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, clientSatisfactionSurveys: [row, ...(d.clientSatisfactionSurveys || [])] }));
      return row;
    }

    const { data: inserted, error } = await supabase
      .from('client_satisfaction_surveys')
      .insert([filterClientSatisfactionSurveyPayload(row)])
      .select()
      .single();
    if (error) {
      if (error.code === '23505') {
        let query = supabase
          .from('client_satisfaction_surveys')
          .select('*')
          .eq('cliente_id', row.cliente_id)
          .in('status', ['pending', 'sent'])
          .limit(1);
        query = row.proyecto_id ? query.eq('proyecto_id', row.proyecto_id) : query.is('proyecto_id', null);
        const { data: existingRows, error: existingError } = await query;
        if (!existingError && existingRows?.[0]) return existingRows[0];
      }
      throw error;
    }
    const saved = inserted || row;
    setData(d => ({ ...d, clientSatisfactionSurveys: [saved, ...(d.clientSatisfactionSurveys || [])] }));
    return saved;
  }, [data.clientSatisfactionSurveys, demoMode]);

  const updateClientSatisfactionSurvey = useCallback(async (id, changes = {}) => {
    const current = (data.clientSatisfactionSurveys || []).find(row => String(row.id) === String(id)) || {};
    const patch = {
      ...changes,
      updated_at: new Date().toISOString(),
    };
    delete patch.id;
    delete patch.created_at;

    if (!isConfigured || USE_SEED || demoMode) {
      const saved = { ...current, ...patch };
      setData(d => ({ ...d, clientSatisfactionSurveys: (d.clientSatisfactionSurveys || []).map(row => String(row.id) === String(id) ? saved : row) }));
      return saved;
    }

    const { data: updated, error } = await supabase
      .from('client_satisfaction_surveys')
      .update(filterClientSatisfactionSurveyPayload(patch))
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    const saved = updated || { ...current, ...patch };
    setData(d => ({ ...d, clientSatisfactionSurveys: (d.clientSatisfactionSurveys || []).map(row => String(row.id) === String(id) ? saved : row) }));

    if (changes.status === 'completed' && current.status !== 'completed') {
      const score = Number(saved.nps_score);
      await addNotification({
        tipo: 'postventa_nps_completed',
        titulo: score <= 6 ? 'NPS con alerta' : 'Encuesta NPS completada',
        descripcion: `NPS ${Number.isFinite(score) ? score : 'recibido'}${saved.feedback ? ' con feedback del cliente' : ''}`,
        module: 'postventa',
        owner: 'CRM',
        recipient_role: 'crm',
        priority: Number.isFinite(score) && score <= 6 ? 'high' : 'medium',
        route: '/postventa?tab=satisfaccion',
        entity_type: 'client_satisfaction_survey',
        entity_id: saved.id,
        cliente_id: saved.cliente_id,
        proyecto_id: saved.proyecto_id,
        dedupe_key: `postventa_nps_completed:${saved.id}`,
      });
    }

    return saved;
  }, [addNotification, data.clientSatisfactionSurveys, demoMode]);

  const addTestimonialRequest = useCallback(async (request = {}) => {
    const now = new Date().toISOString();
    const clienteId = request.cliente_id || request.clienteId || null;
    const proyectoId = request.proyecto_id || request.proyectoId || null;
    const existingOpen = (data.testimonialRequests || []).find(row => (
      String(row.cliente_id || row.clienteId || '') === String(clienteId || '') &&
      String(row.proyecto_id || row.proyectoId || '') === String(proyectoId || '') &&
      ['pending', 'requested', 'received'].includes(String(row.status || 'requested').toLowerCase())
    ));
    if (existingOpen?.id) return existingOpen;
    const row = {
      id: request.id || crypto.randomUUID?.() || Date.now().toString(),
      cliente_id: clienteId,
      proyecto_id: proyectoId,
      status: request.status || 'requested',
      testimonial_text: request.testimonial_text || null,
      public_name: request.public_name || null,
      permission_to_publish: Boolean(request.permission_to_publish),
      requested_at: request.requested_at || now,
      received_at: request.received_at || null,
      approved_at: request.approved_at || null,
      published_at: request.published_at || null,
      created_at: request.created_at || now,
      updated_at: now,
    };
    if (!row.cliente_id) throw new Error('Selecciona un cliente para la solicitud.');

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, testimonialRequests: [row, ...(d.testimonialRequests || [])] }));
      return row;
    }

    const { data: inserted, error } = await supabase
      .from('testimonial_requests')
      .insert([filterTestimonialRequestPayload(row)])
      .select()
      .single();
    if (error) {
      if (error.code === '23505') {
        let query = supabase
          .from('testimonial_requests')
          .select('*')
          .eq('cliente_id', row.cliente_id)
          .in('status', ['pending', 'requested', 'received'])
          .limit(1);
        query = row.proyecto_id ? query.eq('proyecto_id', row.proyecto_id) : query.is('proyecto_id', null);
        const { data: existingRows, error: existingError } = await query;
        if (!existingError && existingRows?.[0]) return existingRows[0];
      }
      throw error;
    }
    const saved = inserted || row;
    setData(d => ({ ...d, testimonialRequests: [saved, ...(d.testimonialRequests || [])] }));
    return saved;
  }, [data.testimonialRequests, demoMode]);

  const updateTestimonialRequest = useCallback(async (id, changes = {}) => {
    const current = (data.testimonialRequests || []).find(row => String(row.id) === String(id)) || {};
    const patch = {
      ...changes,
      updated_at: new Date().toISOString(),
    };
    delete patch.id;
    delete patch.created_at;

    if (!isConfigured || USE_SEED || demoMode) {
      const saved = { ...current, ...patch };
      setData(d => ({ ...d, testimonialRequests: (d.testimonialRequests || []).map(row => String(row.id) === String(id) ? saved : row) }));
      return saved;
    }

    const { data: updated, error } = await supabase
      .from('testimonial_requests')
      .update(filterTestimonialRequestPayload(patch))
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    const saved = updated || { ...current, ...patch };
    setData(d => ({ ...d, testimonialRequests: (d.testimonialRequests || []).map(row => String(row.id) === String(id) ? saved : row) }));

    if (changes.status === 'received' && current.status !== 'received') {
      await addNotification({
        tipo: 'postventa_testimonial_received',
        titulo: 'Testimonio recibido',
        descripcion: saved.public_name || 'Cliente envio testimonio para revisar',
        module: 'postventa',
        owner: 'CRM',
        recipient_role: 'crm',
        priority: 'medium',
        route: '/postventa?tab=satisfaccion',
        entity_type: 'testimonial_request',
        entity_id: saved.id,
        cliente_id: saved.cliente_id,
        proyecto_id: saved.proyecto_id,
        dedupe_key: `postventa_testimonial_received:${saved.id}`,
      });
    }

    return saved;
  }, [addNotification, data.testimonialRequests, demoMode]);

  const addLandingAsset = useCallback(async (asset = {}) => {
    const now = new Date().toISOString();
    const row = normalizeLandingAsset({
      ...asset,
      id: asset.id || crypto.randomUUID?.() || Date.now().toString(),
      created_by: asset.created_by || user?.id || null,
      created_at: asset.created_at || now,
      updated_at: now,
    });
    if (!row.storage_path) throw new Error('Sube una imagen antes de guardar el asset.');

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, landingAssets: [row, ...(d.landingAssets || [])] }));
      return row;
    }

    const payload = filterLandingAssetPayload(row);
    const { data: inserted, error } = await supabase.from('landing_assets').insert([payload]).select().single();
    if (error) {
      console.error('[AppContext.addLandingAsset] No se pudo guardar asset de landing:', { error, payload });
      throw error;
    }

    const saved = normalizeLandingAsset(inserted || row);
    setData(d => ({ ...d, landingAssets: [saved, ...(d.landingAssets || [])] }));
    return saved;
  }, [demoMode, user?.id]);

  const updateLandingAsset = useCallback(async (id, changes = {}) => {
    const current = (data.landingAssets || []).find(row => String(row.id) === String(id)) || {};
    const normalized = normalizeLandingAsset({
      ...current,
      ...changes,
      updated_at: new Date().toISOString(),
    });
    if (!normalized.storage_path) throw new Error('Este asset necesita una imagen valida.');

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, landingAssets: (d.landingAssets || []).map(row => String(row.id) === String(id) ? normalized : row) }));
      return normalized;
    }

    const payload = filterLandingAssetPayload(normalized);
    delete payload.id;
    delete payload.created_at;
    delete payload.created_by;

    const { data: updated, error } = await supabase.from('landing_assets').update(payload).eq('id', id).select().single();
    if (error) {
      console.error('[AppContext.updateLandingAsset] No se pudo actualizar asset de landing:', { error, id, payload });
      throw error;
    }

    const saved = normalizeLandingAsset(updated || normalized);
    setData(d => ({ ...d, landingAssets: (d.landingAssets || []).map(row => String(row.id) === String(id) ? saved : row) }));
    return saved;
  }, [data.landingAssets, demoMode]);

  const deleteLandingAsset = useCallback(async (id) => {
    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, landingAssets: (d.landingAssets || []).filter(row => String(row.id) !== String(id)) }));
      return;
    }

    const { error } = await supabase.from('landing_assets').delete().eq('id', id);
    if (error) {
      console.error('[AppContext.deleteLandingAsset] No se pudo eliminar asset de landing:', { error, id });
      throw error;
    }

    setData(d => ({ ...d, landingAssets: (d.landingAssets || []).filter(row => String(row.id) !== String(id)) }));
  }, [demoMode]);

  // ── Novedades del portal (studio_news) — patrón GEMELO de landing_assets ──────
  const [studioNewsLoading, setStudioNewsLoading] = useState(false);
  const [studioNewsError, setStudioNewsError] = useState(false);
  const writesAreLocal = !isConfigured || USE_SEED || demoMode;

  const reloadStudioNews = useCallback(async () => {
    if (!isConfigured || USE_SEED || demoMode) {
      setStudioNewsError(false);
      setData(d => ({ ...d, studioNews: seedStudioNewsItems() }));
      return;
    }
    setStudioNewsLoading(true);
    try {
      const { data: rows, error } = await supabase
        .from('studio_news')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      setStudioNewsError(false);
      setData(d => ({ ...d, studioNews: (rows || []).map(normalizeStudioNewsRow) }));
    } catch (error) {
      console.error('[AppContext.reloadStudioNews] No se pudieron cargar las novedades:', error);
      setStudioNewsError(true);   // el portal cae al DEFAULT SOLO si la BD falló (no si hay 0 publicadas)
      throw error;
    } finally {
      setStudioNewsLoading(false);
    }
  }, [demoMode]);

  useEffect(() => {
    if (authLoading) return;
    // Superficies deslogueadas con BD real no cargan novedades de staff.
    if (!user?.id && isConfigured && !USE_SEED && !demoMode) return;
    reloadStudioNews().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id, demoMode]);

  const addStudioNews = useCallback(async (item = {}) => {
    const now = new Date().toISOString();
    const id = item.id || crypto.randomUUID?.() || Date.now().toString();
    const createdBy = item.created_by || user?.id || null;
    const publishedAt = item.published_at || (item.published === true ? now : null);
    const normalized = normalizeStudioNewsRow({
      ...buildStudioNewsPayload(item), id, created_by: createdBy, published_at: publishedAt, created_at: now, updated_at: now,
    });

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, studioNews: [...(d.studioNews || []), normalized] }));
      return normalized;
    }

    const payload = { ...buildStudioNewsPayload(item), id, created_by: createdBy, published_at: publishedAt };
    const { data: inserted, error } = await supabase.from('studio_news').insert([payload]).select().single();
    if (error) {
      console.error('[AppContext.addStudioNews] No se pudo crear la novedad:', { error, payload });
      throw error;
    }
    const saved = normalizeStudioNewsRow(inserted || normalized);
    setData(d => ({ ...d, studioNews: [...(d.studioNews || []), saved] }));
    return saved;
  }, [demoMode, user?.id]);

  const updateStudioNews = useCallback(async (id, changes = {}) => {
    const current = (data.studioNews || []).find(row => String(row.id) === String(id)) || {};
    const merged = { ...current, ...changes };
    const now = new Date().toISOString();
    // published_at: se setea SOLO en la transicion borrador→publicada (false→true); NO se limpia
    // al despublicar; NO se pisa al republicar. Editar una fila YA publicada no es una transicion.
    const publishedAt = (!current.published && merged.published === true && !current.published_at)
      ? now
      : (current.published_at || null);
    const payload = { ...buildStudioNewsPayload(merged), published_at: publishedAt };
    const normalized = normalizeStudioNewsRow({
      ...payload, id, created_by: current.created_by || null, created_at: current.created_at || null, updated_at: now,
    });

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, studioNews: (d.studioNews || []).map(row => String(row.id) === String(id) ? normalized : row) }));
      return normalized;
    }

    const { data: updated, error } = await supabase.from('studio_news').update(payload).eq('id', id).select().single();
    if (error) {
      console.error('[AppContext.updateStudioNews] No se pudo actualizar la novedad:', { error, id, payload });
      throw error;
    }
    const saved = normalizeStudioNewsRow(updated || normalized);
    setData(d => ({ ...d, studioNews: (d.studioNews || []).map(row => String(row.id) === String(id) ? saved : row) }));
    return saved;
  }, [data.studioNews, demoMode]);

  const deleteStudioNews = useCallback(async (id, { coverPath, bannerPath } = {}) => {
    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, studioNews: (d.studioNews || []).filter(row => String(row.id) !== String(id)) }));
      return;
    }
    const { error } = await supabase.from('studio_news').delete().eq('id', id);
    if (error) {
      console.error('[AppContext.deleteStudioNews] No se pudo eliminar la novedad:', { error, id });
      throw error;
    }
    await removeStudioNewsObjects([coverPath, bannerPath]);
    setData(d => ({ ...d, studioNews: (d.studioNews || []).filter(row => String(row.id) !== String(id)) }));
  }, [demoMode]);

  const reorderStudioNews = useCallback(async (orderedItems = []) => {
    // orderedItems = arreglo del editor en el NUEVO orden. Optimista + persistencia fila-por-fila.
    const changed = orderedItems
      .map((item, index) => ({ id: item.id, order: index, oldOrder: item.order }))
      .filter(x => Number(x.oldOrder) !== Number(x.order));
    setData(d => ({ ...d, studioNews: orderedItems.map((item, index) => ({ ...item, order: index })) }));

    if (!isConfigured || USE_SEED || demoMode || !changed.length) return;

    for (const row of changed) {
      const { error } = await supabase.from('studio_news').update({ sort_order: row.order }).eq('id', row.id);
      if (error) {
        // La BD quedo a MEDIO reordenar → el llamador debe refetch + avisar, nunca revert
        // optimista a un estado inventado (decision de David).
        console.error('[AppContext.reorderStudioNews] Fallo el guardado de orden:', { error, row });
        throw error;
      }
    }
  }, [demoMode]);

  const setDemoMode = useCallback((on, { silent = false } = {}) => {
    const next = Boolean(on);
    setDemoModeState(next);
    localStorage.setItem('feria_demo_mode', String(next));
    if (!next) {
      setData(d => ({
        ...d,
        clientes: d.clientes.filter(x => !x.is_demo),
        proyectos: d.proyectos.filter(x => !x.is_demo),
        cobros: d.cobros.filter(x => !x.is_demo),
        gastos: d.gastos.filter(x => !x.is_demo),
        leads: d.leads.filter(x => !x.is_demo),
        notificaciones: d.notificaciones.filter(x => !x.is_demo),
        reuniones: (d.reuniones || []).filter(x => !x.is_demo),
        projectUpdates: (d.projectUpdates || []).filter(x => !x.is_demo),
        projectApplications: (d.projectApplications || []).filter(x => !x.is_demo),
        projectApprovalRecords: (d.projectApprovalRecords || []).filter(x => !x.is_demo),
        projectApprovalActs: (d.projectApprovalActs || []).filter(x => !x.is_demo),
        projectAdjustmentRequests: (d.projectAdjustmentRequests || []).filter(x => !x.is_demo),
        brandkitAssets: (d.brandkitAssets || []).filter(x => !x.is_demo),
        internalClientDocuments: (d.internalClientDocuments || []).filter(x => !x.is_demo),
        projectDeliveryActs: (d.projectDeliveryActs || []).filter(x => !x.is_demo),
        brandkitDownloadLogs: (d.brandkitDownloadLogs || []).filter(x => !x.is_demo),
        projectChatMessages: (d.projectChatMessages || []).filter(x => !x.is_demo),
        projectUpdateComments: (d.projectUpdateComments || []).filter(x => !x.is_demo),
        marketingPerformance: (d.marketingPerformance || []).filter(x => !x.is_demo),
        marketingMetaInsights: (d.marketingMetaInsights || []).filter(x => !x.is_demo),
        marketingSources: (d.marketingSources || []).filter(x => !x.is_demo),
        marketingCampaigns: (d.marketingCampaigns || []).filter(x => !x.is_demo),
        metaAdsSyncLogs: (d.metaAdsSyncLogs || []).filter(x => !x.is_demo),
        communicationEvents: (d.communicationEvents || []).filter(x => !x.is_demo),
        supportTickets: (d.supportTickets || []).filter(x => !x.is_demo),
        supportTicketMessages: (d.supportTicketMessages || []).filter(x => !x.is_demo),
        clientSatisfactionSurveys: (d.clientSatisfactionSurveys || []).filter(x => !x.is_demo),
        testimonialRequests: (d.testimonialRequests || []).filter(x => !x.is_demo),
        sueldosProgramados: (d.sueldosProgramados || []).filter(x => !x.is_demo),
        team: (d.team || []).filter(x => !x.is_demo),
      }));
    }
    if (!silent) {
      showToast(next ? 'Modo prueba activo' : 'Modo real activo', next ? '🧪' : '✓');
    }
  }, [showToast]);

  const loadDemoFlow = useCallback(async ({ silent = false } = {}) => {
    const clienteId = DEMO_IDS.cliente;
    const proyectoId = DEMO_IDS.proyecto;
    const cobroId = DEMO_IDS.cobro;
    const contratoId = DEMO_IDS.contrato;
    const briefId = DEMO_IDS.brief;
    const now = new Date().toISOString();
    const teamIds = {
      admin: '00000000-0000-4000-8000-000000000401',
      crm: '00000000-0000-4000-8000-000000000402',
      designer: '00000000-0000-4000-8000-000000000403',
      finance: '00000000-0000-4000-8000-000000000404',
    };
    const demoTeam = [
      { id: teamIds.admin, name:'David Demo', initials:'DD', role:'Director demo', email:'admin.demo@feria.design', color:'#C9A96E', bg:'rgba(201,169,110,0.15)', brands:['feria','bl'], perms:'admin', permissions:{ modules:['dashboard','crm','mensajes','calendario','kanban','contratos','cotizaciones','finanzas','marketing','postventa','inteligencia','admin','portal'], brands:['feria','bl'] }, is_demo:true },
      { id: teamIds.crm, name:'CRM Demo', initials:'CD', role:'CRM / Ventas demo', email:'selena.demo@feria.design', color:'#4ECDC4', bg:'rgba(78,205,196,0.15)', brands:['feria','bl'], perms:'crm', permissions:{ modules:['dashboard','crm','mensajes','calendario','kanban','contratos','cotizaciones','finanzas','marketing','postventa','inteligencia','portal'], brands:['feria','bl'] }, is_demo:true },
      { id: teamIds.designer, name:'Diseñadora Demo', initials:'DI', role:'Diseñadora demo', email:'disenador.demo@feria.design', color:'#D4537E', bg:'rgba(212,83,126,0.15)', brands:['feria','bl'], perms:'creativo', permissions:{ modules:['dashboard','calendario','kanban'], brands:['feria','bl'] }, is_demo:true },
      { id: teamIds.finance, name:'Finanzas Demo', initials:'FI', role:'Finanzas demo', email:'finanzas.demo@feria.design', color:'#7BC67A', bg:'rgba(123,198,122,0.15)', brands:['feria','bl'], perms:'finanzas', permissions:{ modules:['dashboard','finanzas'], brands:['feria','bl'] }, is_demo:true },
    ].map(normalizeTeamMember);
    const cliente = { id: clienteId, nombre: 'Cliente Demo', email: 'cliente.demo@feria.design', telefono: '+593999999999', whatsapp: '+593999999999', empresa: 'Marca Cliente Demo', servicio: 'Branding para Fotógrafos', service_type: 'branding_fotografos', monto: 2800, stage: 5, brand: 'feria', color: '#5B9BD5', portal_access_status:'activo', is_demo: true, created_at: now };
    const demoTimeline = calculateProjectTimeline('Brand Identity', now);
    const proyecto = { id: proyectoId, clienteId, cliente_id: clienteId, nombre: 'Cliente Demo — Branding', estado: 'EL ORIGEN', fase: 4, pctInterno: 25, pctCliente: 35, diasEntrega: demoTimeline.clientBusinessDays, diasEjecucion: demoTimeline.internalBusinessDays, fechaEntregaCliente: demoTimeline.clientDueDate, fechaEntregaInterna: demoTimeline.internalDueDate, paquete: demoTimeline.packageName, aplicacionesIncluidas: demoTimeline.packageAppLimit, package_name: demoTimeline.packageName, package_app_limit: demoTimeline.packageAppLimit, creativo: teamIds.designer, creativo_id: teamIds.designer, production_status:'asignado', designer_assigned_at: now, brand: 'feria', is_demo: true, created_at: now };
    const cobro = { id: cobroId, clienteId, cliente_id: clienteId, nombre: 'Anticipo 60% · Demo', monto: 1680, tipo: 'Anticipo 60%', status: 'paid', via: 'Stripe', is_demo: true, created_at: now, updated_at: now };
    const contrato = { id: contratoId, cliente_id: clienteId, firmante: 'Cliente Demo', firmante_doc: 'Demo', contenido: 'Contrato demo · Branding para Fotógrafos', status: 'firmado', fecha_firma: now, service_type: 'branding_fotografos', estudio_firmado:true, is_demo: true, created_at: now };
    const brief = { id: briefId, cliente_id: clienteId, respuestas: null, completado: false, service_type: 'branding_fotografos', template_key:'branding_fotografos', activo:true, is_demo: true, updated_at: now };
    const lead = { id: '00000000-0000-4000-8000-000000000107', nombre:'Lead Demo', origen:'Instagram', status:'caliente', dias:1, brand:'feria', valor_estimado:2800, is_demo:true, created_at:now };
    const gasto = { id: '00000000-0000-4000-8000-000000000108', nombre:'Suscripción demo · Software', categoria:'Software / Suscripciones', monto:120, status:'pagado', is_demo:true, created_at:now };
    const reunion = { id: '00000000-0000-4000-8000-000000000109', titulo:'Reunión demo · Presentación de marca', tipo:'presentacion', cliente:'Cliente Demo', cliente_id:clienteId, fecha:new Date(Date.now()+2*86400000).toISOString(), duracion:60, link:'https://zoom.us/j/demo', notas:'Revisión del flujo de agenda y seguimiento.', confirmada:true, solicitud_cliente:false, is_demo:true };
    const notificacion = { id: '00000000-0000-4000-8000-000000000106', tipo: 'demo', titulo: 'Flujo demo cargado', desc: 'Cuentas demo, cliente, proyecto asignado, contrato, cobro, brief, reunión y lead listos para probar Feria OS.', leida: false, is_demo: true, created_at: now };
    const projectUpdate = { id:'00000000-0000-4000-8000-000000000110', proyecto_id:proyectoId, tipo:'avance', titulo:'Demo · primera exploración visual', descripcion:'Archivo demo subido para simular revisión interna.', archivo_url:'https://feria.design', status:'pendiente_revision', version_label:'V1', file_type:'pdf', created_by:teamIds.designer, reviewer_id:teamIds.admin, is_demo:true, created_at:now };
    const demoApplications = defaultApplicationsForPackage('Paquete 2').map((app, index) => ({ id:'00000000-0000-4000-8000-0000000002' + String(index).padStart(2,'0'), proyecto_id:proyectoId, ...app, is_demo:true, created_at:now }));
    const demoExtraApp = { id:'00000000-0000-4000-8000-000000000299', proyecto_id:proyectoId, application_id:'presentacion_comercial', name:'Presentación comercial', category:'Comercial', status:'pendiente', is_extra:true, extra_price:220, extra_status:'pendiente_pago', selected_by:'cliente', sort_order:99, is_demo:true, created_at:now };
    const demoChat = { id:'00000000-0000-4000-8000-000000000301', proyecto_id:proyectoId, sender_name:'David Demo', sender_id:teamIds.admin, message:'@Diseñadora Demo sube la ruta 2 para revisión y prioriza la versión más sobria.', mentions:['Diseñadora Demo'], is_demo:true, created_at:now };
    const demoComment = { id:'00000000-0000-4000-8000-000000000302', update_id:projectUpdate.id, proyecto_id:proyectoId, page_ref:'Página 3', body:'Bajar peso visual del símbolo y probar una composición más editorial.', is_demo:true, created_at:now };
    const marketingPerformance = { id:'00000000-0000-4000-8000-000000000303', responsable:'CRM Demo', brand:'feria', channel:'Meta Ads', campaign_name:'Demo · captación fotógrafos', period_start:new Date().toISOString().slice(0,10), period_end:new Date().toISOString().slice(0,10), amount_spent:300, leads:18, clients_converted:3, revenue_generated:4200, notes:'Campaña demo para medir conversión de pauta.', is_demo:true, created_at:now };
    setData(d => ({
      ...d,
      clientes: [cliente, ...d.clientes.filter(x => x.id !== clienteId)],
      proyectos: [proyecto, ...d.proyectos.filter(x => x.id !== proyectoId)],
      cobros: [cobro, ...d.cobros.filter(x => x.id !== cobroId)],
      leads: [lead, ...d.leads.filter(x => x.id !== lead.id)],
      gastos: [gasto, ...d.gastos.filter(x => x.id !== gasto.id)],
      reuniones: [reunion, ...(d.reuniones || []).filter(x => x.id !== reunion.id)],
      projectUpdates: [projectUpdate, ...(d.projectUpdates || []).filter(x => x.id !== projectUpdate.id)],
      projectApplications: [...demoApplications, demoExtraApp, ...(d.projectApplications || []).filter(x => String(x.proyecto_id) !== String(proyectoId))],
      projectApprovalRecords: (d.projectApprovalRecords || []).filter(x => String(x.proyecto_id) !== String(proyectoId)),
      projectAdjustmentRequests: (d.projectAdjustmentRequests || []).filter(x => String(x.proyecto_id) !== String(proyectoId)),
      projectChatMessages: [demoChat, ...(d.projectChatMessages || []).filter(x => x.id !== demoChat.id)],
      projectUpdateComments: [demoComment, ...(d.projectUpdateComments || []).filter(x => x.id !== demoComment.id)],
      notificaciones: [notificacion, ...d.notificaciones.filter(x => !x.is_demo)],
      marketingPerformance: [marketingPerformance, ...(d.marketingPerformance || []).filter(x => x.id !== marketingPerformance.id)],
      team: [...demoTeam, ...(d.team || []).filter(x => !demoTeam.some(m => String(m.id) === String(x.id)))],
    }));
    setDemoMode(true, { silent });
    const shouldPersistDemoFlow = process.env.REACT_APP_PERSIST_DEMO_FLOW === 'true';
    if (isConfigured && shouldPersistDemoFlow) {
      try {
        await supabase.from('team').upsert(demoTeam);
        await supabase.from('clientes').upsert(cliente);
        await supabase.from('proyectos').upsert({ ...proyecto, cliente_id: clienteId, creativo_id: teamIds.designer, pct_interno: proyecto.pctInterno, pct_cliente: proyecto.pctCliente, dias_entrega: proyecto.diasEntrega, dias_ejecucion: proyecto.diasEjecucion, client_due_date: proyecto.fechaEntregaCliente, internal_due_date: proyecto.fechaEntregaInterna });
        await supabase.from('cobros').upsert(cobro);
        await supabase.from('contratos').upsert(contrato);
        await supabase.from('briefs').upsert(brief, { onConflict: 'cliente_id' });
        await supabase.from('leads').upsert(lead);
        await supabase.from('gastos').upsert(gasto);
        await supabase.from('reuniones').upsert(reunion);
        await supabase.from('project_updates').upsert(projectUpdate);
        await supabase.from('project_applications').upsert([...demoApplications, demoExtraApp]);
        await supabase.from('project_chat_messages').upsert(demoChat);
        await supabase.from('project_update_comments').upsert(demoComment);
        await supabase.from('marketing_performance').upsert(marketingPerformance);
      } catch (e) { console.warn('Demo DB insert skipped:', e.message); }
    }
  }, [setDemoMode]);

  const clearDemoFlow = useCallback(async () => {
    setData(d => ({
      ...d,
      clientes: d.clientes.filter(x => !x.is_demo),
      proyectos: d.proyectos.filter(x => !x.is_demo),
      cobros: d.cobros.filter(x => !x.is_demo),
      gastos: d.gastos.filter(x => !x.is_demo),
      leads: d.leads.filter(x => !x.is_demo),
      notificaciones: d.notificaciones.filter(x => !x.is_demo),
      reuniones: (d.reuniones || []).filter(x => !x.is_demo),
        projectUpdates: (d.projectUpdates || []).filter(x => !x.is_demo),
        projectApplications: (d.projectApplications || []).filter(x => !x.is_demo),
        projectApprovalRecords: (d.projectApprovalRecords || []).filter(x => !x.is_demo),
        projectApprovalActs: (d.projectApprovalActs || []).filter(x => !x.is_demo),
        projectAdjustmentRequests: (d.projectAdjustmentRequests || []).filter(x => !x.is_demo),
        brandkitAssets: (d.brandkitAssets || []).filter(x => !x.is_demo),
        internalClientDocuments: (d.internalClientDocuments || []).filter(x => !x.is_demo),
        projectDeliveryActs: (d.projectDeliveryActs || []).filter(x => !x.is_demo),
        brandkitDownloadLogs: (d.brandkitDownloadLogs || []).filter(x => !x.is_demo),
        projectChatMessages: (d.projectChatMessages || []).filter(x => !x.is_demo),
        projectUpdateComments: (d.projectUpdateComments || []).filter(x => !x.is_demo),
        marketingPerformance: (d.marketingPerformance || []).filter(x => !x.is_demo),
        marketingMetaInsights: (d.marketingMetaInsights || []).filter(x => !x.is_demo),
        communicationEvents: (d.communicationEvents || []).filter(x => !x.is_demo),
        sueldosProgramados: (d.sueldosProgramados || []).filter(x => !x.is_demo),
        team: (d.team || []).filter(x => !x.is_demo),
    }));
    setDemoMode(false);
    const shouldPersistDemoFlow = process.env.REACT_APP_PERSIST_DEMO_FLOW === 'true';
    if (isConfigured && shouldPersistDemoFlow) {
      for (const table of ['clientes','proyectos','cobros','gastos','leads','notificaciones','contratos','briefs','reuniones','conversaciones','mensajes_meta','tareas','project_updates','project_applications','project_approval_records','project_approval_acts','project_adjustment_requests','brandkit_assets','project_chat_messages','project_update_comments','marketing_performance','team']) {
        try { await supabase.from(table).delete().eq('is_demo', true); } catch (_) {}
      }
    }
    showToast('Datos demo limpiados', '🧹');
  }, [setDemoMode, showToast]);

  useEffect(() => {
    const hasDemoCliente = (data.clientes || []).some(c => c.is_demo || c.id === DEMO_IDS.cliente);
    if (!demoMode || hasDemoCliente || (!USE_SEED && isConfigured)) return;

    loadDemoFlow({ silent: true }).catch(error => {
      console.warn('No se pudo restaurar el flujo demo:', error);
    });
  }, [demoMode, data.clientes, loadDemoFlow]);

  const updateCliente = useCallback(async (id, changes = {}) => {
    const currentCliente = (data.clientes || []).find(c => String(c.id) === String(id));
    const clientChanges = { ...changes };
    const allowStageOverride = Boolean(clientChanges.crm_transition_override || clientChanges.allowStageOverride);
    const allowBackwards = Boolean(clientChanges.crm_allow_backwards);
    const allowCompound = Boolean(clientChanges.crm_allow_compound);
    const transitionSource = clientChanges.crm_transition_source || 'appcontext.updateCliente';
    const transitionMetadata = clientChanges.crm_transition_metadata || {};
    const relatedEntityType = clientChanges.crm_related_entity_type || null;
    const relatedEntityId = clientChanges.crm_related_entity_id || null;

    delete clientChanges.crm_transition_override;
    delete clientChanges.allowStageOverride;
    delete clientChanges.crm_allow_backwards;
    delete clientChanges.crm_allow_compound;
    delete clientChanges.crm_transition_source;
    delete clientChanges.crm_transition_metadata;
    delete clientChanges.crm_related_entity_type;
    delete clientChanges.crm_related_entity_id;

    const hasStageChange = Object.prototype.hasOwnProperty.call(clientChanges, 'stage');
    const requestedStage = hasStageChange ? clientChanges.stage : null;
    delete clientChanges.stage;

    if (hasStageChange) {
      if (!currentCliente) {
        const error = `Cliente ${id} no encontrado para validar transicion CRM.`;
        console.warn('[AppContext.updateCliente]', error);
        showToast('No se pudo validar la fase del cliente.', '!');
        return { ok: false, error };
      }

      const plan = buildCrmTransitionPlan(currentCliente.stage ?? 0, requestedStage, {
        allowOverride: allowStageOverride,
        allowBackwards,
        allowCompound,
      });
      if (!plan.ok) {
        console.warn('[AppContext.updateCliente]', plan.error);
        showToast('No se puede saltar fases del CRM sin override.', '!');
        return { ok: false, error: plan.error };
      }
    }

    setData(d => ({ ...d, clientes: d.clientes.map(c => c.id === id ? { ...c, ...clientChanges } : c) }));

    let saveError = null;
    if (isConfigured && !USE_SEED && !demoMode && !clientChanges?.is_demo) {
      const payload = pickClienteDbPayload({ ...clientChanges, updated_at: clientChanges?.updated_at || new Date().toISOString() });
      if (Object.keys(payload).length) {
        // Antes se leia solo `error`, y un UPDATE de 0 filas no lo trae: por eso "Marcar
        // listo manualmente" mostraba exito sobre un guardado que RLS habia rechazado.
        let resultado = await updatePorId(supabase, 'clientes', id, payload, { queSeGuardaba: 'el cliente' });
        // El reintento sin `notas` solo aplica al fallo de esquema, nunca al de 0 filas.
        if (!resultado.ok && !resultado.sinFilas
            && Object.prototype.hasOwnProperty.call(payload, 'notas')
            && isMissingClienteSchemaColumn(resultado.error, 'notas')) {
          console.warn('[AppContext.updateCliente] clientes.notas no existe en schema cache; se actualiza sin notas hasta aplicar migracion 066.');
          const retryPayload = withoutClienteDbField(payload, 'notas');
          if (Object.keys(retryPayload).length) {
            resultado = await updatePorId(supabase, 'clientes', id, retryPayload, { queSeGuardaba: 'el cliente' });
          }
        }
        if (!resultado.ok) {
          console.error('[AppContext.updateCliente] Supabase rechazo actualizar cliente:', resultado.error?.message || resultado.error);
          saveError = resultado.error;
        }
      }
    }
    if (saveError) {
      setData(d => ({
        ...d,
        clientes: d.clientes.map(c =>
          c.id === id ? { ...currentCliente } : c
        ),
      }));
      // El fallo por 0 filas trae un mensaje propio y accionable (permiso / no existe);
      // los errores tecnicos siguen genericizados para no sacar jerga a pantalla.
      showToast(
        saveError?.code === 'NO_ROWS' ? saveError.message : 'No se pudo guardar el cambio. Intenta de nuevo.',
        '!',
      );
      return { ok: false, error: saveError };
    }

    if (hasStageChange && currentCliente && Number(requestedStage) !== Number(currentCliente.stage ?? 0)) {
      const phaseResult = await advanceClientPhase({
        clienteId: id,
        targetStage: requestedStage,
        source: transitionSource,
        metadata: transitionMetadata,
        relatedEntityType,
        relatedEntityId,
        allowOverride: allowStageOverride,
        allowBackwards,
        allowCompound,
      });
      if (!phaseResult.ok) return phaseResult;
    }

    return { ok: true };
  }, [advanceClientPhase, data.clientes, demoMode, showToast]);

  const addProyecto = useCallback(async (proyecto) => {
    const projectPackage = proyecto.paquete || proyecto.package_name || 'Brand Starter';
    const projectScope = normalizeProjectScope(proyecto);
    const timeline = calculateProjectTimeline(projectPackage, proyecto.production_started_at || proyecto.fechaInicio || new Date());
    const newProy = {
      ...proyecto,
      id: proyecto.id || crypto.randomUUID?.() || Date.now().toString(),
      created_at: new Date().toISOString(),
      pctInterno: 0, pctCliente: 0,
      paquete: timeline.packageName,
      // `timeline` ya no afirma un cupo: el calendario no sabe cuantas aplicaciones se
      // vendieron. Sale del catalogo canonico, y `null` significa «sin confirmar».
      aplicacionesIncluidas: proyecto.aplicacionesIncluidas
        ?? cupoCanonicoONull({ packageId: proyecto.package_id, packageName: timeline.packageName }),
      // El respaldo era la tabla estatica. Si el proyecto no trae precio, se pregunta al
      // catalogo canonico; si tampoco resuelve, 0 significa «sin precio», no «vale cero».
      package_price: proyecto.package_price ?? proyecto.precioPaquete ?? precioCanonicoONull({ packageName: projectPackage }) ?? 0,
      diasEntrega: proyecto.diasEntrega ?? timeline.clientBusinessDays,
      diasEjecucion: proyecto.diasEjecucion ?? timeline.internalBusinessDays,
      fechaEntregaCliente: projectScope === 'studio' ? null : (proyecto.fechaEntregaCliente || proyecto.fechaEntrega || timeline.clientDueDate),
      fechaEntregaInterna: proyecto.fechaEntregaInterna || timeline.internalDueDate,
      production_status: proyecto.production_status || (proyecto.creativo ? 'asignado' : 'pendiente_asignacion'),
      designer_assigned_at: proyecto.creativo ? new Date().toISOString() : null,
      project_scope: projectScope,
      projectScope,
      is_internal: projectScope === 'studio',
    };
    if (isConfigured) {
      const dbProy = pickProyectoDbPayload({
        id: newProy.id,
        nombre: newProy.nombre,
        estado: newProy.estado,
        fase: newProy.fase,
        brand: newProy.brand,
        prioridad: newProy.prioridad,
        created_at: newProy.created_at,
        updated_at: newProy.updated_at,
        is_demo: newProy.is_demo,
        cliente_id:    projectScope === 'studio' ? null : (newProy.clienteId || newProy.cliente_id || null),
        creativo_id:   newProy.creativo || null,
        pct_interno:   0,
        pct_cliente:   0,
        dias_entrega:  newProy.diasEntrega,
        dias_ejecucion:newProy.diasEjecucion,
        fecha_inicio: newProy.fechaInicio || newProy.fecha_inicio || null,
        fecha_entrega: projectScope === 'studio' ? null : (newProy.fechaEntrega || newProy.fecha_entrega || newProy.fechaEntregaCliente || null),
        client_due_date: projectScope === 'studio' ? null : (newProy.fechaEntregaCliente || newProy.fechaEntrega || null),
        internal_due_date: newProy.fechaEntregaInterna || null,
        package_name: newProy.paquete,
        package_app_limit: newProy.aplicacionesIncluidas,
        package_price: newProy.package_price || 0,
        service_type: newProy.service_type || newProy.servicio,
        production_status: newProy.production_status,
        designer_assigned_at: newProy.designer_assigned_at,
        assigned_reviewer_id: newProy.assigned_reviewer_id || null,
        project_scope: projectScope,
        internal_requester_id: newProy.internal_requester_id || null,
        internal_request_notes: newProy.internal_request_notes || null,
      });
      // ── LA MISMA AUTORIDAD QUE USA EL BRIEF ─────────────────────────────
      //
      // Aqui habia un `insert` directo, y era la UNICA forma en que nacia un
      // proyecto desde el admin. Convivia con la creacion automatica de
      // `complete-client-brief`: dos implementaciones del mismo hecho, cada una
      // con sus reglas, y ninguna capaz de ver a la otra.
      //
      // Ahora las dos pasan por `proyecto_provisionar` (294): crea o devuelve
      // el que ya hay, ata el contrato, y no puede haber dos por la misma
      // compra. La funcion exige el mismo `has_role(admin, crm)` que exigia la
      // politica del insert, asi que llamarla no concede permisos nuevos.
      //
      // El resto del payload se escribe justo despues, igual que antes.
      const { data: provision, error: errProvision } = await supabase.rpc('proyecto_provisionar', {
        p_cliente_id: dbProy.cliente_id || null,
        p_quote_id: dbProy.quote_id || null,
        p_contrato_id: dbProy.contrato_id || null,
        p_brief_id: dbProy.brief_id || null,
        p_nombre: dbProy.nombre || null,
        p_brand: dbProy.brand || null,
        p_service_type: dbProy.service_type || null,
        p_package_name: dbProy.package_name || null,
        p_project_scope: projectScope,
        p_origen: 'admin-ui',
      });
      if (errProvision || provision?.ok !== true) {
        console.error('proyecto_provisionar:', errProvision?.message || provision?.code);
      }
      const provisionadoId = provision?.proyecto_id || null;
      const { data: inserted, error } = provisionadoId
        ? await supabase.from('proyectos')
            .update({ ...dbProy, id: undefined }).eq('id', provisionadoId).select().single()
        : { data: null, error: errProvision || new Error(provision?.code || 'PROVISION_FALLIDA') };
      if (!error && inserted) {
        const normalized = normalizeProyectoRow({ ...inserted, package_price: inserted.package_price });
        const defaultApps = (proyecto.aplicaciones || defaultApplicationsForPackage(normalized.paquete)).map((app, index) => {
          const appId = app.application_id || app.id;
          const ref = applicationById(appId);
          return { proyecto_id: inserted.id, application_id: appId, name: app.name || ref.name, category: app.category || ref.category, status: app.status || 'pendiente', is_extra: Boolean(app.is_extra), ...(() => { const e = precioDeExtraParaPersistir({ esExtra: Boolean(app.is_extra), precio: precioDeLaFila({ ...ref, base_price: app.extra_price ?? ref.base_price ?? ref.basePrice }, { applicationId: appId, mercado: 'nacional' }) }); return { extra_price: e.valor, extra_status: app.extra_status || e.estado }; })(), sort_order: index + 1, designer_id: app.designer_id || null, internal_due_date: app.internal_due_date || null, assignment_comments: app.assignment_comments || '', assigned_at: app.designer_id ? new Date().toISOString() : null };
        });
        let insertedApps = [];
        if (defaultApps.length) { const { data: appRows } = await supabase.from('project_applications').insert(defaultApps).select(); insertedApps = appRows || []; }
        setData(d => ({ ...d, proyectos: [normalized, ...d.proyectos], projectApplications: [...insertedApps, ...(d.projectApplications || [])] }));
        return normalized;
      }
    }
    const localApps = (proyecto.aplicaciones || defaultApplicationsForPackage(newProy.paquete)).map((app, index) => {
      const appId = app.application_id || app.id;
      const ref = applicationById(appId);
      return { id: crypto.randomUUID?.() || String(newProy.id) + '-' + index, proyecto_id: newProy.id, application_id: appId, name: app.name || ref.name, category: app.category || ref.category, status: app.status || 'pendiente', is_extra: Boolean(app.is_extra), ...(() => { const e = precioDeExtraParaPersistir({ esExtra: Boolean(app.is_extra), precio: precioDeLaFila({ ...ref, base_price: app.extra_price ?? ref.base_price ?? ref.basePrice }, { applicationId: appId, mercado: 'nacional' }) }); return { extra_price: e.valor, extra_status: app.extra_status || e.estado }; })(), sort_order: index + 1, designer_id: app.designer_id || null, internal_due_date: app.internal_due_date || null, assignment_comments: app.assignment_comments || '', assigned_at: app.designer_id ? new Date().toISOString() : null };
    });
    setData(d => ({ ...d, proyectos: [newProy, ...d.proyectos], projectApplications: [...localApps, ...(d.projectApplications || [])] }));
    return newProy;
  }, []);

  const updateProyecto = useCallback(async (id, changes) => {
    const proyectoPrevio = (data.proyectos || []).find(p => String(p.id) === String(id));
    setData(d => {
      const nextProjects = d.proyectos.map(p => String(p.id) === String(id) ? { ...p, ...changes } : p);
      // La notificacion de asignacion se creaba ACA, dentro de setData: un objeto en
      // memoria que nunca se insertaba, y encima con `target_member_id`, columna que no
      // existe en `notificaciones`. Resultado: 0 filas de project_assignment en toda la
      // historia. Ahora se emite abajo, con addNotification y despues de confirmar el
      // guardado.
      return { ...d, proyectos: nextProjects };
    });
    if (isConfigured) {
      // Map camelCase to snake_case for DB
      const dbChanges = { ...changes };
      if (changes.clienteId    !== undefined) { dbChanges.cliente_id    = changes.clienteId;    delete dbChanges.clienteId; }
      if (changes.pctInterno   !== undefined) { dbChanges.pct_interno   = changes.pctInterno;   delete dbChanges.pctInterno; }
      if (changes.pctCliente   !== undefined) { dbChanges.pct_cliente   = changes.pctCliente;   delete dbChanges.pctCliente; }
      if (changes.diasEntrega  !== undefined) { dbChanges.dias_entrega  = changes.diasEntrega;  delete dbChanges.diasEntrega; }
      if (changes.diasEjecucion!== undefined) { dbChanges.dias_ejecucion= changes.diasEjecucion;delete dbChanges.diasEjecucion; }
      if (changes.fechaEntregaCliente !== undefined) { dbChanges.client_due_date = changes.fechaEntregaCliente; delete dbChanges.fechaEntregaCliente; }
      if (changes.fechaEntregaInterna !== undefined) { dbChanges.internal_due_date = changes.fechaEntregaInterna; delete dbChanges.fechaEntregaInterna; }
      if (changes.paquete !== undefined) { dbChanges.package_name = changes.paquete; delete dbChanges.paquete; }
      if (changes.aplicacionesIncluidas !== undefined) { dbChanges.package_app_limit = changes.aplicacionesIncluidas; delete dbChanges.aplicacionesIncluidas; }
      if (changes.creativo     !== undefined) { dbChanges.creativo_id   = changes.creativo || null; delete dbChanges.creativo; }
      if (changes.projectScope !== undefined) { dbChanges.project_scope = changes.projectScope; delete dbChanges.projectScope; }
      if (changes.is_internal !== undefined && changes.project_scope === undefined && changes.projectScope === undefined) {
        dbChanges.project_scope = changes.is_internal ? 'studio' : 'client';
        delete dbChanges.is_internal;
      }
      if (changes.designer_assigned_at !== undefined) { dbChanges.designer_assigned_at = changes.designer_assigned_at; }
      if (changes.production_status !== undefined) { dbChanges.production_status = changes.production_status; }
      const safeChanges = pickProyectoDbPayload({ ...dbChanges, updated_at: dbChanges.updated_at || new Date().toISOString() });
      if (Object.keys(safeChanges).length) {
        const resultado = await updatePorId(supabase, 'proyectos', id, safeChanges, { queSeGuardaba: 'el proyecto' });
        if (!resultado.ok) {
          showToast(resultado.sinFilas ? resultado.error.message : 'No se pudo guardar el proyecto. Intenta de nuevo.', '!');
          return resultado;
        }
      }
    }
    // Solo se avisa si el guardado se confirmo. Al desasignar no se notifica a nadie:
    // no hay destinatario, y el aviso util es para quien recibe el trabajo.
    if (Object.prototype.hasOwnProperty.call(changes, 'creativo') && changes.creativo) {
      await notifyTeamMember(changes.creativo, {
        tipo: 'project_assignment',
        titulo: 'Nuevo proyecto asignado',
        descripcion: `${proyectoPrevio?.nombre || 'Un proyecto'} quedo a tu cargo.`,
        module: 'proyectos',
        owner: 'creativo',
        priority: 'high',
        route: '/kanban',
        entity_type: 'proyecto',
        entity_id: id,
        proyecto_id: id,
        cliente_id: proyectoPrevio?.cliente_id || proyectoPrevio?.clienteId || null,
        dedupe_key: `project_assignment:${id}:${changes.creativo}`,
      }, 'la asignacion del proyecto');
    }
    return { ok: true };
  }, [data.proyectos, notifyTeamMember, showToast]);

  const addCliente = useCallback(async (cliente) => {
    if (isConfigured && !USE_SEED && !demoMode && !cliente?.is_demo) {
      const payload = pickClienteDbPayload({ ...cliente, created_at: cliente?.created_at || new Date().toISOString() });
      if (!payload.nombre) {
        throw new Error('El cliente necesita nombre para guardarse.');
      }
      let { data: inserted, error } = await supabase
        .from('clientes')
        .insert([payload])
        .select('*')
        .single();
      if (error && Object.prototype.hasOwnProperty.call(payload, 'notas') && isMissingClienteSchemaColumn(error, 'notas')) {
        console.warn('[AppContext.addCliente] clientes.notas no existe en schema cache; se crea sin notas hasta aplicar migracion 066.');
        const retryPayload = withoutClienteDbField(payload, 'notas');
        const retry = await supabase
          .from('clientes')
          .insert([retryPayload])
          .select('*')
          .single();
        inserted = retry.data;
        error = retry.error;
      }
      if (error) {
        console.error('[AppContext.addCliente] Supabase rechazo crear cliente:', error.message || error);
        throw new Error(error.message || 'Supabase rechazo crear el cliente.');
      }
      if (!inserted?.id) {
        throw new Error('Supabase no devolvio la fila creada del cliente.');
      }
      const normalized = normalizeClienteRow(inserted);
      setData(d => ({ ...d, clientes: [normalized, ...d.clientes.filter(c => String(c.id) !== String(inserted.id))] }));
      return normalized;
    }
    const newCliente = { ...cliente, id: cliente?.id || crypto.randomUUID?.() || Date.now().toString(), is_demo: cliente?.is_demo ?? demoMode };
    setData(d => ({ ...d, clientes: [newCliente, ...d.clientes] }));
    return newCliente;
  }, [demoMode]);

  const createClientWithAccess = useCallback(async (cliente) => {
    if (!isConfigured || USE_SEED || demoMode) {
      throw new Error('Este flujo requiere Supabase real y modo demo desactivado.');
    }
    const { data: resp, error } = await supabase.functions.invoke('create-client-with-access', {
      body: cliente,
    });
    if (error || !resp?.ok) {
      const message = resp?.error || await edgeFunctionErrorMessage(error, 'No se pudo crear acceso al portal.');
      console.error('[AppContext.createClientWithAccess] Edge Function rechazo la solicitud:', {
        message,
        function: 'create-client-with-access',
      });
      throw new Error(message);
    }
    if (!resp?.cliente?.id) {
      throw new Error('Supabase no devolvio el cliente creado o vinculado.');
    }
    const normalized = normalizeClienteRow(resp.cliente);
    setData(d => ({
      ...d,
      clientes: [normalized, ...(d.clientes || []).filter(c => String(c.id) !== String(normalized.id))],
    }));
    return { ...resp, cliente: normalized };
  }, [demoMode]);

  const archiveCliente = useCallback(async (id, reason = '') => {
    const current = (data.clientes || []).find(c => String(c.id) === String(id)) || {};
    const currentName = String(current.nombre || current.name || '').trim().toLowerCase();
    const currentEmail = String(current.email || current.correo || '').trim().toLowerCase();
    if (
      String(id).toLowerCase() === '11734024-eb94-43eb-8a5c-0f172003817a'
      || currentName === 'david mauricio feria'
      || currentEmail === 'david@feria.design'
      || currentEmail.includes('david.mauricio')
    ) {
      throw new Error('Este cliente esta protegido y no puede archivarse desde Admin.');
    }
    const now = new Date().toISOString();
    const changes = {
      archived_at: now,
      archived_by: user?.id || null,
      archive_reason: String(reason || '').trim() || null,
      updated_at: now,
    };
    if (isConfigured && !USE_SEED && !demoMode) {
      const { data: updated, error } = await supabase
        .from('clientes')
        .update(pickClienteDbPayload(changes))
        .eq('id', id)
        .select('*')
        .single();
      if (error) {
        console.error('[AppContext.archiveCliente] Supabase rechazo archivar cliente:', error.message || error);
        throw new Error(error.message || 'No se pudo archivar el cliente.');
      }
      const normalized = normalizeClienteRow(updated);
      setData(d => ({ ...d, clientes: d.clientes.map(c => String(c.id) === String(id) ? { ...c, ...normalized } : c) }));
      return normalized;
    }
    setData(d => ({ ...d, clientes: d.clientes.map(c => String(c.id) === String(id) ? { ...c, ...changes } : c) }));
    return changes;
  }, [data.clientes, demoMode, user?.id]);

  const restoreCliente = useCallback(async (id) => {
    const changes = {
      archived_at: null,
      archived_by: null,
      archive_reason: null,
      updated_at: new Date().toISOString(),
    };
    if (isConfigured && !USE_SEED && !demoMode) {
      const { data: updated, error } = await supabase
        .from('clientes')
        .update(pickClienteDbPayload(changes))
        .eq('id', id)
        .select('*')
        .single();
      if (error) {
        console.error('[AppContext.restoreCliente] Supabase rechazo restaurar cliente:', error.message || error);
        throw new Error(error.message || 'No se pudo restaurar el cliente.');
      }
      const normalized = normalizeClienteRow(updated);
      setData(d => ({ ...d, clientes: d.clientes.map(c => String(c.id) === String(id) ? { ...c, ...normalized } : c) }));
      return normalized;
    }
    setData(d => ({ ...d, clientes: d.clientes.map(c => String(c.id) === String(id) ? { ...c, ...changes } : c) }));
    return changes;
  }, [demoMode]);

  const deleteClienteSafely = useCallback(async (id) => {
    if (!id) throw new Error('cliente_id requerido');
    const current = (data.clientes || []).find(c => String(c.id) === String(id)) || {};
    const currentName = String(current.nombre || current.name || '').trim().toLowerCase();
    const currentEmail = String(current.email || current.correo || '').trim().toLowerCase();
    if (
      String(id).toLowerCase() === '11734024-eb94-43eb-8a5c-0f172003817a'
      || currentName === 'david mauricio feria'
      || currentEmail === 'david@feria.design'
      || currentEmail.includes('david.mauricio')
    ) {
      throw new Error('Este cliente esta protegido y no puede eliminarse desde Admin.');
    }
    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, clientes: d.clientes.filter(c => String(c.id) !== String(id)) }));
      return { ok: true, deleted: true };
    }
    const { data: resp, error } = await supabase.functions.invoke('delete-client-if-safe', {
      body: { cliente_id: id },
    });
    if (error || !resp?.ok) {
      const message = resp?.error || await edgeFunctionErrorMessage(error, 'No se pudo eliminar el cliente.');
      const detail = Array.isArray(resp?.blockers) && resp.blockers.length
        ? `${message} (${resp.blockers.map(item => `${item.label}: ${item.count}`).join(', ')})`
        : message;
      throw new Error(detail);
    }
    setData(d => ({ ...d, clientes: d.clientes.filter(c => String(c.id) !== String(id)) }));
    return resp;
  }, [data.clientes, demoMode]);

  const previewClientDelete = useCallback(async (id) => {
    if (!id) throw new Error('cliente_id requerido');
    if (!isConfigured || USE_SEED || demoMode) {
      return { ok: true, dry_run: true, mode: 'local_preview', summary: {}, dependency_counts: {}, auth_users: [] };
    }
    const { data: resp, error } = await supabase.functions.invoke('purge-test-client', {
      body: {
        cliente_id: id,
        action: 'dry_run_delete_client',
        dry_run: true,
      },
    });
    const structuredError = resp || await edgeFunctionErrorPayload(error);
    if (error || !structuredError?.ok) {
      const message = structuredError?.error || structuredError?.message || await edgeFunctionErrorMessage(error, 'No se pudo revisar el cliente.');
      const err = new Error(message);
      err.response = structuredError || null;
      throw err;
    }
    return structuredError;
  }, [demoMode]);

  const purgeTestClient = useCallback(async (id, confirmation = {}) => {
    if (!id) throw new Error('cliente_id requerido');
    const current = (data.clientes || []).find(c => String(c.id) === String(id)) || {};
    const currentName = String(current.nombre || current.name || '').trim().toLowerCase();
    const currentEmail = String(current.email || current.correo || '').trim().toLowerCase();
    if (
      String(id).toLowerCase() === '11734024-eb94-43eb-8a5c-0f172003817a'
      || currentName === 'david mauricio feria'
      || currentEmail === 'david@feria.design'
      || currentEmail.includes('david.mauricio')
    ) {
      throw new Error('Este cliente esta protegido y no puede eliminarse desde Admin.');
    }
    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, clientes: d.clientes.filter(c => String(c.id) !== String(id)) }));
      return { ok: true, purged: true, storage_cleanup_performed: false };
    }
    const { data: resp, error } = await supabase.functions.invoke('purge-test-client', {
      body: {
        cliente_id: id,
        client_type_confirmation: confirmation.client_type_confirmation || 'test',
        confirm_test_client: confirmation.confirm_test_client === true,
        force_test_purge: confirmation.force_test_purge === true,
        confirm_force_irreversible: confirmation.confirm_force_irreversible === true,
        confirmation_phrase: confirmation.confirmation_phrase || '',
      },
    });
    const structuredError = resp || await edgeFunctionErrorPayload(error);
    if (error || !structuredError?.ok) {
      const message = structuredError?.error || structuredError?.message || await edgeFunctionErrorMessage(error, 'No se pudo eliminar el cliente de prueba.');
      const blockerList = Array.isArray(structuredError?.absolute_blockers) && structuredError.absolute_blockers.length
        ? structuredError.absolute_blockers
        : structuredError?.protected_blockers;
      const detail = Array.isArray(blockerList) && blockerList.length
        ? `${message} (${blockerList.map(item => `${item.label}: ${item.count}`).join(', ')})`
        : message;
      const err = new Error(detail);
      err.response = structuredError || null;
      throw err;
    }
    setData(d => ({ ...d, clientes: d.clientes.filter(c => String(c.id) !== String(id)) }));
    return structuredError;
  }, [data.clientes, demoMode]);

  const deleteCliente = deleteClienteSafely;

  const addLead = useCallback(async (lead) => {
    if (isConfigured && !USE_SEED && !demoMode && !lead?.is_demo) {
      const payload = normalizeLeadDbPayload(lead);
      logLeadMarketingSchemaAudit(payload, 'lead_create');
      let { data: inserted, error } = await supabase
        .from('leads').insert([payload]).select().single();
      if (error && isMissingLeadMarketingSchemaColumn(error)) {
        const legacyPayload = normalizeLeadDbPayload(lead, { legacyOnly: true });
        console.warn('[AppContext.addLead] Reintentando lead con aliases legacy por cache de esquema PostgREST.', {
          source_id_present: Boolean(legacyPayload.source_id),
          campaign_id_present: Boolean(legacyPayload.campaign_id),
        });
        ({ data: inserted, error } = await supabase
          .from('leads').insert([legacyPayload]).select().single());
      }
      if (error) {
        console.error('[AppContext.addLead] Supabase rechazo crear lead:', error.message || error);
        throw new Error(error.message || 'Supabase rechazo crear el lead.');
      }
      if (!inserted) throw new Error('Supabase no devolvio la fila creada del lead.');
      setData(d => ({ ...d, leads: [inserted, ...d.leads] }));
      return inserted;
    }
    const newLead = { ...lead, id: crypto.randomUUID?.() || Date.now().toString() };
    setData(d => ({ ...d, leads: [newLead, ...d.leads] }));
    return newLead;
  }, [demoMode]);

  const updateLead = useCallback(async (id, changes) => {
    if (isConfigured && !USE_SEED && !demoMode && !changes?.is_demo) {
      const payload = normalizeLeadDbPayload(changes);
      logLeadMarketingSchemaAudit(payload, 'lead_update');
      let { data: updated, error } = await supabase
        .from('leads')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error && isMissingLeadMarketingSchemaColumn(error)) {
        const legacyPayload = normalizeLeadDbPayload(changes, { legacyOnly: true });
        console.warn('[AppContext.updateLead] Reintentando lead con aliases legacy por cache de esquema PostgREST.', {
          source_id_present: Boolean(legacyPayload.source_id),
          campaign_id_present: Boolean(legacyPayload.campaign_id),
        });
        ({ data: updated, error } = await supabase
          .from('leads')
          .update(legacyPayload)
          .eq('id', id)
          .select()
          .single());
      }
      if (error) {
        console.error('[AppContext.updateLead] Supabase rechazo actualizar lead:', error.message || error);
        throw new Error(error.message || 'Supabase rechazo actualizar el lead.');
      }
      if (!updated) throw new Error('Supabase no devolvio la fila actualizada del lead.');
      setData(d => ({ ...d, leads: d.leads.map(l => String(l.id) === String(id) ? { ...l, ...updated } : l) }));
      return updated;
    }
    setData(d => ({ ...d, leads: d.leads.map(l => l.id === id ? { ...l, ...changes } : l) }));
    return { id, ...changes };
  }, [demoMode]);

  const deleteLead = useCallback(async (id) => {
    if (!id) return;
    if (isConfigured && !USE_SEED && !demoMode) {
      const { error } = await supabase.from('leads').delete().eq('id', id);
      if (error) {
        console.error('[AppContext.deleteLead] Supabase rechazo borrar lead:', error.message || error);
        throw new Error(error.message || 'Supabase rechazo borrar el lead.');
      }
    }
    setData(d => ({ ...d, leads: d.leads.filter(l => String(l.id) !== String(id)) }));
  }, [demoMode]);

  const addGasto = useCallback(async (gasto) => {
    const payload = filterGastoPayload({ ...gasto, updated_at: new Date().toISOString() });
    if (!isConfigured || USE_SEED || demoMode) {
      const newGasto = { ...payload, id: payload.id || crypto.randomUUID?.() || Date.now().toString(), is_demo: gasto?.is_demo ?? demoMode };
      setData(d => ({ ...d, gastos: [newGasto, ...d.gastos] }));
      return newGasto;
    }

    if (isConfigured) {
      const { data: inserted, error } = await supabase
        .from('gastos').insert([payload]).select().single();
      if (!error && inserted) {
        setData(d => ({ ...d, gastos: [inserted, ...d.gastos] }));
        return inserted;
      }
      if (error) console.error('[AppContext.addGasto] No se pudo guardar gasto:', { error, payload });
    }
    const newGasto = { ...payload, id: payload.id || crypto.randomUUID?.() || Date.now().toString() };
    setData(d => ({ ...d, gastos: [newGasto, ...d.gastos] }));
    return newGasto;
  }, [demoMode]);

  const updateGasto = useCallback(async (id, changes) => {
    const payload = filterGastoPayload({ ...changes, updated_at: new Date().toISOString() });
    setData(d => ({ ...d, gastos: d.gastos.map(g => String(g.id) === String(id) ? { ...g, ...payload } : g) }));
    if (isConfigured) {
      const { error } = await supabase.from('gastos').update(payload).eq('id', id);
      if (error) {
        console.error('[AppContext.updateGasto] No se pudo actualizar gasto:', { error, id, payload });
        throw error;
      }
    }
  }, []);

  const deleteGasto = useCallback(async (id, options = {}) => {
    const current = (data.gastos || []).find(row => String(row.id) === String(id));
    if (!id) return;

    if (isConfigured && !USE_SEED && !demoMode) {
      const { error } = await supabase.from('gastos').delete().eq('id', id);
      if (error) {
        console.error('[AppContext.deleteGasto] No se pudo eliminar gasto:', { error, id });
        throw error;
      }
    }

    setData(d => ({ ...d, gastos: (d.gastos || []).filter(row => String(row.id) !== String(id)) }));

    if (!options.skipSubscriptionSync && current?.suscripcion_id) {
      if (isConfigured && !USE_SEED && !demoMode) {
        const { error } = await supabase.from('suscripciones').delete().eq('id', current.suscripcion_id);
        if (error) {
          console.error('[AppContext.deleteGasto] No se pudo eliminar suscripcion vinculada:', { error, suscripcion_id: current.suscripcion_id });
          throw error;
        }
      }
      setData(d => ({ ...d, suscripciones: (d.suscripciones || []).filter(row => String(row.id) !== String(current.suscripcion_id)) }));
    }
  }, [data.gastos, demoMode]);

  const syncSubscriptionExpense = useCallback(async (suscripcion = {}, action = 'upsert') => {
    if (!suscripcion?.id) return null;
    const existing = findSubscriptionExpense(data.gastos || [], suscripcion);
    const shouldKeep = action !== 'delete' && shouldMirrorSubscriptionToExpense(suscripcion);

    if (!shouldKeep) {
      const ids = existing?.id ? [existing.id] : [];
      if (isConfigured && !USE_SEED && !demoMode) {
        if (ids.length) {
          const { error } = await supabase.from('gastos').delete().in('id', ids);
          if (error) {
            console.error('[AppContext.syncSubscriptionExpense] No se pudo eliminar gasto de suscripcion:', { error, ids, suscripcion_id: suscripcion.id });
            throw error;
          }
        } else {
          const { error } = await supabase.from('gastos').delete().eq('suscripcion_id', suscripcion.id);
          if (error) {
            console.error('[AppContext.syncSubscriptionExpense] No se pudo eliminar gasto vinculado por suscripcion_id:', { error, suscripcion_id: suscripcion.id });
            throw error;
          }
        }
      }
      setData(d => ({
        ...d,
        gastos: (d.gastos || []).filter(row => String(row.suscripcion_id || '') !== String(suscripcion.id) && (!existing?.id || String(row.id) !== String(existing.id))),
      }));
      return null;
    }

    const payload = filterGastoPayload(buildSubscriptionExpensePayload(suscripcion, existing || {}));
    if (existing?.id) {
      if (isConfigured && !USE_SEED && !demoMode) {
        const { data: updated, error } = await supabase.from('gastos').update(payload).eq('id', existing.id).select().single();
        if (error) {
          console.error('[AppContext.syncSubscriptionExpense] No se pudo actualizar gasto de suscripcion:', { error, payload });
          throw error;
        }
        const saved = updated || { ...existing, ...payload };
        setData(d => ({ ...d, gastos: (d.gastos || []).map(row => String(row.id) === String(existing.id) ? saved : row) }));
        return saved;
      }
      const saved = { ...existing, ...payload };
      setData(d => ({ ...d, gastos: (d.gastos || []).map(row => String(row.id) === String(existing.id) ? saved : row) }));
      return saved;
    }

    if (isConfigured && !USE_SEED && !demoMode) {
      const { data: inserted, error } = await supabase.from('gastos').insert([payload]).select().single();
      if (error) {
        console.error('[AppContext.syncSubscriptionExpense] No se pudo crear gasto de suscripcion:', { error, payload });
        throw error;
      }
      const saved = inserted || { ...payload, id: crypto.randomUUID?.() || Date.now().toString() };
      setData(d => ({ ...d, gastos: [saved, ...(d.gastos || [])] }));
      return saved;
    }

    const saved = { ...payload, id: payload.id || crypto.randomUUID?.() || Date.now().toString() };
    setData(d => ({ ...d, gastos: [saved, ...(d.gastos || [])] }));
    return saved;
  }, [data.gastos, demoMode]);

  const addCobro = useCallback(async (cobro) => {
    const trustedInsertSource = Boolean(
      cobro?.stripe_session_id ||
      cobro?.checkout_session_id ||
      cobro?.stripe_id ||
      cobro?.transferencia_aprobada === true ||
      (
        String(cobro?.transferencia_estado || '').toLowerCase() === 'transferencia_aprobada' &&
        String(cobro?.payment_source || '').toLowerCase() === 'bank_transfer'
      ) ||
      String(cobro?.paid_via || '').toLowerCase().includes('transferencia aprobada') ||
      String(cobro?.paid_via || '').toLowerCase().includes('stripe verified') ||
      String(cobro?.paid_via || '').toLowerCase().includes('stripe webhook') ||
      String(cobro?.paid_via || '').toLowerCase().includes('stripe_webhook') ||
      String(cobro?.paid_via || '').toLowerCase().includes('stripe_verify')
    );
    const unsafePaidInsert = String(cobro?.status || '').toLowerCase() === 'paid' && !trustedInsertSource;
    const safeCobro = unsafePaidInsert && !demoMode
      ? { ...cobro, status: 'pending', paid_at: null, fecha_pago: null }
      : cobro;

    if (unsafePaidInsert && !demoMode) {
      console.error('[AppContext.addCobro] Cobro paid bloqueado sin confirmacion Stripe o transferencia aprobada.', {
        tipo: cobro?.tipo || cobro?.nombre,
        cliente_id: cobro?.cliente_id || cobro?.clienteId,
      });
    }

    if (!isConfigured || USE_SEED || demoMode || safeCobro?.is_demo) {
      const newCobro = { ...safeCobro, id: crypto.randomUUID?.() || Date.now().toString(), is_demo: safeCobro?.is_demo ?? demoMode };
      setData(d => ({ ...d, cobros: [newCobro, ...d.cobros] }));
      return newCobro;
    }

    if (isConfigured) {
      const { clienteId, concepto: _concepto, ...restCobro } = safeCobro || {};
      const clienteForAttribution = (data.clientes || []).find(cliente => String(cliente.id) === String(restCobro.cliente_id || clienteId || ''));
      const quoteForAttribution = (data.cotizacionesEnviadas || []).find(quote => String(quote.id) === String(restCobro.quote_id || restCobro.quoteId || ''));
      const inheritedAttribution = inheritedMarketingAttribution({
        quote: quoteForAttribution,
        cliente: clienteForAttribution,
      });
      const cleanCobro = {
        ...restCobro,
        cliente_id: restCobro.cliente_id || clienteId || null,
        nombre: restCobro.nombre || restCobro.tipo || 'Cobro',
        created_at: restCobro.created_at || new Date().toISOString(),
        ...mergeMissingMarketingAttribution(restCobro, inheritedAttribution),
      };
      const { data: inserted, error } = await supabase
        .from('cobros')
        .insert([cleanCobro])
        .select().single();
      if (!error && inserted) {
        const normalized = { ...inserted, clienteId: inserted.cliente_id };
        setData(d => ({ ...d, cobros: [normalized, ...d.cobros] }));
        return normalized;
      }
      console.error('[AppContext.addCobro] Supabase rechazo el cobro:', error?.message || error);
      throw error || new Error('No se pudo guardar el cobro en Supabase.');
    }
    const newCobro = { ...safeCobro, id: crypto.randomUUID?.() || Date.now().toString() };
    setData(d => ({ ...d, cobros: [newCobro, ...d.cobros] }));
    return newCobro;
  }, [data.clientes, data.cotizacionesEnviadas, demoMode]);


  // ── PONERSE AL DIA DESPUES DE UNA OPERACION SERVER-SIDE ────────────────────
  // `approve_final_bank_transfer` (migracion 194) hace TODO el trabajo dentro de
  // una transaccion: cobro, proyecto y portal_progreso ya quedaron escritos
  // cuando esto corre. Aqui NO se escribe nada — solo se releen las filas para
  // que la pantalla deje de mostrar el estado anterior. Cualquier `update` desde
  // el navegador en este punto seria una segunda verdad compitiendo con la que
  // el servidor ya sello.
  const sincronizarCobroTrasAprobacion = useCallback(async (cobroId) => {
    if (!isConfigured || USE_SEED || !cobroId) return null;
    const { data: cobro, error } = await supabase.from('cobros').select('*').eq('id', cobroId).maybeSingle();
    if (error || !cobro) {
      console.warn('[AppContext.sincronizarCobroTrasAprobacion] No se pudo releer el cobro:', error?.message || error);
      return null;
    }
    let proyecto = null;
    if (cobro.proyecto_id) {
      const { data: filaProyecto } = await supabase.from('proyectos').select('*').eq('id', cobro.proyecto_id).maybeSingle();
      proyecto = filaProyecto || null;
    }
    setData(d => ({
      ...d,
      cobros: (d.cobros || []).map(c => (String(c.id) === String(cobroId) ? { ...c, ...cobro } : c)),
      proyectos: proyecto
        ? (d.proyectos || []).map(p => (String(p.id) === String(proyecto.id) ? { ...p, ...proyecto } : p))
        : (d.proyectos || []),
    }));
    return { cobro, proyecto };
  }, []);

  const updateCobro = useCallback(async (id, changes) => {
    const normalizedChanges = { ...changes };
    if (normalizedChanges.clienteId && !normalizedChanges.cliente_id) {
      normalizedChanges.cliente_id = normalizedChanges.clienteId;
      delete normalizedChanges.clienteId;
    }

    const previousCobro = (data.cobros || []).find(c => String(c.id) === String(id));
    const isMarkingPaid = changes?.status === 'paid' && previousCobro?.status !== 'paid';
    const isDemoCobro = Boolean(demoMode || previousCobro?.is_demo || changes?.is_demo);
    const previousQuoteForInitialPayment = (data.cotizacionesEnviadas || []).find(q => (
      String(q.initial_payment_cobro_id || '') === String(id)
      || (previousCobro?.quote_id && String(q.id || '') === String(previousCobro.quote_id))
      || (changes?.quote_id && String(q.id || '') === String(changes.quote_id))
    ));
    // 🔴 EL SILENCIO QUE SE ELIMINA: todo el avance de la cotizacion cuelga de HABERLA
    // encontrado en la cache. Si el cobro DECLARA una cotizacion y esa fila no aparece, hoy
    // el bloque entero se salta sin decir nada: pago confirmado, cotizacion sin avanzar,
    // acceso sin preparar. Pasa con un usuario `finanzas` —rol soportado por el producto—
    // porque el front autoriza por MODULO y la RLS de cotizaciones_enviadas por ROL.
    const diagnosticoCotizacion = diagnosticoDeCotizacionDelCobro({
      cobro: previousCobro,
      cambios: changes,
      cotizacionEncontrada: previousQuoteForInitialPayment,
    });

    const isQuoteInitialPaymentCobro = Boolean(previousQuoteForInitialPayment) && (
      String(previousQuoteForInitialPayment?.initial_payment_cobro_id || '') === String(id)
      || String(previousCobro?.payment_source || changes?.payment_source || '').toLowerCase() === 'quote_initial_payment'
    );
    const trustedPaymentSource = Boolean(
      changes?.stripe_session_id ||
      changes?.checkout_session_id ||
      changes?.stripe_id ||
      changes?.transferencia_aprobada === true ||
      (
        String(changes?.transferencia_estado || '').toLowerCase() === 'transferencia_aprobada' &&
        String(changes?.payment_source || '').toLowerCase() === 'bank_transfer'
      ) ||
      String(changes?.paid_via || '').toLowerCase().includes('transferencia aprobada') ||
      String(changes?.paid_via || '').toLowerCase().includes('stripe verified') ||
      String(changes?.paid_via || '').toLowerCase().includes('stripe webhook') ||
      String(changes?.paid_via || '').toLowerCase().includes('stripe_webhook') ||
      String(changes?.paid_via || '').toLowerCase().includes('stripe_verify')
    );

    if (isMarkingPaid && !isDemoCobro && !trustedPaymentSource) {
      console.error('[AppContext.updateCobro] Bloqueado cambio a paid sin Stripe verificado o transferencia aprobada.', {
        cobro_id: id,
        previous_status: previousCobro?.status,
        requested: changes,
      });
      return { ok: false, blocked: true, error: 'Pago bloqueado: falta confirmacion real.' };
    }

    const paidAt = isMarkingPaid ? (changes?.paid_at || changes?.fecha_pago || new Date().toISOString()) : null;
    const paymentTimestamps = isMarkingPaid ? { paid_at: paidAt, fecha_pago: paidAt } : {};
    const isApprovedBankTransfer = Boolean(
      String(normalizedChanges.transferencia_estado || '').toLowerCase() === 'transferencia_aprobada'
      || String(normalizedChanges.paid_via || '').toLowerCase().includes('transferencia aprobada')
    );

    if (isMarkingPaid && isConfigured && !USE_SEED && !isDemoCobro && isQuoteInitialPaymentCobro && isApprovedBankTransfer) {
      // PASO 1 — Marcar 'paid' desde la SESIÓN DEL STAFF (client-side). El guard 088 solo acepta la
      // transferencia con has_role('admin','finanzas') + auth.uid() + transferencia_validada_por = auth.uid()
      // + un audit 'transferencia_aprobada' reciente por auth.uid() (que confirmApproveTransfer ya escribió).
      // La edge fn corre como service_role (auth.uid()=null) y NO puede pasar ese guard; por eso se marca acá.
      const paidPatch = {
        status: 'paid',
        transferencia_estado: 'transferencia_aprobada',
        transferencia_validada_at: normalizedChanges.transferencia_validada_at || paidAt,
        transferencia_validada_por: normalizedChanges.transferencia_validada_por || null,
        paid_at: paidAt,
        fecha_pago: paidAt,
        paid_via: 'transferencia aprobada',
        payment_source: 'quote_initial_payment',
        payment_reference: normalizedChanges.payment_reference || previousCobro?.comprobante_path || (previousCobro?.comprobante_url ? `legacy_receipt_reference:${id}` : null),
        updated_at: paidAt,
      };
      const { data: paidRow, error: paidError } = await supabase
        .from('cobros')
        .update(paidPatch)
        .eq('id', id)
        .select('*')
        .maybeSingle();
      if (paidError || !paidRow) {
        // Borde 1: el guard rechazó → NADA se invoca; el cobro queda en revisión (re-aprobable).
        console.error('[AppContext.updateCobro] Guard rechazó marcar el anticipo como pagado:', paidError?.message || paidError);
        return { ok: false, blocked: true, error: paidError?.message || 'No se pudo marcar el cobro como pagado.' };
      }
      // Reflejar el 'paid' en el estado local YA (por si la edge fn falla después: el pago igual está hecho).
      setData(d => ({
        ...d,
        cobros: (d.cobros || []).map(c => String(c.id) === String(id)
          ? { ...c, ...normalizedChanges, ...paymentTimestamps, ...paidRow, clienteId: paidRow.cliente_id || normalizedChanges.cliente_id || c.clienteId }
          : c),
      }));

      // PASO 2 — La edge fn (service_role) ve el cobro YA 'paid' → SALTA su patchCobroPaid (cobroIsPaid) y
      // solo entrega accesos + emite la factura. Su fallo NO revierte el pago (que ya está aprobado).
      try {
        const { data: approvalResult, error: approvalError } = await supabase.functions.invoke('approve-quote-initial-transfer', {
          body: {
            cobro_id: id,
            payment_reference: paidPatch.payment_reference,
            approved_at: paidPatch.transferencia_validada_at,
          },
        });
        if (approvalError || !approvalResult?.ok) {
          throw new Error(approvalResult?.error || approvalError?.message || 'entrega de accesos/factura falló');
        }
        const returnedQuote = approvalResult.quote || null;
        if (returnedQuote?.id) {
          setData(d => ({
            ...d,
            cotizacionesEnviadas: (d.cotizacionesEnviadas || []).map(q => String(q.id) === String(returnedQuote.id) ? { ...q, ...returnedQuote } : q),
          }));
        }
        return { ok: true, accessStatus: approvalResult.access_status || null, warnings: approvalResult.warnings || [] };
      } catch (deliveryError) {
        // Borde 2: el pago YA está aprobado; solo la ENTREGA (accesos/factura) quedó diferida y es reintentable.
        // La factura la retoma el cron retry-facturas; los accesos, re-disparando la entrega del cobro ya pagado.
        console.warn('[AppContext.updateCobro] Pago aprobado; entrega de accesos/factura diferida (reintentable):', deliveryError?.message || deliveryError);
        return { ok: true, paid: true, accessStatus: 'pending_retry', warnings: [`entrega_diferida: ${deliveryError?.message || deliveryError}`] };
      }
    }

    let clienteForPayment = null;
    let access = null;
    let finalPaymentUnlock = null;
    let finalPaymentClientId = null;
    let finalPaymentProjectId = null;
    let quoteInitialPaymentPatch = null;
    // Qué pasó REALMENTE con la cotización: lo dice la RPC de la 184, no el hecho de llamarla.
    let avanceDeCotizacion = null;
    const resolveFinalPaymentProject = async (cobro, cobroChanges = {}, fallbackClientId = null) => {
      const directProjectId =
        cobro?.proyecto_id ||
        cobro?.proyectoId ||
        cobroChanges?.proyecto_id ||
        cobroChanges?.proyectoId ||
        null;
      const approvalActId =
        cobro?.approval_act_id ||
        cobro?.approvalActId ||
        cobroChanges?.approval_act_id ||
        cobroChanges?.approvalActId ||
        null;

      if (directProjectId) {
        const project = (data.proyectos || []).find(p => String(p.id) === String(directProjectId));
        return {
          projectId: directProjectId,
          clientId: project?.cliente_id || project?.clienteId || fallbackClientId || cobro?.cliente_id || cobro?.clienteId || null,
          project,
          approvalActId,
        };
      }

      if (!approvalActId) {
        throw new Error('El pago final no tiene proyecto_id ni approval_act_id para desbloquear el proyecto.');
      }

      const localAct = (data.projectApprovalActs || []).find(act => String(act.id) === String(approvalActId));
      let approvalAct = localAct;

      if (!approvalAct && isConfigured && !USE_SEED) {
        const { data: fetchedAct, error: actError } = await supabase
          .from('project_approval_acts')
          .select('id, proyecto_id, cliente_id')
          .eq('id', approvalActId)
          .maybeSingle();

        if (actError) {
          console.error('[AppContext.updateCobro] No se pudo consultar el acta de aprobacion del pago final:', actError.message || actError);
          throw new Error('No se pudo consultar el acta de aprobacion asociada al pago final.');
        }

        approvalAct = fetchedAct;
      }

      if (!approvalAct?.proyecto_id) {
        throw new Error('No se pudo resolver el proyecto asociado al acta de aprobacion del pago final.');
      }

      const project = (data.proyectos || []).find(p => String(p.id) === String(approvalAct.proyecto_id));
      return {
        projectId: approvalAct.proyecto_id,
        clientId: approvalAct.cliente_id || project?.cliente_id || project?.clienteId || fallbackClientId || cobro?.cliente_id || cobro?.clienteId || null,
        project,
        approvalActId,
      };
    };

    if (isMarkingPaid && previousCobro) {
      const clientId = previousCobro.clienteId || previousCobro.cliente_id || changes.cliente_id;
      const rawStage = String(previousCobro.payment_stage || previousCobro.tipo || previousCobro.nombre || changes.payment_stage || changes.tipo || changes.nombre || '').toLowerCase();
      const isAdvance60 = rawStage.includes('anticipo_60') || rawStage.includes('anticipo 60') || rawStage.includes('60%');
      const isFinal40 = rawStage.includes('final_40') || rawStage.includes('saldo final') || rawStage.includes('pago final') || rawStage.includes('40%');
      clienteForPayment = (data.clientes || []).find(c => String(c.id) === String(clientId));
      if (clienteForPayment && isAdvance60 && !isDemoCobro && !isQuoteInitialPaymentCobro) {
        access = buildPortalAccess(clienteForPayment);
      }
      if (isQuoteInitialPaymentCobro && previousQuoteForInitialPayment?.id) {
        const quoteInitialMethodText = [
          previousCobro?.payment_source,
          changes?.payment_source,
          previousCobro?.paid_via,
          changes?.paid_via,
          previousCobro?.via,
          changes?.via,
          previousCobro?.transferencia_estado,
          changes?.transferencia_estado,
        ].map(value => String(value || '').toLowerCase()).join(' ');
        const quoteInitialMethod = quoteInitialMethodText.includes('transferencia') || quoteInitialMethodText.includes('bank')
          ? 'transferencia'
          : 'stripe';
        quoteInitialPaymentPatch = {
          quote_onboarding_status: 'access_ready',
          initial_payment_status: 'confirmed',
          initial_payment_method: quoteInitialMethod,
          initial_payment_amount: Number(previousCobro?.monto || changes?.monto || previousQuoteForInitialPayment.initial_payment_amount || 0),
          initial_payment_cobro_id: id,
          initial_payment_confirmed_at: paidAt,
          access_preparation_status: 'ready',
          access_prepared_at: paidAt,
          access_error: null,
          updated_at: paidAt,
        };
      }
      if (isFinal40 && !isDemoCobro) {
        const resolvedFinalPayment = await resolveFinalPaymentProject(previousCobro, changes, clientId);
        finalPaymentClientId = resolvedFinalPayment.clientId;
        finalPaymentProjectId = resolvedFinalPayment.projectId;
        const currentFinalProject =
          resolvedFinalPayment.project ||
          (data.proyectos || []).find(p => String(p.id) === String(finalPaymentProjectId));
        const statusProject = {
          ...(currentFinalProject || {}),
          final_payment_paid_at: currentFinalProject?.final_payment_paid_at || paidAt,
          brandkit_unlocked_at: currentFinalProject?.brandkit_unlocked_at || paidAt,
        };
        finalPaymentUnlock = {
          final_payment_paid_at: statusProject.final_payment_paid_at,
          brandkit_unlocked_at: statusProject.brandkit_unlocked_at,
          production_status: resolveProductionStatus(statusProject, 'brandkit_liberado'),
          updated_at: paidAt,
        };
      }
    }

    const syncPaidBeforeLocal = Boolean(isMarkingPaid && isConfigured && !USE_SEED && !isDemoCobro);
    if (syncPaidBeforeLocal) {
      // Marcar un cobro como pagado es de lo mas caro de perder en silencio: si RLS lo
      // rechaza y nadie se entera, el sistema cree cobrado algo que no lo esta.
      const resultado = await updatePorId(
        supabase,
        'cobros',
        id,
        { ...normalizedChanges, ...paymentTimestamps, updated_at: new Date().toISOString() },
        { queSeGuardaba: 'el cobro' },
      );
      const error = resultado.ok ? null : resultado.error;
      if (error) {
        console.error('[AppContext.updateCobro] Supabase rechazo marcar cobro como paid:', error.message || error);
        throw error;
      }

      if (finalPaymentUnlock) {
        if (finalPaymentProjectId) {
          const { error: projectError } = await supabase
            .from('proyectos')
            .update(finalPaymentUnlock)
            .eq('id', finalPaymentProjectId);
          if (projectError) {
            console.error('[AppContext.updateCobro] Pago final confirmado, pero no se pudo habilitar entrega en proyecto:', projectError.message || projectError);
            throw projectError;
          }
        }
        if (finalPaymentClientId && isConfigured && !USE_SEED) {
          try {
            const { error: progressError } = await supabase
              .from('portal_progreso')
              .update({
                final_payment_paid_at: finalPaymentUnlock.final_payment_paid_at,
                brandkit_unlocked_at: finalPaymentUnlock.brandkit_unlocked_at,
                updated_at: paidAt,
              })
              .eq('cliente_id', finalPaymentClientId);

            if (progressError) {
              console.warn('[AppContext.updateCobro] Pago final confirmado, pero no se pudo sincronizar portal_progreso:', progressError.message || progressError);
            }
          } catch (progressError) {
            console.warn('[AppContext.updateCobro] Error sincronizando portal_progreso tras pago final:', progressError);
          }
        }
        if (finalPaymentClientId && isConfigured && !USE_SEED) {
          supabase.from('notificaciones').insert([{
            tipo: 'pago_final_confirmado',
            titulo: 'Pago final 40% confirmado',
            descripcion: 'Transferencia final aprobada. Entrega y Brand Kit quedan habilitados de forma controlada.',
            cliente_id: finalPaymentClientId,
            proyecto_id: finalPaymentProjectId || null,
            cobro_id: id,
            channel: 'interno',
            leida: false,
            created_at: paidAt,
            payload: { cobro_id: id, payment_stage: 'final_40', source: 'bank_transfer' },
          }]).then(({ error }) => {
            if (error) console.error('[AppContext.updateCobro] No se pudo registrar notificacion de pago final:', error);
          }).catch(error => {
            console.error('[AppContext.updateCobro] Error registrando notificacion de pago final:', error);
          });
        }
      }

      // Emision fiscal SRI del saldo 40% (solo transferencia nacional aprobada; finalPaymentUnlock
      // ya implica final_40 no-demo). Fire-and-record: no bloquea el pago; el peor caso es una
      // factura 'pendiente' que el cron retry-facturas reintenta. El 60% NO pasa por aqui (lo
      // emite approve-quote-initial-transfer server-side y retorna antes). Ver supabase/functions/emitirFactura.
      if (finalPaymentUnlock && isApprovedBankTransfer) {
        try {
          const { error: facturaError } = await supabase.functions.invoke('emitirFactura', { body: { cobro_id: id } });
          if (facturaError) console.error('[AppContext.updateCobro] emitirFactura (saldo 40%) fallo, no bloquea el pago:', facturaError.message || facturaError);
        } catch (facturaError) {
          console.error('[AppContext.updateCobro] emitirFactura (saldo 40%) excepcion, no bloquea el pago:', facturaError);
        }
      }

      if (quoteInitialPaymentPatch && previousQuoteForInitialPayment?.id) {
        // El efecto financiero sobre la cotización pasa por la frontera de la 184: un solo
        // camino para admin, crm y finanzas. Ver lib/cobroCotizacion.js.
        const avance = await avanzarCotizacionPorCobro(supabase, id, {
          metodo: quoteInitialPaymentPatch.initial_payment_method,
          monto: quoteInitialPaymentPatch.initial_payment_amount,
          confirmadoAt: paidAt,
        });
        avanceDeCotizacion = avance.estado;
        if (avance.estado !== 'avanzada') {
          console.error('[AppContext.updateCobro] Pago inicial confirmado, la cotizacion NO avanzo:',
            avance.estado, avance.error?.message || '');
        }
        supabase.from('notificaciones').insert([{
          tipo: 'quote_initial_payment_confirmed',
          titulo: 'Confirmacion inicial recibida',
          descripcion: 'Pago inicial de cotizacion confirmado. Accesos listos para enviar de forma interna.',
          cliente_id: previousQuoteForInitialPayment.cliente_id || previousCobro?.cliente_id || null,
          cobro_id: id,
          channel: 'interno',
          leida: false,
          created_at: paidAt,
          payload: { cobro_id: id, quote_id: previousQuoteForInitialPayment.id, source: 'appcontext-update-cobro' },
        }]).then(({ error }) => {
          if (error) console.error('[AppContext.updateCobro] No se pudo registrar notificacion de confirmacion inicial:', error);
        }).catch(error => {
          console.error('[AppContext.updateCobro] Error registrando notificacion de confirmacion inicial:', error);
        });
      }
    }

    if (access && clienteForPayment) {
      const phaseResult = await advanceClientPhase({
        clienteId: clienteForPayment.id,
        targetStage: 4,
        source: 'payment_advance_60',
        metadata: { cobro_id: id, payment_stage: 'anticipo_60' },
        relatedEntityType: 'cobro',
        relatedEntityId: id,
        allowCompound: true,
        silent: true,
      });
      if (!phaseResult.ok) {
        console.error('[AppContext.updateCobro] Pago confirmado, pero no se pudo avanzar fase CRM:', phaseResult.error || phaseResult);
      }
    }

    setData(d => {
      const nextClientes = access && clienteForPayment
        ? d.clientes.map(c => String(c.id) === String(clienteForPayment.id)
            ? { ...c, ...access }
            : c)
        : d.clientes;

      const notif = access && clienteForPayment ? {
        id: crypto.randomUUID?.() || `${Date.now()}-portal-access`,
        tipo: 'portal_access',
        titulo: 'Pago 60% confirmado · acceso enviado',
        desc: `${clienteForPayment.nombre} realizó el pago. Se enviaron credenciales por correo y WhatsApp.`,
        descripcion: `${clienteForPayment.nombre} realizó el pago. Se enviaron credenciales por correo y WhatsApp.`,
        cliente_id: clienteForPayment.id,
        cobro_id: id,
        leida: false,
        created_at: paidAt,
      } : null;

      const finalPaymentNotif = finalPaymentUnlock ? {
        id: crypto.randomUUID?.() || `${Date.now()}-final-payment`,
        tipo: 'pago_final_confirmado',
        titulo: 'Pago final 40% confirmado',
        desc: 'Transferencia final aprobada. Entrega y Brand Kit quedan habilitados de forma controlada.',
        descripcion: 'Transferencia final aprobada. Entrega y Brand Kit quedan habilitados de forma controlada.',
        cliente_id: finalPaymentClientId,
        proyecto_id: finalPaymentProjectId || null,
        cobro_id: id,
        channel: 'interno',
        leida: false,
        created_at: paidAt,
      } : null;

      return {
        ...d,
        clientes: nextClientes,
        proyectos: finalPaymentUnlock && finalPaymentProjectId
          ? (d.proyectos || []).map(p => String(p.id) === String(finalPaymentProjectId) ? { ...p, ...finalPaymentUnlock } : p)
          : d.proyectos,
        cotizacionesEnviadas: quoteInitialPaymentPatch && previousQuoteForInitialPayment?.id
          ? (d.cotizacionesEnviadas || []).map(q => String(q.id) === String(previousQuoteForInitialPayment.id) ? { ...q, ...quoteInitialPaymentPatch } : q)
          : d.cotizacionesEnviadas,
        cobros: d.cobros.map(c => String(c.id) === String(id) ? { ...c, ...changes, ...(changes.cliente_id ? { clienteId: changes.cliente_id } : {}), ...paymentTimestamps } : c),
        notificaciones: [finalPaymentNotif, notif].filter(Boolean).length
          ? [...[finalPaymentNotif, notif].filter(Boolean), ...d.notificaciones]
          : d.notificaciones,
      };
    });

    if (isConfigured && !USE_SEED && !isDemoCobro) {
      if (!syncPaidBeforeLocal) {
        const { error } = await supabase
          .from('cobros')
          .update({ ...normalizedChanges, ...paymentTimestamps, updated_at: new Date().toISOString() })
          .eq('id', id);
        if (error) {
          console.error('[AppContext.updateCobro] Supabase rechazo updateCobro:', error.message || error);
          throw error;
        }
      }

      if (quoteInitialPaymentPatch && previousQuoteForInitialPayment?.id && !syncPaidBeforeLocal) {
        // Mismo camino que arriba: la frontera server-side de la 184.
        const avance = await avanzarCotizacionPorCobro(supabase, id, {
          metodo: quoteInitialPaymentPatch.initial_payment_method,
          monto: quoteInitialPaymentPatch.initial_payment_amount,
          confirmadoAt: paidAt,
        });
        avanceDeCotizacion = avance.estado;
        if (avance.estado !== 'avanzada') {
          console.error('[AppContext.updateCobro] Cotizacion NO avanzo tras el pago inicial:',
            avance.estado, avance.error?.message || '');
        }
        supabase.from('notificaciones').insert([{
          tipo: 'quote_initial_payment_confirmed',
          titulo: 'Confirmacion inicial recibida',
          descripcion: 'Pago inicial de cotizacion confirmado. Accesos listos para enviar de forma interna.',
          cliente_id: previousQuoteForInitialPayment.cliente_id || previousCobro?.cliente_id || null,
          cobro_id: id,
          channel: 'interno',
          leida: false,
          created_at: paidAt,
          payload: { cobro_id: id, quote_id: previousQuoteForInitialPayment.id, source: 'appcontext-update-cobro' },
        }]).then(({ error }) => {
          if (error) console.error('[AppContext.updateCobro] No se pudo registrar notificacion de confirmacion inicial:', error);
        }).catch(error => {
          console.error('[AppContext.updateCobro] Error registrando notificacion de confirmacion inicial:', error);
        });
      }

      if (access && clienteForPayment) {
        // Este update entrega el ACCESO al portal tras confirmar el pago. Perderlo en
        // silencio es el caso David Naranjo: pago cobrado y cliente sin poder entrar.
        const accesoResultado = await updatePorId(
          supabase,
          'clientes',
          clienteForPayment.id,
          { ...access, updated_at: new Date().toISOString() },
          { queSeGuardaba: 'el acceso del cliente' },
        );
        const clienteUpdateError = accesoResultado.ok ? null : accesoResultado.error;
        if (clienteUpdateError) {
          console.error('[AppContext.updateCobro] Pago confirmado, pero no se pudo actualizar acceso del cliente:', clienteUpdateError);
        }
        import('../lib/emails').then(({ emailBrief }) => emailBrief({
          clienteNombre: clienteForPayment.nombre,
          clienteEmail: clienteForPayment.email,
          briefLink: access.portal_url,
        })).catch(error => {
          console.error('[AppContext.updateCobro] No se pudo enviar email de brief tras pago:', error);
        });
        // La notificación se escribía en paralelo al aprovisionamiento y ya
        // afirmaba "acceso enviado · se enviaron credenciales por correo y
        // WhatsApp". Nadie esperaba a saberlo: si `provision-client-access`
        // fallaba, el equipo leía en su bandeja que el cliente ya tenía acceso
        // y dejaba de mirar. Ahora el aviso se escribe DESPUÉS y dice lo que
        // realmente pasó.
        //
        // El pago no se revierte por esto: ya está confirmado. Lo que cambia es
        // que el aviso deja de ser una afirmación sin respaldo.
        //
        // Que estos tres pasos —cobro, acceso, aviso— no sean una sola
        // operación atómica es un problema distinto y sigue abierto en §7: el
        // cron `retry-access` es la red, no el arreglo de fondo.
        (async () => {
          let accesoEntregado = false;
          let motivo = '';
          try {
            const { data: acceso, error: accesoError } = await supabase.functions
              .invoke('provision-client-access', { body: { cliente_id: clienteForPayment.id } });
            if (accesoError) motivo = accesoError.message || 'la función de acceso devolvió error';
            else if (acceso && acceso.ok === false) motivo = acceso.error || 'la función de acceso no completó la entrega';
            else accesoEntregado = true;
          } catch (error) {
            motivo = error?.message || 'no se pudo invocar la entrega de acceso';
          }
          if (!accesoEntregado) console.error('[AppContext.updateCobro] provision-client-access fallo:', motivo);

          const { error: notifError } = await supabase.from('notificaciones').insert([{
            tipo: 'portal_access',
            titulo: accesoEntregado
              ? 'Pago 60% confirmado · acceso enviado'
              : 'Pago 60% confirmado · ACCESO PENDIENTE',
            descripcion: accesoEntregado
              ? `${clienteForPayment.nombre} realizó el pago. Se enviaron credenciales por correo y WhatsApp.`
              : `${clienteForPayment.nombre} realizó el pago, pero la entrega de acceso NO se completó (${motivo}). Revisar y reintentar.`,
            cliente_id: clienteForPayment.id,
            cobro_id: id,
            leida: false,
            created_at: paidAt,
          }]).select('id');
          if (notifError) console.error('[AppContext.updateCobro] No se pudo registrar notificacion de portal_access:', notifError);
        })();
      }
    }
    // El cobro SI se guardo: no se lanza. Pero lo que quedo a medias viaja al llamador para
    // que la pantalla lo diga, en vez de mostrar un exito liso.
    // 🔴 EL RESULTADO DE LA RPC MANDA sobre el diagnóstico de caché: la 184 resuelve la
    // cotización en el servidor, así que si dijo 'avanzada' el efecto ocurrió aunque el front
    // no tuviera la fila. Y si falló, se dice — «la RPC respondió» no es «el efecto ocurrió».
    if (isMarkingPaid && avanceDeCotizacion && avanceDeCotizacion !== 'avanzada') {
      const aviso = avisoDeAvanceDeCotizacion(avanceDeCotizacion);
      if (aviso) return { ok: true, avisoCotizacion: aviso };
    }
    // Solo si NUNCA se intentó avanzar (la cotización no estaba en caché y no se llamó a la
    // RPC) sigue aplicando el diagnóstico anterior, que es lo que detecta ese hueco.
    if (isMarkingPaid && !avanceDeCotizacion && diagnosticoCotizacion === 'declarada_no_visible') {
      console.error('[AppContext.updateCobro] Cobro pagado, pero la cotizacion declarada no es visible con este acceso:', {
        cobro_id: id, quote_id: previousCobro?.quote_id || changes?.quote_id || null,
      });
      return { ok: true, avisoCotizacion: AVISO_COTIZACION_NO_VISIBLE };
    }
    return { ok: true };
  }, [advanceClientPhase, data.cobros, data.clientes, data.cotizacionesEnviadas, data.projectApprovalActs, data.proyectos, demoMode]);

  const addIngreso = useCallback(async (ingreso = {}) => {
    const now = new Date().toISOString();
    const cobroForAttribution = (data.cobros || []).find(cobro => String(cobro.id) === String(ingreso.cobro_id || ingreso.cobroId || ''));
    const clienteForAttribution = (data.clientes || []).find(cliente => String(cliente.id) === String(ingreso.cliente_id || ingreso.clienteId || cobroForAttribution?.cliente_id || cobroForAttribution?.clienteId || ''));
    const attributionPatch = mergeMissingMarketingAttribution(
      ingreso,
      inheritedMarketingAttribution({ cobro: cobroForAttribution, cliente: clienteForAttribution }),
    );
    const row = calculateIngresoLocally({
      ...ingreso,
      ...attributionPatch,
      id: ingreso.id || crypto.randomUUID?.() || Date.now().toString(),
      cliente_id: nullableUuid(ingreso.cliente_id || ingreso.clienteId),
      proyecto_id: nullableUuid(ingreso.proyecto_id || ingreso.proyectoId),
      cobro_id: nullableUuid(ingreso.cobro_id || ingreso.cobroId),
      origen_ingreso: ingreso.origen_ingreso || 'nacional',
      primer_pago_pct: ingreso.primer_pago_pct ?? 60,
      created_at: ingreso.created_at || now,
      updated_at: now,
      created_by: ingreso.created_by || user?.id || null,
    });

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, ingresos: [row, ...(d.ingresos || [])] }));
      return row;
    }

    const payload = filterIngresoPayload(row);
    const { data: inserted, error } = await supabase.from('ingresos').insert([payload]).select().single();
    if (error) {
      console.error('[AppContext.addIngreso] No se pudo guardar ingreso:', { error, payload });
      throw error;
    }

    const saved = inserted || row;
    setData(d => ({ ...d, ingresos: [saved, ...(d.ingresos || [])] }));
    return saved;
  }, [data.cobros, data.clientes, demoMode, user?.id]);

  const updateIngreso = useCallback(async (id, changes = {}) => {
    const normalizedChanges = { ...changes, updated_at: new Date().toISOString() };
    if (Object.prototype.hasOwnProperty.call(changes, 'clienteId') || Object.prototype.hasOwnProperty.call(changes, 'cliente_id')) {
      normalizedChanges.cliente_id = nullableUuid(changes.cliente_id || changes.clienteId);
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'proyectoId') || Object.prototype.hasOwnProperty.call(changes, 'proyecto_id')) {
      normalizedChanges.proyecto_id = nullableUuid(changes.proyecto_id || changes.proyectoId);
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'cobroId') || Object.prototype.hasOwnProperty.call(changes, 'cobro_id')) {
      normalizedChanges.cobro_id = nullableUuid(changes.cobro_id || changes.cobroId);
    }
    delete normalizedChanges.clienteId;
    delete normalizedChanges.proyectoId;
    delete normalizedChanges.cobroId;

    const current = (data.ingresos || []).find(row => String(row.id) === String(id));
    const cobroForAttribution = (data.cobros || []).find(cobro => String(cobro.id) === String(normalizedChanges.cobro_id || current?.cobro_id || ''));
    const clienteForAttribution = (data.clientes || []).find(cliente => String(cliente.id) === String(normalizedChanges.cliente_id || current?.cliente_id || cobroForAttribution?.cliente_id || cobroForAttribution?.clienteId || ''));
    Object.assign(
      normalizedChanges,
      mergeMissingMarketingAttribution(
        { ...(current || {}), ...normalizedChanges },
        inheritedMarketingAttribution({ cobro: cobroForAttribution, cliente: clienteForAttribution }),
      ),
    );
    const localPatch = calculateIngresoLocally({ ...(current || {}), ...normalizedChanges });

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, ingresos: (d.ingresos || []).map(row => String(row.id) === String(id) ? localPatch : row) }));
      return localPatch;
    }

    const payload = filterIngresoPayload(normalizedChanges);
    const { data: updated, error } = await supabase.from('ingresos').update(payload).eq('id', id).select().single();
    if (error) {
      console.error('[AppContext.updateIngreso] No se pudo actualizar ingreso:', { error, id, payload });
      throw error;
    }

    const saved = updated || localPatch;
    setData(d => ({ ...d, ingresos: (d.ingresos || []).map(row => String(row.id) === String(id) ? saved : row) }));
    return saved;
  }, [data.cobros, data.clientes, data.ingresos, demoMode]);

  const deleteIngreso = useCallback(async (id) => {
    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, ingresos: (d.ingresos || []).filter(row => String(row.id) !== String(id)) }));
      return;
    }

    const { error } = await supabase.from('ingresos').delete().eq('id', id);
    if (error) {
      console.error('[AppContext.deleteIngreso] No se pudo eliminar ingreso:', { error, id });
      throw error;
    }

    setData(d => ({ ...d, ingresos: (d.ingresos || []).filter(row => String(row.id) !== String(id)) }));
  }, [demoMode]);

  const addSuscripcion = useCallback(async (suscripcion = {}) => {
    const now = new Date().toISOString();
    const row = calculateSuscripcionLocally({
      ...suscripcion,
      id: suscripcion.id || crypto.randomUUID?.() || Date.now().toString(),
      created_at: suscripcion.created_at || now,
      updated_at: now,
      created_by: suscripcion.created_by || user?.id || null,
    });

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, suscripciones: [row, ...(d.suscripciones || [])] }));
      await syncSubscriptionExpense(row, 'upsert');
      return row;
    }

    const payload = filterSuscripcionPayload(row);
    const { data: inserted, error } = await supabase.from('suscripciones').insert([payload]).select().single();
    if (error) {
      console.error('[AppContext.addSuscripcion] No se pudo guardar suscripcion:', { error, payload });
      throw error;
    }

    const saved = inserted || row;
    setData(d => ({ ...d, suscripciones: [saved, ...(d.suscripciones || [])] }));
    await syncSubscriptionExpense(saved, 'upsert');
    return saved;
  }, [demoMode, syncSubscriptionExpense, user?.id]);

  const updateSuscripcion = useCallback(async (id, changes = {}) => {
    const current = (data.suscripciones || []).find(row => String(row.id) === String(id));
    const normalizedChanges = calculateSuscripcionLocally({
      ...(current || {}),
      ...changes,
      updated_at: new Date().toISOString(),
    });

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, suscripciones: (d.suscripciones || []).map(row => String(row.id) === String(id) ? normalizedChanges : row) }));
      await syncSubscriptionExpense(normalizedChanges, 'upsert');
      return normalizedChanges;
    }

    const payload = filterSuscripcionPayload(normalizedChanges);
    delete payload.id;
    delete payload.created_at;
    delete payload.created_by;

    const { data: updated, error } = await supabase.from('suscripciones').update(payload).eq('id', id).select().single();
    if (error) {
      console.error('[AppContext.updateSuscripcion] No se pudo actualizar suscripcion:', { error, id, payload });
      throw error;
    }

    const saved = updated || normalizedChanges;
    setData(d => ({ ...d, suscripciones: (d.suscripciones || []).map(row => String(row.id) === String(id) ? saved : row) }));
    await syncSubscriptionExpense(saved, 'upsert');
    return saved;
  }, [data.suscripciones, demoMode, syncSubscriptionExpense]);

  const deleteSuscripcion = useCallback(async (id) => {
    const current = (data.suscripciones || []).find(row => String(row.id) === String(id)) || { id };
    if (!isConfigured || USE_SEED || demoMode) {
      await syncSubscriptionExpense(current, 'delete');
      setData(d => ({ ...d, suscripciones: (d.suscripciones || []).filter(row => String(row.id) !== String(id)) }));
      return;
    }

    await syncSubscriptionExpense(current, 'delete');
    const { error } = await supabase.from('suscripciones').delete().eq('id', id);
    if (error) {
      console.error('[AppContext.deleteSuscripcion] No se pudo eliminar suscripcion:', { error, id });
      throw error;
    }

    setData(d => ({ ...d, suscripciones: (d.suscripciones || []).filter(row => String(row.id) !== String(id)) }));
  }, [data.suscripciones, demoMode, syncSubscriptionExpense]);

  const syncFixedExpenseSource = useCallback(async (gastoFijo = {}, action = 'update') => {
    const ref = getFinanceSyncRef(gastoFijo);
    if (!ref?.id) return;

    if (ref.type === 'payroll') {
      if (action === 'delete') {
        if (isConfigured && !USE_SEED && !demoMode) {
          const { error } = await supabase.from('sueldos_programados').delete().eq('id', ref.id);
          if (error) {
            console.error('[AppContext.syncFixedExpenseSource] No se pudo eliminar sueldo vinculado:', { error, ref });
            throw error;
          }
        }
        setData(d => ({ ...d, sueldosProgramados: (d.sueldosProgramados || []).filter(row => String(row.id) !== String(ref.id)) }));
        return;
      }

      const payload = {
        valor: normalizeMoney(gastoFijo.monto_mensual, 0),
        cargo: gastoFijo.categoria || undefined,
        updated_at: new Date().toISOString(),
      };
      if (isConfigured && !USE_SEED && !demoMode) {
        const { error } = await supabase.from('sueldos_programados').update(pickSueldoProgramadoDbPayload(payload)).eq('id', ref.id);
        if (error) {
          console.error('[AppContext.syncFixedExpenseSource] No se pudo actualizar sueldo vinculado:', { error, ref, payload });
          throw error;
        }
      }
      setData(d => ({
        ...d,
        sueldosProgramados: (d.sueldosProgramados || []).map(row => String(row.id) === String(ref.id) ? { ...row, ...payload } : row),
      }));
      return;
    }

    if (ref.type === 'debt') {
      if (action === 'delete') {
        if (isConfigured && !USE_SEED && !demoMode) {
          const { error } = await supabase.from('deudas').delete().eq('id', ref.id);
          if (error) {
            console.error('[AppContext.syncFixedExpenseSource] No se pudo eliminar deuda vinculada:', { error, ref });
            throw error;
          }
        }
        setData(d => ({ ...d, deudas: (d.deudas || []).filter(row => String(row.id) !== String(ref.id)) }));
        return;
      }

      const amount = normalizeMoney(gastoFijo.monto_mensual, 0);
      const payload = {
        cuota_mensual: amount,
        cuota: amount,
        updated_at: new Date().toISOString(),
      };
      if (isConfigured && !USE_SEED && !demoMode) {
        const { error } = await supabase.from('deudas').update(pickDeudaDbPayload(payload)).eq('id', ref.id);
        if (error) {
          console.error('[AppContext.syncFixedExpenseSource] No se pudo actualizar deuda vinculada:', { error, ref, payload });
          throw error;
        }
      }
      setData(d => ({
        ...d,
        deudas: (d.deudas || []).map(row => String(row.id) === String(ref.id) ? { ...row, ...payload } : row),
      }));
    }
  }, [demoMode]);

  const addGastoFijo = useCallback(async (gastoFijo = {}) => {
    const now = new Date().toISOString();
    const row = calculateGastoFijoLocally({
      ...gastoFijo,
      id: gastoFijo.id || crypto.randomUUID?.() || Date.now().toString(),
      created_at: gastoFijo.created_at || now,
      updated_at: now,
      created_by: gastoFijo.created_by || user?.id || null,
    });

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, gastosFijos: [row, ...(d.gastosFijos || [])] }));
      return row;
    }

    const payload = filterGastoFijoPayload(row);
    const { data: inserted, error } = await supabase.from('gastos_fijos').insert([payload]).select().single();
    if (error) {
      console.error('[AppContext.addGastoFijo] No se pudo guardar gasto fijo:', { error, payload });
      throw error;
    }

    const saved = inserted || row;
    setData(d => ({ ...d, gastosFijos: [saved, ...(d.gastosFijos || [])] }));
    return saved;
  }, [demoMode, user?.id]);

  const updateGastoFijo = useCallback(async (id, changes = {}, options = {}) => {
    const current = (data.gastosFijos || []).find(row => String(row.id) === String(id));
    const normalizedChanges = calculateGastoFijoLocally({
      ...(current || {}),
      ...changes,
      updated_at: new Date().toISOString(),
    });

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, gastosFijos: (d.gastosFijos || []).map(row => String(row.id) === String(id) ? normalizedChanges : row) }));
      if (!options.skipSourceSync) await syncFixedExpenseSource(normalizedChanges, 'update');
      return normalizedChanges;
    }

    const payload = filterGastoFijoPayload(normalizedChanges);
    delete payload.id;
    delete payload.created_at;
    delete payload.created_by;

    const { data: updated, error } = await supabase.from('gastos_fijos').update(payload).eq('id', id).select().single();
    if (error) {
      console.error('[AppContext.updateGastoFijo] No se pudo actualizar gasto fijo:', { error, id, payload });
      throw error;
    }

    const saved = updated || normalizedChanges;
    setData(d => ({ ...d, gastosFijos: (d.gastosFijos || []).map(row => String(row.id) === String(id) ? saved : row) }));
    if (!options.skipSourceSync) await syncFixedExpenseSource(saved, 'update');
    return saved;
  }, [data.gastosFijos, demoMode, syncFixedExpenseSource]);

  const deleteGastoFijo = useCallback(async (id, options = {}) => {
    const current = (data.gastosFijos || []).find(row => String(row.id) === String(id));
    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, gastosFijos: (d.gastosFijos || []).filter(row => String(row.id) !== String(id)) }));
      if (!options.skipSourceSync && current) await syncFixedExpenseSource(current, 'delete');
      return;
    }

    const { error } = await supabase.from('gastos_fijos').delete().eq('id', id);
    if (error) {
      console.error('[AppContext.deleteGastoFijo] No se pudo eliminar gasto fijo:', { error, id });
      throw error;
    }

    setData(d => ({ ...d, gastosFijos: (d.gastosFijos || []).filter(row => String(row.id) !== String(id)) }));
    if (!options.skipSourceSync && current) await syncFixedExpenseSource(current, 'delete');
  }, [data.gastosFijos, demoMode, syncFixedExpenseSource]);

  const syncPayrollFixedExpense = useCallback(async (sueldo = {}) => {
    if (!sueldo?.id) return null;
    const existing = findPayrollFixedExpense(data.gastosFijos || [], sueldo);
    const amount = payrollMonthlyValue(sueldo);

    if (amount <= 0 || sueldo.estado === 'inactivo') {
      if (existing?.id) await deleteGastoFijo(existing.id, { skipSourceSync: true });
      return null;
    }

    const payload = buildPayrollFixedExpensePayload(sueldo, existing || {});
    if (existing?.id) return updateGastoFijo(existing.id, payload, { skipSourceSync: true });
    return addGastoFijo(payload);
  }, [addGastoFijo, data.gastosFijos, deleteGastoFijo, updateGastoFijo]);

  const deletePayrollFixedExpense = useCallback(async (sueldo = {}) => {
    const existing = findPayrollFixedExpense(data.gastosFijos || [], sueldo);
    if (existing?.id) await deleteGastoFijo(existing.id, { skipSourceSync: true });
  }, [data.gastosFijos, deleteGastoFijo]);

  const removeLegacyDebtExpenses = useCallback(async () => {
    const legacyRows = (data.gastos || []).filter(isLegacyDebtExpenseRow);
    if (!legacyRows.length) return { removed: 0 };

    const ids = legacyRows.map(row => row.id).filter(Boolean);
    if (ids.length && isConfigured && !USE_SEED && !demoMode) {
      const { error } = await supabase.from('gastos').delete().in('id', ids);
      if (error) {
        console.error('[AppContext.removeLegacyDebtExpenses] No se pudo limpiar prestamos legacy en gastos:', { error, ids });
        throw error;
      }
    }

    setData(d => ({ ...d, gastos: (d.gastos || []).filter(row => !ids.includes(row.id) && !isLegacyDebtExpenseRow(row)) }));
    return { removed: legacyRows.length };
  }, [data.gastos, demoMode]);

  const syncDebtFixedExpense = useCallback(async (deuda = {}) => {
    if (!deuda?.id) return null;
    const existing = findDebtFixedExpense(data.gastosFijos || [], deuda);
    const amount = debtMonthlyValue(deuda);

    if (amount <= 0 || isDebtSettled(deuda)) {
      if (existing?.id) await deleteGastoFijo(existing.id, { skipSourceSync: true });
      await removeLegacyDebtExpenses();
      return null;
    }

    const payload = buildDebtFixedExpensePayload(deuda, existing || {});
    const saved = existing?.id
      ? await updateGastoFijo(existing.id, payload, { skipSourceSync: true })
      : await addGastoFijo(payload);
    await removeLegacyDebtExpenses();
    return saved;
  }, [addGastoFijo, data.gastosFijos, deleteGastoFijo, removeLegacyDebtExpenses, updateGastoFijo]);

  const deleteDebtFixedExpense = useCallback(async (deuda = {}) => {
    const existing = findDebtFixedExpense(data.gastosFijos || [], deuda);
    if (existing?.id) await deleteGastoFijo(existing.id, { skipSourceSync: true });
    await removeLegacyDebtExpenses();
  }, [data.gastosFijos, deleteGastoFijo, removeLegacyDebtExpenses]);

  const saveFlujoConfig = useCallback(async (config = {}) => {
    const now = new Date().toISOString();
    const existing = config.id
      ? (data.flujoConfig || []).find(row => String(row.id) === String(config.id))
      : (data.flujoConfig || []).find(row => row.activo !== false) || (data.flujoConfig || [])[0];
    const row = calculateFlujoConfigLocally({
      ...(existing || {}),
      ...config,
      id: config.id || existing?.id || crypto.randomUUID?.() || Date.now().toString(),
      created_at: config.created_at || existing?.created_at || now,
      updated_at: now,
      created_by: config.created_by || existing?.created_by || user?.id || null,
      activo: config.activo !== undefined ? config.activo : true,
    });

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => {
        const currentRows = d.flujoConfig || [];
        const exists = currentRows.some(item => String(item.id) === String(row.id));
        return {
          ...d,
          flujoConfig: exists
            ? currentRows.map(item => String(item.id) === String(row.id) ? row : item)
            : [row, ...currentRows],
        };
      });
      return row;
    }

    const payload = filterFlujoConfigPayload(row);
    const updatePayload = { ...payload };
    delete updatePayload.id;
    delete updatePayload.created_at;
    delete updatePayload.created_by;
    const { data: savedRow, error } = existing?.id
      ? await supabase.from('flujo_config').update(updatePayload).eq('id', existing.id).select().single()
      : await supabase.from('flujo_config').insert([payload]).select().single();

    if (error) {
      console.error('[AppContext.saveFlujoConfig] No se pudo guardar configuracion de flujo:', { error, payload });
      throw error;
    }

    const saved = savedRow || row;
    setData(d => {
      const currentRows = d.flujoConfig || [];
      const exists = currentRows.some(item => String(item.id) === String(saved.id));
      return {
        ...d,
        flujoConfig: exists
          ? currentRows.map(item => String(item.id) === String(saved.id) ? saved : item)
          : [saved, ...currentRows],
      };
    });
    return saved;
  }, [data.flujoConfig, demoMode, user?.id]);

  const addContractParticipant = useCallback(async (participant = {}) => {
    const now = new Date().toISOString();
    const email = String(participant.email || '').trim().toLowerCase();
    const row = filterContractParticipantPayload({
      ...participant,
      email: email || null,
      name: String(participant.name || '').trim(),
      role: participant.role || 'co_signer',
      actor_type: participant.actor_type || 'portal_member',
      required: participant.required !== false,
      sign_order: Number(participant.sign_order || participant.signOrder || 1),
      status: participant.status || 'pending',
      created_at: participant.created_at || now,
      updated_at: now,
    });
    if (!row.contrato_id || !row.cliente_id || !row.name || !row.email) {
      throw new Error('Nombre, email, contrato y cliente son requeridos para agregar firmante.');
    }
    if (row.role === 'owner') {
      throw new Error('El titular se crea automaticamente al preparar el contrato.');
    }
    const duplicate = (data.contractParticipants || []).find(item =>
      String(item.contrato_id) === String(row.contrato_id)
      && String(item.status || 'pending') !== 'void'
      && String(item.email || '').trim().toLowerCase() === email
    );
    if (duplicate?.id) throw new Error('Ya existe un firmante activo con ese email para este contrato.');

    if (!isConfigured || USE_SEED || demoMode || participant.is_demo) {
      const local = { ...row, id: row.id || crypto.randomUUID?.() || `${Date.now()}-contract-participant` };
      setData(d => ({ ...d, contractParticipants: upsertRowById(d.contractParticipants, local, { position: 'end' }) }));
      return local;
    }

    const { data: inserted, error } = await supabase.from('contract_participants').insert([row]).select().single();
    if (error) throw error;
    setData(d => ({ ...d, contractParticipants: upsertRowById(d.contractParticipants, inserted, { position: 'end' }) }));
    return inserted;
  }, [data.contractParticipants, demoMode]);

  const updateContractParticipant = useCallback(async (id, changes = {}) => {
    const existing = (data.contractParticipants || []).find(item => String(item.id) === String(id));
    if (!existing?.id) throw new Error('No se encontro el firmante.');
    const hasSignature = (data.contractSignatures || []).some(signature => String(signature.contract_participant_id) === String(id));
    const existingStatus = String(existing.status || '').toLowerCase();
    if (existing.role === 'owner') throw new Error('El titular no se edita desde gestion de participantes.');
    if (existingStatus === 'signed' || hasSignature) throw new Error('Este firmante ya firmo y no puede editarse desde CRM.');

    const nextEmail = changes.email !== undefined ? String(changes.email || '').trim().toLowerCase() : String(existing.email || '').trim().toLowerCase();
    if (nextEmail) {
      const duplicate = (data.contractParticipants || []).find(item =>
        String(item.id) !== String(id)
        && String(item.contrato_id) === String(existing.contrato_id)
        && String(item.status || 'pending') !== 'void'
        && String(item.email || '').trim().toLowerCase() === nextEmail
      );
      if (duplicate?.id) throw new Error('Ya existe otro firmante activo con ese email.');
    }

    const payload = filterContractParticipantPayload({
      ...changes,
      email: changes.email !== undefined ? nextEmail || null : undefined,
      name: changes.name !== undefined ? String(changes.name || '').trim() : undefined,
      sign_order: changes.sign_order !== undefined || changes.signOrder !== undefined ? Number(changes.sign_order || changes.signOrder || existing.sign_order || 1) : undefined,
      updated_at: new Date().toISOString(),
    });
    delete payload.role;
    delete payload.contrato_id;
    delete payload.cliente_id;
    if (payload.status && !['pending', 'void'].includes(String(payload.status))) delete payload.status;

    const local = { ...existing, ...payload };
    if (!isConfigured || USE_SEED || demoMode || existing.is_demo) {
      setData(d => ({ ...d, contractParticipants: upsertRowById(d.contractParticipants, local) }));
      return local;
    }
    const { data: updated, error } = await supabase.from('contract_participants').update(payload).eq('id', id).select().single();
    if (error) throw error;
    setData(d => ({ ...d, contractParticipants: upsertRowById(d.contractParticipants, updated) }));
    return updated;
  }, [data.contractParticipants, data.contractSignatures, demoMode]);

  const addBriefParticipation = useCallback(async (participant = {}) => {
    const now = new Date().toISOString();
    const email = String(participant.email || '').trim().toLowerCase();
    const role = participant.role || 'guest';
    const row = filterBriefParticipationPayload({
      ...participant,
      email: email || null,
      name: String(participant.name || '').trim(),
      role,
      actor_type: participant.actor_type || (role === 'owner' ? 'owner' : 'portal_member'),
      required: participant.required === true,
      status: participant.status || 'pending',
      created_at: participant.created_at || now,
      updated_at: now,
    });
    if (!row.cliente_id || !row.role) throw new Error('Cliente y rol son requeridos para agregar participante de brief.');
    if (row.role !== 'owner' && !row.portal_member_id && (!row.name || !row.email)) {
      throw new Error('Nombre y email son requeridos para participantes de brief sin acceso asociado.');
    }
    const duplicate = email && (data.briefParticipation || []).find(item =>
      String(item.cliente_id) === String(row.cliente_id)
      && String(item.status || 'pending') !== 'void'
      && String(item.email || '').trim().toLowerCase() === email
    );
    if (duplicate?.id) throw new Error('Ya existe un participante activo de brief con ese email.');

    if (!isConfigured || USE_SEED || demoMode || participant.is_demo) {
      const local = { ...row, id: row.id || crypto.randomUUID?.() || `${Date.now()}-brief-participant` };
      setData(d => ({ ...d, briefParticipation: upsertRowById(d.briefParticipation, local, { position: 'end' }) }));
      return local;
    }
    const { data: inserted, error } = await supabase.from('brief_participation').insert([row]).select().single();
    if (error) throw error;
    setData(d => ({ ...d, briefParticipation: upsertRowById(d.briefParticipation, inserted, { position: 'end' }) }));
    return inserted;
  }, [data.briefParticipation, demoMode]);

  const updateBriefParticipation = useCallback(async (id, changes = {}) => {
    const existing = (data.briefParticipation || []).find(item => String(item.id) === String(id));
    if (!existing?.id) throw new Error('No se encontro el participante de brief.');
    const existingStatus = String(existing.status || '').toLowerCase();
    if (existingStatus === 'completed') throw new Error('Este participante ya completo su parte del brief y no puede editarse desde CRM.');

    const nextEmail = changes.email !== undefined ? String(changes.email || '').trim().toLowerCase() : String(existing.email || '').trim().toLowerCase();
    if (nextEmail) {
      const duplicate = (data.briefParticipation || []).find(item =>
        String(item.id) !== String(id)
        && String(item.cliente_id) === String(existing.cliente_id)
        && String(item.status || 'pending') !== 'void'
        && String(item.email || '').trim().toLowerCase() === nextEmail
      );
      if (duplicate?.id) throw new Error('Ya existe otro participante activo de brief con ese email.');
    }

    const payload = filterBriefParticipationPayload({
      ...changes,
      email: changes.email !== undefined ? nextEmail || null : undefined,
      name: changes.name !== undefined ? String(changes.name || '').trim() : undefined,
      updated_at: new Date().toISOString(),
    });
    delete payload.cliente_id;
    if (payload.status && !['pending', 'void'].includes(String(payload.status))) delete payload.status;

    const local = { ...existing, ...payload };
    if (!isConfigured || USE_SEED || demoMode || existing.is_demo) {
      setData(d => ({ ...d, briefParticipation: upsertRowById(d.briefParticipation, local) }));
      return local;
    }
    const { data: updated, error } = await supabase.from('brief_participation').update(payload).eq('id', id).select().single();
    if (error) throw error;
    setData(d => ({ ...d, briefParticipation: upsertRowById(d.briefParticipation, updated) }));
    return updated;
  }, [data.briefParticipation, demoMode]);

  const sendParticipantAccess = useCallback(async ({
    clienteId,
    participantType,
    contractParticipantId = null,
    briefParticipationId = null,
    name = '',
    email = '',
  } = {}) => {
    if (!clienteId) throw new Error('Selecciona un cliente para enviar acceso.');
    if (!participantType) throw new Error('Selecciona el tipo de participante.');
    if (!isConfigured || USE_SEED || demoMode) {
      throw new Error('El envio de accesos requiere Supabase configurado.');
    }

    const { data: result, error } = await supabase.functions.invoke('send-participant-access', {
      body: {
        cliente_id: clienteId,
        participant_type: participantType,
        contract_participant_id: contractParticipantId,
        brief_participation_id: briefParticipationId,
        name,
        email,
      },
    });

    if (error || result?.ok === false) {
      let detail = null;
      try {
        if (error?.context?.json) detail = await error.context.json();
      } catch (_) {}
      throw new Error(detail?.error || result?.error || error?.message || 'No se pudo enviar el acceso del participante.');
    }

    setData(d => {
      let next = { ...d };
      if (result.member?.id) {
        next.clientPortalMembers = upsertRowById(next.clientPortalMembers || [], result.member);
      }
      if (result.contract_participant?.id) {
        next.contractParticipants = upsertRowById(next.contractParticipants || [], result.contract_participant);
      }
      if (Array.isArray(result.brief_participation)) {
        result.brief_participation.forEach(row => {
          if (row?.id) next.briefParticipation = upsertRowById(next.briefParticipation || [], row);
        });
      }
      if (result.communication_event?.id) {
        next.communicationEvents = upsertRowById(next.communicationEvents || [], result.communication_event, { position: 'start' });
      }
      return next;
    });

    return result;
  }, [demoMode]);

  const setupPortalParticipants = useCallback(async ({
    clienteId,
    contractId = null,
    contractMode = '',
    contractParticipants = [],
    briefMode = '',
    briefParticipants = [],
  } = {}) => {
    if (!clienteId) throw new Error('Selecciona un cliente para configurar participantes.');
    if (!contractMode) throw new Error('Selecciona quienes firmaran el contrato.');
    if (!briefMode) throw new Error('Selecciona quienes participaran en el brief.');
    if (!isConfigured || USE_SEED || demoMode) {
      throw new Error('La configuracion automatica de participantes requiere Supabase configurado.');
    }

    const { data: result, error } = await supabase.functions.invoke('setup-portal-participants', {
      body: {
        cliente_id: clienteId,
        contract_id: contractId,
        contract_mode: contractMode,
        contract_participants: contractParticipants,
        brief_mode: briefMode,
        brief_participants: briefParticipants,
      },
    });

    if (error || result?.ok === false) {
      let detail = null;
      try {
        if (error?.context?.json) detail = await error.context.json();
      } catch (_) {}
      throw new Error(detail?.error || result?.error || error?.message || 'No se pudo configurar participantes desde el portal.');
    }

    setData(d => {
      let next = { ...d };
      if (result.contract?.id) {
        next.contratos = upsertRowById(next.contratos || [], { ...result.contract, clienteId: result.contract.cliente_id || result.contract.clienteId });
      }
      if (Array.isArray(result.contract_participants)) {
        result.contract_participants.forEach(row => {
          if (row?.id) next.contractParticipants = upsertRowById(next.contractParticipants || [], row);
        });
      }
      if (Array.isArray(result.brief_participation)) {
        result.brief_participation.forEach(row => {
          if (row?.id) next.briefParticipation = upsertRowById(next.briefParticipation || [], row);
        });
      }
      if (Array.isArray(result.client_portal_members)) {
        result.client_portal_members.forEach(row => {
          if (row?.id) next.clientPortalMembers = upsertRowById(next.clientPortalMembers || [], row);
        });
      }
      if (Array.isArray(result.communication_events)) {
        result.communication_events.forEach(row => {
          if (row?.id) next.communicationEvents = upsertRowById(next.communicationEvents || [], row, { position: 'start' });
        });
      }
      return next;
    });

    return result;
  }, [demoMode]);

  const prepareContractForPortal = useCallback(async (clienteId, contractData = {}) => {
    const now = new Date().toISOString();
    const cliente = (data.clientes || []).find(c => String(c.id) === String(clienteId)) || {};
    const isDemoFlow = Boolean(demoMode || cliente.is_demo || contractData.is_demo);
    const status = 'listo_para_firma';
    const preferredName = contractData.nombre_preferido || cliente.nombre_preferido || cliente.nombre || contractData.nombre || 'Cliente';
    const contractPackage = contractData.paquete || cliente.servicio || cliente.paquete || '';
    const contractAmount = resolveContractAmount(contractData, cliente);
    // `!(x > 0)` y no `x <= 0`: cubre null, undefined y NaN sin depender de la coercion.
    if (!(contractAmount > 0)) {
      throw new Error('Selecciona un paquete o define un monto válido antes de enviar el contrato.');
    }
    const acceptedQuotes = (data.cotizacionesEnviadas || []).filter(quote =>
      String(quote.cliente_id || quote.clienteId || '') === String(clienteId) &&
      String(quote.quote_status || quote.status || '').toLowerCase() === 'accepted'
    );
    const canonicalQuoteId = contractData.quote_id || contractData.public_quote_id || (acceptedQuotes.length === 1 ? acceptedQuotes[0].id : null);
    const legalData = {
      nombre: contractData.nombre || cliente.nombre || '',
      nombre_preferido: preferredName,
      email: contractData.email || cliente.email || '',
      telefono: contractData.telefono || cliente.telefono || '',
      whatsapp: contractData.whatsapp || cliente.whatsapp || cliente.telefono || '',
      cedula: contractData.cedula || cliente.cedula || '',
      ruc: contractData.ruc || cliente.ruc || '',
      razon_social: contractData.razon_social || contractData.razonSocial || cliente.razon_social || cliente.razonSocial || '',
      direccion_facturacion: contractData.direccion_facturacion || contractData.direccionFacturacion || cliente.direccion_facturacion || cliente.direccionFacturacion || '',
      pais: contractData.pais || cliente.pais || '',
      metodo_pago: contractData.metodo_pago || contractData.metodoPago || cliente.metodo_pago || cliente.metodoPago || '',
      paquete: contractPackage,
      monto: contractAmount,
      commercial_adjustment_reason: contractData.commercial_adjustment_reason || '',
      commercial_adjustment_at: contractData.commercial_adjustment_at || '',
      require_identity_document: Boolean(contractData.require_identity_document || contractData.requireIdentityDocument),
    };
    const row = pickContratoDbPayload({
      cliente_id: clienteId,
      contenido: contractData.contenido || '',
      status,
      legal_data: legalData,
      service_type: contractData.service_type || cliente.service_type || contractPackage || cliente.servicio || '',
      require_identity_document: Boolean(contractData.require_identity_document || contractData.requireIdentityDocument),
      estudio_firmado: true,
      quote_id: canonicalQuoteId || null,
      proyecto_id: contractData.proyecto_id || contractData.proyectoId || null,
      is_demo: isDemoFlow,
      ready_for_signature_at: now,
      portal_access_requested_at: now,
      updated_at: now,
    });
    if (!canonicalQuoteId) delete row.quote_id;
    if (!(contractData.proyecto_id || contractData.proyectoId)) delete row.proyecto_id;

    if (!isConfigured || USE_SEED || isDemoFlow) {
      const demoNotification = {
        id: crypto.randomUUID?.() || `${Date.now()}-contract-ready`,
        tipo: 'contrato_listo',
        titulo: 'Contrato listo y acceso preparado',
        desc: `${preferredName} tiene contrato listo para firma. Modo demo: no se envio email real ni se toco Supabase.`,
        descripcion: `${preferredName} tiene contrato listo para firma. Modo demo: no se envio email real ni se toco Supabase.`,
        cliente_id: clienteId,
        leida: false,
        is_demo: true,
        created_at: now,
      };
      await advanceClientPhase({
        clienteId,
        targetStage: 2,
        source: 'prepare_contract_demo',
        metadata: { contract_status: status, quote_id: canonicalQuoteId },
        relatedEntityType: 'contrato',
        relatedEntityId: row.id || null,
        allowCompound: true,
        silent: true,
      });
      setData(d => ({
        ...d,
        clientes: d.clientes.map(c => String(c.id) === String(clienteId)
          ? { ...c, contract_status: status, monto: contractAmount, servicio: contractPackage || c.servicio, service_type: contractData.service_type || c.service_type || contractPackage || c.servicio, portal_access_status: 'demo_preparado', updated_at: now }
          : c),
        contratos: [{ ...row, clienteId }, ...(d.contratos || []).filter(c => String(c.cliente_id || c.clienteId) !== String(clienteId) || !['listo_para_firma', 'enviado', 'pendiente_firma'].includes(String(c.status || '').toLowerCase()))],
        notificaciones: [demoNotification, ...(d.notificaciones || [])],
      }));
      return { ok: true, demo: true, contract: row, portal_access: { ok: true, status: 'demo_preparado', email_sent: false, whatsapp_sent: false } };
    }

    if (!clienteId) throw new Error('Cliente requerido para preparar contrato.');
    if (!row.contenido.trim()) throw new Error('El contrato no tiene contenido para guardar.');

    const { data: existingReadyContracts, error: contractLookupError } = await supabase
      .from('contratos')
      .select('id,status,created_at')
      .eq('cliente_id', clienteId)
      .in('status', ['listo_para_firma', 'enviado', 'pendiente_firma'])
      .order('created_at', { ascending: false })
      .limit(1);
    if (contractLookupError) {
      console.error('[AppContext.prepareContractForPortal] No se pudo buscar contrato listo existente:', contractLookupError.message || contractLookupError);
      throw contractLookupError;
    }

    const existingReadyContract = existingReadyContracts?.[0];
    const contractRequest = existingReadyContract?.id
      ? supabase.from('contratos').update(row).eq('id', existingReadyContract.id).select().single()
      : supabase.from('contratos').insert([row]).select().single();

    const { data: savedContract, error: contractError } = await contractRequest;
    if (contractError) {
      console.error('[AppContext.prepareContractForPortal] Supabase rechazo guardar contrato listo:', contractError.message || contractError);
      throw contractError;
    }

    const phaseResult = await advanceClientPhase({
      clienteId,
      targetStage: 2,
      source: 'prepare_contract_for_portal',
      metadata: { contract_id: savedContract?.id || null, quote_id: canonicalQuoteId },
      relatedEntityType: 'contrato',
      relatedEntityId: savedContract?.id || null,
      allowCompound: true,
    });
    if (!phaseResult.ok) throw new Error(phaseResult.error || 'No se pudo avanzar el CRM a contrato preparado.');

    const clienteResultado = await updatePorId(
      supabase,
      'clientes',
      clienteId,
      {
        servicio: contractPackage || cliente.servicio || null,
        service_type: contractData.service_type || cliente.service_type || contractPackage || cliente.servicio || null,
        monto: contractAmount,
        updated_at: now,
      },
      { queSeGuardaba: 'los datos del cliente' },
    );
    const clienteError = clienteResultado.ok ? null : clienteResultado.error;
    if (clienteError) {
      console.error('[AppContext.prepareContractForPortal] Contrato listo guardado, pero fallo actualizar cliente:', clienteError.message || clienteError);
      throw clienteError;
    }

    const { data: accessData, error: accessError } = await supabase.functions.invoke('provision-client-access', {
      body: {
        cliente_id: clienteId,
        source: 'contract_ready',
        allow_contract_ready: true,
      },
    });
    if (accessError) {
      console.error('[AppContext.prepareContractForPortal] No se pudo activar acceso portal-first:', accessError);
      throw accessError;
    }

    const portalAccess = accessData?.portal_access || accessData;
    if (!portalAccess?.ok) {
      console.error('[AppContext.prepareContractForPortal] Acceso portal no quedo activo:', {
        status: portalAccess?.status,
        message: portalAccess?.message || accessData?.message,
      });
      throw new Error(portalAccess?.message || accessData?.message || 'No se pudo activar el acceso al portal.');
    }

    const emailStatus = portalAccess.email_sent ? 'Email enviado' : 'Email pendiente';
    const whatsappStatus = portalAccess.whatsapp_sent ? 'WhatsApp enviado' : 'WhatsApp pendiente';
    const notification = {
      tipo: 'contrato_listo',
      titulo: 'Contrato listo y acceso enviado al cliente',
      descripcion: `${preferredName} tiene contrato listo para firma. ${emailStatus} · ${whatsappStatus}.`,
      desc: `${preferredName} tiene contrato listo para firma. ${emailStatus} · ${whatsappStatus}.`,
      cliente_id: clienteId,
      leida: false,
      created_at: now,
    };
    await addNotification(notification);

    setData(d => ({
      ...d,
      clientes: d.clientes.map(c => String(c.id) === String(clienteId)
        ? {
            ...c,
            contract_status: status,
            monto: contractAmount,
            servicio: contractPackage || c.servicio,
            service_type: contractData.service_type || c.service_type || contractPackage || c.servicio,
            portal_access_status: 'activo',
            portal_user_id: portalAccess.user_id || c.portal_user_id,
            portal_access_created_at: now,
            portal_access_sent_at: portalAccess.email_sent ? now : c.portal_access_sent_at,
            portal_access_whatsapp_sent_at: portalAccess.whatsapp_sent ? now : c.portal_access_whatsapp_sent_at,
            updated_at: now,
          }
        : c),
      contratos: [
        { ...(savedContract || row), clienteId: (savedContract || row).cliente_id || clienteId },
        ...(d.contratos || []).filter(c => String(c.id) !== String(savedContract?.id) && (String(c.cliente_id || c.clienteId) !== String(clienteId) || !['listo_para_firma', 'enviado', 'pendiente_firma'].includes(String(c.status || '').toLowerCase()))),
      ],
    }));

    return { ok: true, contract: savedContract || row, portal_access: portalAccess };
  }, [addNotification, advanceClientPhase, data.clientes, data.cotizacionesEnviadas, demoMode]);

  const firmarContrato = useCallback(async (clienteId, contratoData = {}) => {
    // Declarados afuera del bloque de la Edge: el retorno de exito los incluye siempre,
    // y en el camino local (demo/sin Supabase) quedan en su valor neutro.
    let crmPendienteTrasFirma = false;
    let cobroPendienteTrasFirma = null;
    if (isStaleContractLegalContent(contratoData.contenido || '')) {
      throw new Error(CONTRACT_STALE_ADMIN_MESSAGE);
    }
    let hash = contratoData.hash || '';
    if (!hash || hash.length < 40) {
      const payload = `${clienteId}|${contratoData.firmante || ''}|${new Date().toISOString()}|${(contratoData.contenido || '').slice(0, 500)}`;
      const buf     = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(payload));
      hash          = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
    }

    const ahora = new Date().toISOString();
    const cliente = data.clientes.find(c => String(c.id) === String(clienteId)) || {};
    const montoTotal = resolveContractAmount(contratoData, cliente);
    if (!(montoTotal > 0)) {
      throw new Error('El estudio debe completar el valor del contrato antes de generar el cobro inicial.');
    }
    // ── EL ANTICIPO ACEPTADO MANDA SOBRE EL 60% CALCULADO ────────────────────
    //
    // `montoTotal` ya sale de la instantánea aceptada, pero el 60% se decidía aquí,
    // en el navegador. El porcentaje de anticipo es una POLÍTICA que se congela al
    // aceptar (`final_advance_amount`), y no todas las ventas son 60/40 —Quick cobra
    // total—. Recalcularlo aquí era una segunda autoridad del reparto.
    //
    // El cálculo sobrevive SOLO para contratos firmados antes de que la instantánea
    // llevara el campo: sin él no hay a qué preguntarle, y romper esas firmas sería
    // peor que el defecto.
    const anticipoAceptado = normalizeMoney(
      contratoData?.quote_snapshot?.final_advance_amount ?? contratoData?.quote_snapshot?.advance,
      0,
    );
    const monto60 = anticipoAceptado > 0 ? anticipoAceptado : Math.round(montoTotal * 0.6);
    const paymentStage = 'anticipo_60';
    const remoteEnabled = isConfigured && !USE_SEED && !demoMode && !cliente.is_demo && !contratoData.is_demo;
    const requireReadyContract = contratoData.requireReadyContract === true;
    if (remoteEnabled && !requireReadyContract) {
      logContractSigningDiagnostic('blocked_missing_ready_contract_guard', {
        contract_id: contratoData.id || null,
        cliente_id: clienteId,
        source: contratoData.source || 'unknown',
        remoteEnabled,
      });
      throw new Error('Firma bloqueada: usa el flujo seguro de contrato listo antes de firmar.');
    }

    const row = pickContratoDbPayload({
      id:           contratoData.id || undefined,
      cliente_id:   clienteId,
      firmante:     contratoData.firmante     || '',
      firmante_doc: contratoData.firmante_doc || '',
      fecha_firma:  contratoData.fecha_firma  || ahora,
      hash_firma:   hash,
      contenido:    contratoData.contenido    || '',
      client_signature_name: contratoData.firmante || '',
      client_signed_email: contratoData.email || cliente.email || '',
      locked_at:    ahora,
      status:       'firmado',
    });

    let auditEntry = {
      user_id: cliente.portal_user_id || contratoData.user_id || null,
      accion: 'contrato_firmado',
      tabla: 'contratos',
      registro_id: contratoData.id || null,
      detalle: {
        cliente_id: clienteId,
        firmante: contratoData.firmante,
        email: contratoData.email || cliente.email || '',
        fecha: ahora,
        hash,
        source: contratoData.source || 'client_portal',
      },
      created_at: ahora,
    };

    let signedContract = { ...row, hash_firma: hash };
    let cobro60 = null;

    if (remoteEnabled && requireReadyContract) {
      const signPayload = {
        contract_id: contratoData.id || null,
        cliente_id: clienteId,
        signature_name: contratoData.firmante || '',
        accepted: true,
        identity_document_path: contratoData.identity_document_path || '',
        identity_document_type: contratoData.identity_document_type || '',
        identity_consent: contratoData.identity_consent === true,
      };
      logContractSigningDiagnostic('edge_invoke_start', {
        contract_id: signPayload.contract_id,
        cliente_id: signPayload.cliente_id,
        requireReadyContract,
        remoteEnabled,
        hasSignatureName: Boolean(String(signPayload.signature_name || '').trim()),
        hasIdentityDocument: Boolean(signPayload.identity_document_path),
        payloadKeys: Object.keys(signPayload).filter(key => signPayload[key] !== ''),
      });
      const { data: signResult, error: signError } = await supabase.functions.invoke('sign-client-contract', {
        body: signPayload,
      });
      logContractSigningDiagnostic('edge_invoke_result', {
        contract_id: signPayload.contract_id,
        edgeResponseStatus: signError?.status || signError?.context?.status || signResult?.status || null,
        edgeResponseOk: Boolean(signResult?.ok && signResult?.contract?.id && !signError),
        edgeErrorCode: signError?.name || signResult?.error_code || signResult?.code || null,
        hasEdgeError: Boolean(signError || signResult?.error),
        fully_signed: signResult?.fully_signed,
        contract_status: signResult?.contract_status || signResult?.contract?.status || null,
        pending_signatures: signResult?.pending_signatures,
      });
      if (signError) {
        const { text } = await portalErrorMessage(signError, { context: 'firmar-contrato', fallback: 'No se pudo firmar el contrato de forma segura.' });
        throw new Error(text);
      }
      if (!signResult?.ok || !signResult?.contract?.id) {
        const { text } = await portalErrorMessage(signResult, { context: 'firmar-contrato', fallback: 'No se pudo firmar el contrato de forma segura.' });
        throw new Error(text);
      }

      signedContract = signResult.contract;
      hash = signedContract.hash_firma || signResult.hash_firma || signResult.signature_hash || hash;
      cobro60 = signResult.cobro60 ? { ...signResult.cobro60, clienteId: signResult.cobro60.cliente_id || clienteId } : null;
      // Hechos posteriores a la firma que pueden quedar pendientes. Viajan SEPARADOS del
      // hecho de la firma para que la pantalla no los mezcle ni los silencie.
      crmPendienteTrasFirma = signResult.crm_phase_pending === true;
      cobroPendienteTrasFirma = signResult.cobro_inicial_pendiente || null;
      if (signResult.fully_signed === false) {
        setData(d => ({
          ...d,
          contratos: [
            {
              ...signedContract,
              clienteId,
              signature_progress: {
                contract_status: signResult.contract_status || 'partially_signed',
                fully_signed: false,
                pending_signatures: Number(signResult.pending_signatures || 0),
                participant: signResult.participant || null,
              },
            },
            ...(d.contratos || []).filter(c => String(c.id) !== String(signedContract.id)),
          ],
        }));
        return {
          ...signedContract,
          fully_signed: false,
          pending_signatures: Number(signResult.pending_signatures || 0),
          contract_status: signResult.contract_status || 'partially_signed',
          participant: signResult.participant || null,
          signature: signResult.signature || null,
        };
      }
      auditEntry = {
        ...auditEntry,
        user_id: cliente.portal_user_id || contratoData.user_id || auditEntry.user_id,
        registro_id: String(signedContract.id),
        detalle: {
          ...auditEntry.detalle,
          contrato_id: signedContract.id,
          fecha: signedContract.fecha_firma || signResult.fecha_firma || ahora,
          hash,
          source: 'sign-client-contract',
        },
      };
    } else if (remoteEnabled) {
      let existingReadyContract = null;
      if (contratoData.id) {
        const { data: existingContract, error: exactLookupError } = await supabase
          .from('contratos')
          .select('id,status,created_at,fecha_firma,hash_firma')
          .eq('id', contratoData.id)
          .eq('cliente_id', clienteId)
          .maybeSingle();
        if (exactLookupError) {
          console.error('[AppContext.firmarContrato] No se pudo buscar contrato por id:', exactLookupError.message || exactLookupError);
          throw exactLookupError;
        }
        if (!existingContract) {
          throw new Error('No se encontro un contrato listo para este cliente.');
        }
        if (String(existingContract.status || '').toLowerCase() === 'firmado' || existingContract.fecha_firma || existingContract.hash_firma) {
          throw new Error('Este contrato ya fue firmado.');
        }
        if (!['listo_para_firma', 'enviado', 'pendiente_firma'].includes(String(existingContract.status || '').toLowerCase())) {
          throw new Error('El contrato no esta listo para firma.');
        }
        existingReadyContract = existingContract;
      } else {
        const { data: existingReadyContracts, error: contractLookupError } = await supabase
          .from('contratos')
          .select('id,status,created_at')
          .eq('cliente_id', clienteId)
          .in('status', ['listo_para_firma', 'enviado', 'pendiente_firma'])
          .order('created_at', { ascending: false })
          .limit(1);
        if (contractLookupError) {
          console.error('[AppContext.firmarContrato] No se pudo buscar contrato listo existente:', contractLookupError.message || contractLookupError);
          throw contractLookupError;
        }
        existingReadyContract = existingReadyContracts?.[0] || null;
      }

      if (requireReadyContract && !existingReadyContract?.id) {
        throw new Error('No hay un contrato listo para firma en el portal.');
      }

      const contractRequest = existingReadyContract?.id
        ? supabase.from('contratos').update(row).eq('id', existingReadyContract.id).select().single()
        : supabase.from('contratos').insert([row]).select().single();
      const { data: inserted, error: contractError } = await contractRequest;
      if (contractError) {
        console.error('[AppContext.firmarContrato] Supabase rechazo guardar contrato firmado:', contractError.message || contractError);
        throw contractError;
      } else {
        signedContract = inserted || signedContract;
      }
      auditEntry = {
        ...auditEntry,
        registro_id: String(signedContract.id || contratoData.id || clienteId),
        detalle: {
          ...auditEntry.detalle,
          contrato_id: signedContract.id || contratoData.id || null,
        },
      };
      const { error: auditError } = await supabase.from('audit_logs').insert([auditEntry]);
      if (auditError) console.error('[AppContext.firmarContrato] No se pudo guardar audit log de firma:', auditError);
    }

    const newStage = Math.max(contratoData.stage || cliente.stage || 3, 3);
    const phaseResult = await advanceClientPhase({
      clienteId,
      targetStage: newStage,
      source: requireReadyContract ? 'sign_client_contract_edge_sync' : 'sign_client_contract',
      metadata: { contract_id: signedContract?.id || contratoData.id || null },
      relatedEntityType: 'contrato',
      relatedEntityId: signedContract?.id || contratoData.id || null,
      allowCompound: true,
      silent: true,
    });
    if (!phaseResult.ok) {
      console.error('[AppContext.firmarContrato] Contrato firmado, pero no se pudo avanzar fase CRM:', phaseResult.error || phaseResult);
    }

    let paymentLinkUrl = '';
    const existingCobro60 = cobro60 || (data.cobros || []).find(c =>
      String(c.clienteId || c.cliente_id) === String(clienteId) &&
      String(c.payment_stage || '').toLowerCase() === paymentStage &&
      String(c.status || '').toLowerCase() !== 'paid'
    );

    if (monto60 > 0 || cobro60?.id) {
      cobro60 = existingCobro60 || {
        id: crypto.randomUUID?.() || `${Date.now()}-anticipo-60`,
        clienteId,
        cliente_id: clienteId,
        nombre: 'Anticipo 60%',
        tipo: 'Anticipo 60%',
        concepto: `Anticipo 60% · ${cliente.servicio || contratoData.paquete || 'Proyecto Feria Design'}`,
        monto: monto60,
        status: 'pending',
        via: 'Stripe',
        payment_stage: paymentStage,
        vence: addDaysISO(3),
        created_at: ahora,
        updated_at: ahora,
      };

      const cobroAlreadyInState = (data.cobros || []).some(c => String(c.id) === String(cobro60.id));
      if (!existingCobro60) {
        if (remoteEnabled) {
          const { clienteId: _clientId, concepto: _concepto, ...dbCobro } = cobro60;
          const quoteForAttribution = (data.cotizacionesEnviadas || []).find(quote => String(quote.id) === String(dbCobro.quote_id || ''));
          Object.assign(
            dbCobro,
            mergeMissingMarketingAttribution(dbCobro, inheritedMarketingAttribution({ quote: quoteForAttribution, cliente })),
          );
          const { data: insertedCobro, error } = await supabase
            .from('cobros')
            .insert([dbCobro])
            .select().single();
          if (!error && insertedCobro) cobro60 = { ...insertedCobro, clienteId: insertedCobro.cliente_id };
        }
        setData(d => ({ ...d, cobros: [{ ...cobro60, clienteId }, ...d.cobros] }));
      } else if (cobro60?.id && !cobroAlreadyInState) {
        setData(d => ({ ...d, cobros: [{ ...cobro60, clienteId }, ...d.cobros] }));
      }

      if (remoteEnabled) {
        paymentLinkUrl = cobro60.payment_link || cobro60.stripe_payment_link || '';
        const paymentAmount = normalizeMoney(cobro60.monto ?? monto60, monto60);

        if (!paymentLinkUrl) {
          const { createPaymentLink, savePaymentLink } = await import('../lib/stripe');
          const linkResult = await createPaymentLink({
            clienteNombre: cliente.nombre || contratoData.firmante || 'Cliente',
            clienteEmail: cliente.email || contratoData.email || '',
            monto: paymentAmount,
            tipo: 'Anticipo 60%',
            cobro_id: cobro60.id,
            payment_stage: paymentStage,
            cliente_id: clienteId,
            success_url: `${window.location.origin}/pago-completado?return_to=${encodeURIComponent('/portal')}&session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: `${window.location.origin}/portal`,
          });

          if (linkResult?.ok && linkResult.url) {
            paymentLinkUrl = linkResult.url;
            await savePaymentLink(cobro60.id, paymentLinkUrl);
            setData(d => ({
              ...d,
              cobros: d.cobros.map(c => String(c.id) === String(cobro60.id) ? { ...c, stripe_payment_link: paymentLinkUrl, payment_link: paymentLinkUrl } : c),
            }));
          } else if (linkResult?.error) {
            console.error('[AppContext.firmarContrato] No se pudo crear link Stripe del anticipo:', linkResult.error);
          }
        }

      if (paymentLinkUrl) {
        import('../lib/emails').then(({ emailCobroLink }) => emailCobroLink({
          clienteNombre: cliente.nombre || contratoData.firmante || 'Cliente',
          clienteEmail: cliente.email || contratoData.email || '',
          tipo: 'Anticipo 60%',
            monto: paymentAmount,
            paymentLink: paymentLinkUrl,
          })).catch(error => {
            console.error('[AppContext.firmarContrato] No se pudo enviar email de cobro inicial:', error);
          });
        } else {
          console.error('[AppContext.firmarContrato] No se envio email de cobro inicial porque no hay link Stripe.');
        }

        import('../lib/communications').then(({ dispatchCriticalCommunication }) => dispatchCriticalCommunication({
          eventKey: 'contract_signed',
          channel: 'email',
          templateKey: 'contrato_firmado',
          recipientEmail: cliente.email || contratoData.email || '',
          clienteId,
          contratoId: signedContract.id || contratoData.id || null,
          cobroId: cobro60?.id || null,
          payload: {
            nombre: cliente.nombre || contratoData.firmante || 'Cliente',
            proyecto: cliente.servicio || contratoData.paquete || 'Proyecto Feria Design',
            hash,
            portalUrl: `${window.location.origin}/portal`,
          },
        })).then(result => {
          if (result?.ok === false) console.error('[AppContext.firmarContrato] No se pudo enviar email de contrato firmado:', result.error || result);
        }).catch(error => {
          console.error('[AppContext.firmarContrato] Error enviando email de contrato firmado:', error);
        });
      }
    }

    const notifContrato = {
      id: crypto.randomUUID?.() || `${Date.now()}-contrato-firmado`,
      tipo: 'contrato_firmado',
      titulo: 'Contrato firmado',
      desc: `${cliente.nombre || contratoData.firmante || 'Cliente'} firmó el contrato. Se activó el cobro del 60%.`,
      descripcion: `${cliente.nombre || contratoData.firmante || 'Cliente'} firmó el contrato. Se activó el cobro del 60%.`,
      cliente_id: clienteId,
      cobro_id: cobro60?.id || null,
      leida: false,
      created_at: ahora,
    };

    setData(d => ({
      ...d,
      auditLog: [auditEntry, ...(d.auditLog || [])],
      contratos: [
        { ...signedContract, clienteId },
        ...(d.contratos || []).filter(c => String(c.id) !== String(signedContract.id)),
      ],
      clientes: d.clientes.map(c => String(c.id) === String(clienteId)
        ? { ...c, client_signature_name: contratoData.firmante || '', fecha_firma: row.fecha_firma, hash_firma: hash }
        : c),
      notificaciones: [notifContrato, ...d.notificaciones],
    }));

    if (remoteEnabled) {
      supabase.from('notificaciones').insert([notifContrato]).then(({ error }) => {
        if (error) console.error('[AppContext.firmarContrato] No se pudo registrar notificacion de contrato firmado:', error);
      }).catch(error => {
        console.error('[AppContext.firmarContrato] Error registrando notificacion de contrato firmado:', error);
      });
    }

    return { ...signedContract, hash_firma: hash, cobro60, paymentLinkUrl, crmPendiente: crmPendienteTrasFirma, cobroPendiente: cobroPendienteTrasFirma };
  }, [advanceClientPhase, data.clientes, data.cobros, data.cotizacionesEnviadas, demoMode]);

  const saveBrief = useCallback(async (clienteId, respuestas, options = {}) => {
    if (!clienteId || !isConfigured || USE_SEED || demoMode) return null;
    const quoteId = String(options.quote_id || '').trim();
    let existingBrief = null;
    try {
      let existingQuery = supabase
        .from('briefs')
        .select('id,respuestas')
        .eq('cliente_id', clienteId)
        .order('updated_at', { ascending: false })
        .limit(1);
      if (quoteId) {
        existingQuery = existingQuery.eq('quote_id', quoteId);
      } else {
        existingQuery = existingQuery.is('quote_id', null);
      }
      const { data: existingRows, error: existingError } = await existingQuery;
      if (existingError) throw existingError;
      existingBrief = Array.isArray(existingRows) ? existingRows[0] || null : null;
    } catch (error) {
      console.error('[AppContext.saveBrief] No se pudo revisar brief existente:', error);
      throw error;
    }
    let finalResponses = respuestas;
    if (options.appendSubmission) {
      finalResponses = mergeBriefSubmission(existingBrief?.respuestas, respuestas, options.responder || {});
    }

    const payload = {
      cliente_id: clienteId,
      respuestas: finalResponses,
      completado: Boolean(options.completado),
      updated_at: new Date().toISOString(),
    };
    if (options.service_type) payload.service_type = options.service_type;
    if (options.template_key) payload.template_key = options.template_key;
    if (options.contrato_id) payload.contrato_id = options.contrato_id;
    if (options.proyecto_id) payload.proyecto_id = options.proyecto_id;
    if (options.quote_id) payload.quote_id = options.quote_id;
    if (options.activo !== undefined) payload.activo = Boolean(options.activo);

    try {
      const query = existingBrief?.id
        ? supabase.from('briefs').update(payload).eq('id', existingBrief.id)
        : supabase.from('briefs').insert(payload);
      const { data: row, error } = await query.select().single();
      if (error) throw error;
      return row;
    } catch (error) {
      console.error('[AppContext.saveBrief] No se pudo guardar brief:', error);
      throw error;
    }
  }, [demoMode]);

  // Una aprobacion es un hecho que despues se cita como prueba. Antes esta
  // funcion no devolvia nada y descartaba el resultado del upsert: quien la
  // llamaba no tenia forma de distinguir una aprobacion guardada de una
  // rechazada. Ahora exige la fila de vuelta.
  const saveAprobacion = useCallback(async (clienteId, aprobacionId, aprobado) => {
    if (!isConfigured) return { ok: true };

    const { data: filas, error } = await supabase.from('aprobaciones').upsert({
      cliente_id:    clienteId,
      aprobacion_id: aprobacionId,
      aprobado,
      fecha:         new Date().toISOString(),
    }, { onConflict: 'cliente_id,aprobacion_id' }).select('id');

    if (error) {
      console.error('[AppContext.saveAprobacion] No se pudo guardar la aprobacion:', error.message || error);
      return { ok: false, error };
    }
    if (!filas || filas.length === 0) {
      console.warn('[AppContext.saveAprobacion] upsert de 0 filas: no se guardo la aprobacion.');
      return { ok: false, sinFilas: true, error: { message: 'No se guardó la aprobación: no tienes permiso sobre este registro o ya no existe.', code: SIN_FILAS_CODE } };
    }
    return { ok: true, rows: filas };
  }, []);

  const addSueldoProgramado = useCallback(async (sueldo = {}) => {
    const now = new Date().toISOString();
    const row = {
      id: sueldo.id || crypto.randomUUID?.() || Date.now().toString(),
      persona: sueldo.persona || 'Persona',
      cargo: sueldo.cargo || '',
      valor: normalizeMoney(sueldo.valor, 0),
      frecuencia: sueldo.frecuencia || 'mensual',
      fecha_pago: sueldo.fecha_pago || null,
      estado: sueldo.estado || 'pendiente',
      notas: sueldo.notas || '',
      is_demo: sueldo.is_demo ?? demoMode,
      created_at: sueldo.created_at || now,
      updated_at: sueldo.updated_at || now,
    };

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, sueldosProgramados: [row, ...(d.sueldosProgramados || [])] }));
      await syncPayrollFixedExpense(row);
      return row;
    }

    const payload = pickSueldoProgramadoDbPayload(row);
    const { data: inserted, error } = await supabase.from('sueldos_programados').insert([payload]).select().single();
    if (error) {
      console.error('[Feria OS] Error saving scheduled payroll to Supabase', { error, payload });
      throw error;
    }

    const saved = inserted || row;
    setData(d => ({ ...d, sueldosProgramados: [saved, ...(d.sueldosProgramados || [])] }));
    await syncPayrollFixedExpense(saved);
    return saved;
  }, [demoMode, syncPayrollFixedExpense]);

  const updateSueldoProgramado = useCallback(async (id, changes = {}) => {
    const current = (data.sueldosProgramados || []).find(x => String(x.id) === String(id));
    const payload = pickSueldoProgramadoDbPayload({
      ...changes,
      ...(Object.prototype.hasOwnProperty.call(changes, 'valor') ? { valor: normalizeMoney(changes.valor, 0) } : {}),
      updated_at: changes.updated_at || new Date().toISOString(),
    });
    const merged = { ...(current || { id }), ...payload };

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, sueldosProgramados: (d.sueldosProgramados || []).map(x => String(x.id) === String(id) ? { ...x, ...payload } : x) }));
      await syncPayrollFixedExpense(merged);
      return merged;
    }

    if (!Object.keys(payload).length) {
      console.warn('[Feria OS] Scheduled payroll update skipped because payload has no Supabase columns', { id, changes });
      return;
    }

    const { error } = await supabase.from('sueldos_programados').update(payload).eq('id', id);
    if (error) {
      console.error('[Feria OS] Error updating scheduled payroll in Supabase', { error, id, payload });
      throw error;
    }

    setData(d => ({ ...d, sueldosProgramados: (d.sueldosProgramados || []).map(x => String(x.id) === String(id) ? { ...x, ...payload } : x) }));
    await syncPayrollFixedExpense(merged);
    return merged;
  }, [data.sueldosProgramados, demoMode, syncPayrollFixedExpense]);

  const deleteSueldoProgramado = useCallback(async (id) => {
    const current = (data.sueldosProgramados || []).find(x => String(x.id) === String(id)) || { id };
    await deletePayrollFixedExpense(current);

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, sueldosProgramados: (d.sueldosProgramados || []).filter(x => String(x.id) !== String(id)) }));
      return;
    }

    const { error } = await supabase.from('sueldos_programados').delete().eq('id', id);
    if (error) {
      console.error('[Feria OS] Error deleting scheduled payroll from Supabase', { error, id });
      throw error;
    }

    setData(d => ({ ...d, sueldosProgramados: (d.sueldosProgramados || []).filter(x => String(x.id) !== String(id)) }));
  }, [data.sueldosProgramados, deletePayrollFixedExpense, demoMode]);

  const addDeuda = useCallback(async (deuda) => {
    const row = { ...deuda, id: deuda.id || crypto.randomUUID?.() || Date.now().toString(), status: deuda.status || 'pendiente', created_at: deuda.created_at || new Date().toISOString() };
    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, deudas: [row, ...(d.deudas || [])] }));
      await syncDebtFixedExpense(row);
      return row;
    }

    const payload = pickDeudaDbPayload(row);
    const { data: inserted, error } = await supabase.from('deudas').insert([payload]).select().single();
    if (error) {
      console.error('[Feria OS] Error saving debt to Supabase', { error, payload });
      throw error;
    }

    const saved = inserted || row;
    setData(d => ({ ...d, deudas: [saved, ...(d.deudas || [])] }));
    await syncDebtFixedExpense(saved);
    return saved;
  }, [demoMode, syncDebtFixedExpense]);

  const updateDeuda = useCallback(async (id, changes) => {
    const current = (data.deudas || []).find(x => String(x.id) === String(id));
    const updatePayloadSource = { ...(changes || {}), updated_at: changes?.updated_at || new Date().toISOString() };
    const mergedChanges = { ...(current || { id }), ...updatePayloadSource };
    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, deudas: (d.deudas || []).map(x => String(x.id) === String(id) ? { ...x, ...changes } : x) }));
      await syncDebtFixedExpense(mergedChanges);
      return mergedChanges;
    }

    const payload = pickDeudaDbPayload(updatePayloadSource);
    if (!Object.keys(payload).length) {
      console.warn('[Feria OS] Debt update skipped because payload has no Supabase columns', { id, changes });
      return;
    }

    const { error } = await supabase.from('deudas').update(payload).eq('id', id);
    if (error) {
      console.error('[Feria OS] Error updating debt in Supabase', { error, id, payload });
      throw error;
    }

    setData(d => ({ ...d, deudas: (d.deudas || []).map(x => String(x.id) === String(id) ? { ...x, ...payload } : x) }));
    await syncDebtFixedExpense(mergedChanges);
    return mergedChanges;
  }, [data.deudas, demoMode, syncDebtFixedExpense]);

  const deleteDeuda = useCallback(async (id) => {
    const current = (data.deudas || []).find(x => String(x.id) === String(id)) || { id };
    await deleteDebtFixedExpense(current);

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, deudas: (d.deudas || []).filter(x => String(x.id) !== String(id)) }));
      return;
    }

    const { data: deletedRows, error } = await supabase
      .from('deudas')
      .delete()
      .eq('id', id)
      .select('id');

    if (error) {
      console.error('[Feria OS] Error deleting debt from Supabase', { error, id });
      throw error;
    }

    if (!Array.isArray(deletedRows) || deletedRows.length === 0) {
      const notDeletedError = new Error('Supabase did not delete any debt rows');
      console.error('[Feria OS] Debt delete did not affect any Supabase rows', { id });
      throw notDeletedError;
    }

    setData(d => ({ ...d, deudas: (d.deudas || []).filter(x => String(x.id) !== String(id)) }));
  }, [data.deudas, deleteDebtFixedExpense, demoMode]);

  const addTeamMember = useCallback(async (member) => {
    const alreadyPersisted = Boolean(member.alreadyPersisted || member.__alreadyPersisted);
    const normalized = normalizeTeamMember({
      ...member,
      id: member.id || crypto.randomUUID?.() || Date.now().toString(),
      created_at: new Date().toISOString(),
    });
    setData(d => ({ ...d, team: [normalized, ...d.team.map(normalizeTeamMember)] }));
    if (alreadyPersisted) return normalized;
    if (isConfigured) {
      const dbMember = pickTeamDbPayload({ ...normalized, permissions: normalized.permissions });
      const { data: inserted, error } = await supabase.from('team').insert([dbMember]).select().single();
      if (!error && inserted) return normalizeTeamMember(inserted);
      if (error && normalized.email) {
        const existingUpdate = { ...dbMember };
        delete existingUpdate.id;
        delete existingUpdate.created_at;
        const { data: updated } = await supabase
          .from('team')
          .update(existingUpdate)
          .eq('email', normalized.email)
          .select()
          .single();
        if (updated) return normalizeTeamMember(updated);
      }
      // Ni el insert ni el update por email dejaron fila. Antes se devolvia
      // igualmente el miembro construido en memoria, que quien llamaba recibia
      // como si estuviera creado: aparecia en la lista del equipo y no existia.
      console.error('[AppContext.addTeamMember] No se pudo crear ni actualizar el miembro:', error?.message || error);
      return { ok: false, error: error || { message: 'No se guardó el miembro del equipo: no tienes permiso o el registro ya no existe.' } };
    }
    return normalized;
  }, []);

  // Cambiar los permisos de un miembro es un hecho de autorizacion. Antes esto
  // era `try { await ... } catch (_) {}`: PostgREST no lanza —devuelve `error`—
  // asi que ese catch no se ejecutaba nunca, y un UPDATE de 0 filas tampoco
  // trae error. La lista quedaba con los permisos nuevos en pantalla y la base
  // con los viejos, sin una sola señal.
  const updateTeamMember = useCallback(async (id, changes) => {
    const previo = (data.team || []).find(m => String(m.id) === String(id)) || null;
    setData(d => ({ ...d, team: d.team.map(m => String(m.id) === String(id) ? normalizeTeamMember({ ...m, ...changes }) : normalizeTeamMember(m)) }));
    if (!isConfigured) return { ok: true };

    const dbChanges = pickTeamDbPayload(changes);
    delete dbChanges.id;
    delete dbChanges.created_at;
    if (!Object.keys(dbChanges).length) return { ok: true };

    const resultado = await updatePorId(supabase, 'team', id, dbChanges, { queSeGuardaba: 'el miembro del equipo' });
    if (!resultado.ok && previo) {
      // Devolver la fila a como estaba: dejarla con los permisos nuevos seria
      // seguir afirmando el cambio que la base rechazo.
      setData(d => ({ ...d, team: d.team.map(m => String(m.id) === String(id) ? normalizeTeamMember(previo) : m) }));
    }
    return resultado;
  }, [data.team]);

  const deleteTeamMember = useCallback(async (id) => {
    const previo = (data.team || []).find(m => String(m.id) === String(id)) || null;
    setData(d => ({ ...d, team: d.team.filter(m => String(m.id) !== String(id)) }));
    if (!isConfigured) return { ok: true };

    const resultado = await borrarPorId(supabase, 'team', id, { queSeBorraba: 'el miembro del equipo' });
    if (!resultado.ok && previo) {
      setData(d => ({
        ...d,
        team: d.team.some(m => String(m.id) === String(id))
          ? d.team
          : [...d.team, normalizeTeamMember(previo)],
      }));
    }
    return resultado;
  }, [data.team]);

  const addProjectUpdate = useCallback(async ({ proyectoId, tipo = 'avance', titulo, descripcion, archivo_url = '', attachment_bucket = '', attachment_path = '', attachment_name = '', attachment_type = '', attachment_size = 0, reviewer_id = null, file_type = '', version_label = '', application_id = null }) => {
    const currentProject = (data.proyectos || []).find(p => String(p.id) === String(proyectoId));
    const assignedReviewerId = reviewer_id || currentProject?.assigned_reviewer_id || currentProject?.assignedReviewerId || currentProject?.reviewer_id || null;
    const row = {
      id: crypto.randomUUID?.() || Date.now().toString(),
      proyecto_id: proyectoId,
      tipo,
      titulo: titulo || 'Avance de diseño',
      descripcion: descripcion || '',
      archivo_url,
      attachment_bucket,
      attachment_path,
      attachment_name,
      attachment_type,
      attachment_size: Number(attachment_size || 0),
      reviewer_id: reviewer_id || null,
      file_type,
      version_label,
      application_id: nullableUuid(application_id),
      status: 'pendiente_revision',
      published_to_client: false,
      published_at: null,
      presentation_version: version_label || '',
      client_visible_status: 'draft',
      client_decision_status: 'pending',
      client_decision_at: null,
      created_at: new Date().toISOString(),
    };
    const notifyAdvanceSubmitted = async (savedUpdate = row) => {
      try {
        // Si hay revisor asignado el aviso es para esa persona (traducido a profiles.id);
        // si no, cae al rol crm, que sí matchea por `owner`/`recipient_role`.
        const notificar = assignedReviewerId
          ? payload => notifyTeamMember(assignedReviewerId, payload, 'la revision del avance')
          : payload => addNotification(payload);
        await notificar({
          tipo: 'project_update_submitted',
          titulo: 'Avance pendiente de revision',
          descripcion: `${currentProject?.nombre || 'Proyecto'} tiene un avance pendiente. Proxima accion: revisar avance.`,
          priority: 'high',
          module: 'proyectos',
          route: '/kanban',
          owner: assignedReviewerId ? 'revisor' : 'crm',
          recipient_role: assignedReviewerId ? null : 'crm',
          entity_type: 'project_update',
          entity_id: savedUpdate.id || row.id,
          proyecto_id: proyectoId,
          dedupe_key: `project_update_submitted:${savedUpdate.id || row.id}`,
          metadata: {
            project_id: proyectoId,
            reviewer_id: assignedReviewerId || null,
            next_action: 'Revisar avance',
          },
        });
      } catch (error) {
        console.warn('[AppContext.addProjectUpdate] No se pudo crear notificacion de avance:', error?.message || error);
      }
    };
    setData(d => ({ ...d, projectUpdates: upsertRowById(d.projectUpdates, row, { position: 'start', sort: sortNewestFirst }) }));
    if (isConfigured) {
      try {
        const { data: inserted, error } = await supabase.from('project_updates').insert([filterProjectUpdatePayload(row)]).select().single();
        if (error) throw error;
        if (inserted) {
          setData(d => ({ ...d, projectUpdates: upsertRowById(d.projectUpdates, inserted, { position: 'start', sort: sortNewestFirst }) }));
          await notifyAdvanceSubmitted(inserted);
          refreshProjectReviewData(proyectoId);
          return inserted;
        }
        throw new Error('Supabase no devolvio la fila creada de project_updates.');
      } catch (error) {
        setData(d => ({ ...d, projectUpdates: (d.projectUpdates || []).filter(x => String(x.id) !== String(row.id)) }));
        console.error('[AppContext.addProjectUpdate] No se pudo guardar avance de proyecto:', error);
        throw error;
      }
    }
    await notifyAdvanceSubmitted(row);
    return row;
  }, [addNotification, notifyTeamMember, data.proyectos, refreshProjectReviewData]);

  const reviewProjectUpdate = useCallback(async (updateId, feedback, status = 'feedback') => {
    const currentUpdate = (data.projectUpdates || []).find(u => String(u.id) === String(updateId));
    const projectId = currentUpdate?.proyecto_id || currentUpdate?.proyectoId || null;
    const currentProject = (data.proyectos || []).find(p => String(p.id) === String(projectId));
    const designerId = currentProject?.creativo_id || currentProject?.creativo || currentProject?.designer_id || null;
    const organized = String(feedback || '')
      .split(/\n+/)
      .map(x => x.trim())
      .filter(Boolean)
      .map((line, idx) => (idx + 1) + '. ' + line.replace(/^[-•\d.\s]+/, ''))
      .join('\n');
    const changes = { feedback, organized_feedback: organized || feedback, status, reviewed_at: new Date().toISOString() };
    setData(d => {
      const current = d.projectUpdates || [];
      if (!current.some(x => String(x.id) === String(updateId))) return d;
      return {
        ...d,
        projectUpdates: sortNewestFirst(current.map(x => String(x.id) === String(updateId) ? { ...x, ...changes } : x)),
      };
    });
    let persisted = !isConfigured;
    if (isConfigured) {
      try {
        const { data: updated, error } = await supabase.from('project_updates').update(changes).eq('id', updateId).select().single();
        if (error) throw error;
        if (updated?.id) {
          setData(d => ({ ...d, projectUpdates: upsertRowById(d.projectUpdates, updated, { position: 'start', sort: sortNewestFirst }) }));
        }
        persisted = true;
      } catch (error) {
        console.error('[AppContext.reviewProjectUpdate] No se pudo actualizar revision de avance:', error);
      }
    }
    // Los tres botones de revision no daban NINGUNA senal en pantalla: se tocaba
    // "Aprobar avance" y no pasaba nada visible. Por eso durante el recorrido se
    // toco tres veces el boton equivocado sin notarlo.
    if (!persisted) {
      showToast('No se pudo guardar la revision del avance. Intenta de nuevo.', '!');
    } else if (String(status || '').toLowerCase() === 'aprobado') {
      showToast('Avance aprobado', '✓');
    } else {
      showToast('Feedback enviado al diseñador', '↑');
    }
    if (persisted && projectId) {
      refreshProjectReviewData(projectId);
    }
    // Antes se excluia 'aprobado': al disenador se le avisaba solo cuando algo estaba
    // mal. Ahora tambien se avisa la aprobacion, que es la mitad buena del trabajo.
    if (persisted) {
      const aprobado = String(status || '').toLowerCase() === 'aprobado';
      const proyectoNombre = currentProject?.nombre || 'Proyecto';
      try {
        const notificar = designerId
          ? payload => notifyTeamMember(designerId, payload, 'la revision del avance')
          : payload => addNotification(payload);
        await notificar({
          tipo: 'project_update_feedback',
          titulo: aprobado ? 'Avance aprobado' : 'Feedback recibido',
          descripcion: aprobado
            ? `${proyectoNombre}: tu avance fue aprobado por el revisor.`
            : `${proyectoNombre} tiene feedback del revisor. Proxima accion: revisar cambios solicitados.`,
          priority: aprobado ? 'low' : (String(status || '').toLowerCase().includes('cambio') ? 'high' : 'medium'),
          module: 'proyectos',
          route: '/kanban',
          owner: 'creativo',
          recipient_role: designerId ? null : 'creativo',
          entity_type: 'project_update',
          entity_id: updateId,
          proyecto_id: projectId,
          dedupe_key: `project_update_feedback:${updateId}:${aprobado ? 'aprobado' : 'feedback'}`,
          metadata: {
            project_id: projectId,
            status,
            next_action: aprobado ? 'Sin accion pendiente' : 'Revisar cambios solicitados',
          },
        });
      } catch (error) {
        console.warn('[AppContext.reviewProjectUpdate] No se pudo crear notificacion de feedback:', error?.message || error);
      }
    }
  }, [addNotification, notifyTeamMember, data.projectUpdates, data.proyectos, refreshProjectReviewData, showToast]);

  // No existia forma de corregir un avance mal creado: sin edicion y sin borrado, un
  // titulo equivocado o una descripcion vacia quedaban para siempre en la tabla.
  const updateProjectUpdate = useCallback(async (updateId, changes = {}) => {
    const payload = filterProjectUpdatePayload({ ...changes, updated_at: new Date().toISOString() });
    if (!Object.keys(payload).length) return { ok: true };
    setData(d => ({
      ...d,
      projectUpdates: (d.projectUpdates || []).map(u => String(u.id) === String(updateId) ? { ...u, ...payload } : u),
    }));
    if (!isConfigured || USE_SEED || demoMode) return { ok: true };
    const resultado = await updatePorId(supabase, 'project_updates', updateId, payload, { queSeGuardaba: 'el avance' });
    if (!resultado.ok) {
      showToast(resultado.sinFilas ? resultado.error.message : 'No se pudo guardar el avance. Intenta de nuevo.', '!');
      return resultado;
    }
    showToast('Avance actualizado', '✓');
    return { ok: true };
  }, [demoMode, showToast]);

  // SOLO borra avances NO publicados, y el guard vive ACA y no en la UI: `project_updates`
  // tiene 4 FK entrantes en ON DELETE CASCADE, una de ellas `project_approval_acts`, que
  // son actas FIRMADAS con hash de auditoria. Borrar un avance publicado destruiria la
  // evidencia de que el cliente aprobo ese trabajo, y el CASCADE es silencioso.
  const deleteProjectUpdate = useCallback(async (updateId) => {
    const actual = (data.projectUpdates || []).find(u => String(u.id) === String(updateId));
    if (!actual) return { ok: false, error: 'No se encontro el avance.' };
    if (actual.published_to_client) {
      const error = 'Este avance ya esta publicado al cliente y no se puede borrar.';
      showToast(error, '!');
      return { ok: false, error };
    }
    if (isConfigured && !USE_SEED && !demoMode) {
      const { error } = await supabase.from('project_updates').delete().eq('id', updateId);
      if (error) {
        console.error('[AppContext.deleteProjectUpdate] No se pudo borrar el avance:', error);
        showToast('No se pudo borrar el avance. Intenta de nuevo.', '!');
        return { ok: false, error };
      }
      // El archivo no se borra en cascada: no hay relacion entre la fila y el storage.
      // Si falla, se sigue igual pero queda el rastro: un adjunto huerfano invisible es
      // basura que nadie va a encontrar nunca.
      if (actual.attachment_path) {
        const { error: storageError } = await supabase.storage
          .from(actual.attachment_bucket || 'mensajes-adjuntos')
          .remove([actual.attachment_path]);
        if (storageError) {
          console.error('[AppContext.deleteProjectUpdate] Avance borrado, pero su adjunto quedo huerfano en storage:', {
            path: actual.attachment_path,
            bucket: actual.attachment_bucket || 'mensajes-adjuntos',
            error: storageError,
          });
          showToast('Avance borrado, pero su archivo quedo en el almacenamiento.', '⚠');
        }
      }
    }
    setData(d => ({
      ...d,
      projectUpdates: (d.projectUpdates || []).filter(u => String(u.id) !== String(updateId)),
      projectUpdateComments: (d.projectUpdateComments || []).filter(c => String(c.update_id) !== String(updateId)),
    }));
    return { ok: true };
  }, [data.projectUpdates, demoMode, showToast]);

  const publishProjectUpdateToClient = useCallback(async (updateId) => {
    const now = new Date().toISOString();
    const currentUpdate = (data.projectUpdates || []).find(u => String(u.id) === String(updateId));
    if (!currentUpdate) throw new Error('No se encontro el avance para publicar.');
    if (!currentUpdate.archivo_url && !currentUpdate.descripcion) {
      throw new Error('Agrega un archivo, link o descripcion antes de publicar al cliente.');
    }
    const projectId = currentUpdate.proyecto_id || currentUpdate.proyectoId;
    const currentProject = (data.proyectos || []).find(p => String(p.id) === String(projectId));
    const clienteId = currentProject?.cliente_id || currentProject?.clienteId || null;
    const changes = {
      published_to_client: true,
      published_at: now,
      published_by: null,
      presentation_version: currentUpdate.presentation_version || currentUpdate.version_label || 'V1',
      client_visible_status: 'ready_for_review',
      client_decision_status: 'pending',
      client_decision_at: null,
      updated_at: now,
    };
    const projectChanges = {
      production_status: advanceProductionStatus(currentProject?.production_status, 'presentado_cliente', { allowReviewReset: true }),
      updated_at: now,
    };
    const notif = {
      id: crypto.randomUUID?.() || `${Date.now()}-presentation-published`,
      tipo: 'presentacion_publicada',
      titulo: 'Presentacion publicada al cliente',
      // Iba tambien `desc:`, columna que NO existe en `notificaciones`: PostgREST
      // rechazaba el insert entero y el unico rastro era un console.error, asi que
      // esta notificacion nunca se creo. Y sin owner/recipient_role no la veia nadie,
      // porque matchesOwner (Notificaciones:23) compara contra esos campos.
      descripcion: `${currentProject?.nombre || 'Proyecto'} ya tiene una presentacion visible en el portal.`,
      owner: 'crm',
      recipient_role: 'crm',
      cliente_id: clienteId,
      proyecto_id: projectId || null,
      channel: 'interno',
      leida: false,
      created_at: now,
      payload: { update_id: updateId, source: 'publishProjectUpdateToClient' },
    };

    if (isConfigured) {
      const { data: updatedRows, error: updateError } = await supabase.from('project_updates').update(filterProjectUpdatePayload(changes)).eq('id', updateId).select();
      if (updateError) {
        console.error('[AppContext.publishProjectUpdateToClient] No se pudo publicar avance:', updateError);
        // La migración 190 impide publicar antes de que exista una presentación
        // realizada. Llega como SQLSTATE 23514 con el texto del trigger (sin
        // acentos, porque viaja desde PostgreSQL). Se traduce a la frase que el
        // equipo necesita leer, en vez de soltar un error crudo de la base.
        if (String(updateError.code) === '23514' && /presentacion realizada/i.test(String(updateError.message || ''))) {
          throw new Error('Primero registra la presentación al cliente como realizada. Marca la reunión de presentación como completada en el Calendario y vuelve a publicar este avance.');
        }
        throw updateError;
      }
      if (!updatedRows?.length) {
        throw new Error('No se pudo publicar porque el avance no existe en la base de datos.');
      }
      const persistedUpdate = updatedRows[0];
      let projectPersisted = false;
      if (projectId) {
        // `projectPersisted` alimenta lo que se le reporta al usuario; sin verificar filas
        // se marcaba como persistido un update que RLS pudo haber descartado.
        const proyectoResultado = await updatePorId(supabase, 'proyectos', projectId, projectChanges, { queSeGuardaba: 'el proyecto' });
        const projectError = proyectoResultado.ok ? null : proyectoResultado.error;
        if (projectError) {
          console.error('[AppContext.publishProjectUpdateToClient] No se pudo actualizar proyecto publicado:', projectError);
        } else {
          projectPersisted = true;
        }
      }
      const { error: notifError } = await supabase.from('notificaciones').insert([notif]);
      if (notifError) console.error('[AppContext.publishProjectUpdateToClient] No se pudo registrar notificacion interna:', notifError);

      // ┌─ AVISAR AL EQUIPO QUE PRODUCE, NO SOLO AL CRM ──────────────────────────┐
      // │ Publicar avisaba UNA sola vez, con `owner:'crm'`. El disenador asignado  │
      // │ y el revisor —los dos que tienen algo que hacer despues de publicar— no  │
      // │ se enteraban de nada. Se enteraban si miraban.                           │
      // │                                                                          │
      // │ Va con `notifyTeamMember`, que resuelve `team.id -> profiles.id` y FALLA │
      // │ RUIDOSO con un toast si no puede: un destinatario sin resolver es        │
      // │ exactamente lo que dejo 525 notificaciones sin dueno.                    │
      // │                                                                          │
      // │ ⚠️ EL CLIENTE SIGUE SIN ENTERARSE. Este cambio avisa al EQUIPO. El aviso │
      // │ al cliente necesita emisor de correo y se cruza con las plantillas       │
      // │ huerfanas (A2): va con el frente del cliente, no con este.               │
      // └──────────────────────────────────────────────────────────────────────────┘
      const equipoAAvisar = Array.from(new Set([
        currentProject?.creativo || currentProject?.creativo_id || null,
        currentProject?.assigned_reviewer_id || null,
      ].filter(Boolean).map(String)));

      for (const teamId of equipoAAvisar) {
        await notifyTeamMember(teamId, {
          tipo: 'presentacion_publicada',
          titulo: 'Se publico tu avance al cliente',
          descripcion: `${currentProject?.nombre || 'Proyecto'} ya esta visible en el portal, esperando la decision del cliente.`,
          owner: 'creativo',
          module: 'proyectos',
          cliente_id: clienteId,
          proyecto_id: projectId || null,
          channel: 'interno',
          payload: { update_id: updateId, source: 'publishProjectUpdateToClient' },
        }, 'la publicacion del avance');
      }

      // ┌─ Y AHORA SI: AVISARLE AL CLIENTE ───────────────────────────────────────┐
      // │ Publicar y avisar son dos hechos distintos, y se reportan por separado.  │
      // │ NO se mueve la fase del CRM para conseguir el correo (decision de        │
      // │ producto, 2026-08-09): eso acoplaria dos verdades que no son la misma.   │
      // │                                                                          │
      // │ Un correo caido NO revierte una publicacion valida: el avance ESTA en el │
      // │ portal. Lo que cambia es lo que la UI afirma.                            │
      // └──────────────────────────────────────────────────────────────────────────┘
      const cliente = (data.clientes || []).find(c => String(c.id) === String(clienteId)) || null;
      const claveDelAviso = claveDeAvisoDeAvance(updateId);
      let estadoDelAviso = 'sin_correo';

      // Idempotencia: `communication_events` no tiene unicidad sobre `event_key`, asi que
      // se consulta ANTES de enviar. Cubre recargas y reintentos; NO cubre dos envios
      // simultaneos en vuelo (documentado como riesgo residual).
      let eventoPrevio = null;
      if (claveDelAviso) {
        const { data: previos } = await supabase
          .from('communication_events')
          .select('id,status')
          .eq('event_key', claveDelAviso)
          .order('created_at', { ascending: false })
          .limit(1);
        eventoPrevio = previos?.[0] || null;
      }

      const decision = decidirAvisoDeAvance({ correoCliente: cliente?.email, eventoPrevio });
      if (decision === 'ya_avisado') {
        estadoDelAviso = 'ya_avisado';
      } else if (decision === 'enviar') {
        const envio = await dispatchCriticalCommunication({
          eventKey: claveDelAviso,
          channel: 'email',
          templateKey: PLANTILLA_AVISO_DE_AVANCE,
          recipientEmail: cliente.email,
          clienteId,
          proyectoId: projectId || null,
          isDemo: Boolean(cliente?.is_demo || demoMode || USE_SEED),
          payload: {
            cliente_nombre: cliente?.nombre || cliente?.empresa || 'Cliente',
            project_name: currentProject?.nombre || 'tu proyecto',
            portal_url: PORTAL_URL,
            update_id: updateId,
            source: 'publishProjectUpdateToClient',
          },
        });
        estadoDelAviso = envio?.ok ? 'enviado' : 'fallido';
        if (!envio?.ok) {
          // Un fallo puede significar dos cosas MUY distintas: que el correo no salio, o que
          // otra publicacion simultanea gano la carrera y el indice unico rechazo esta. Se
          // vuelve a preguntar antes de decirle al staff que no se aviso: reportar un fallo
          // que no ocurrio es la misma clase de mentira que reportar un exito que tampoco.
          const { data: ahora } = await supabase
            .from('communication_events')
            .select('id,status')
            .eq('event_key', claveDelAviso)
            .order('created_at', { ascending: false })
            .limit(1);
          if (decidirAvisoDeAvance({ correoCliente: cliente.email, eventoPrevio: ahora?.[0] || null }) === 'ya_avisado') {
            estadoDelAviso = 'ya_avisado';
          } else {
            console.error('[AppContext.publishProjectUpdateToClient] El avance se publico pero el aviso al cliente fallo:', envio?.error || envio);
          }
        }
      }

      setData(d => ({
        ...d,
        projectUpdates: (d.projectUpdates || []).map(u => String(u.id) === String(updateId) ? { ...u, ...persistedUpdate } : u),
        proyectos: projectPersisted ? (d.proyectos || []).map(p => String(p.id) === String(projectId) ? { ...p, ...projectChanges } : p) : d.proyectos,
        notificaciones: notifError ? d.notificaciones : [notif, ...(d.notificaciones || [])],
      }));

      return { ok: true, published_at: persistedUpdate.published_at || now, update: persistedUpdate, aviso: estadoDelAviso };
    }

    setData(d => ({
      ...d,
      projectUpdates: (d.projectUpdates || []).map(u => String(u.id) === String(updateId) ? { ...u, ...changes } : u),
      proyectos: (d.proyectos || []).map(p => String(p.id) === String(projectId) ? { ...p, ...projectChanges } : p),
      notificaciones: [notif, ...(d.notificaciones || [])],
    }));

    return { ok: true, published_at: now };
  }, [data.clientes, data.projectUpdates, data.proyectos, demoMode, notifyTeamMember]);

  const addBrandkitAsset = useCallback(async (asset = {}) => {
    const now = new Date().toISOString();
    const row = {
      id: asset.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Date.now().toString()),
      cliente_id: asset.cliente_id || asset.clienteId,
      proyecto_id: asset.proyecto_id || asset.proyectoId,
      asset_type: asset.asset_type || asset.assetType || 'otros',
      title: asset.title || asset.titulo || 'Asset Brand Kit',
      description: asset.description || asset.descripcion || '',
      storage_bucket: asset.storage_bucket || 'brandkit-assets',
      storage_path: asset.storage_path || '',
      mime_type: asset.mime_type || '',
      file_size: asset.file_size || null,
      status: asset.status || 'draft',
      uploaded_by: asset.uploaded_by || null,
      published_at: asset.status === 'published' ? (asset.published_at || now) : asset.published_at || null,
      access_grant_count: Number(asset.access_grant_count || 0),
      last_access_granted_at: asset.last_access_granted_at || null,
      is_demo: Boolean(asset.is_demo || demoMode),
      created_at: asset.created_at || now,
      updated_at: now,
    };

    if (!row.cliente_id || !row.proyecto_id) {
      throw new Error('El asset necesita cliente y proyecto.');
    }

    if (isConfigured && !demoMode && !row.is_demo) {
      const dbRow = filterBrandkitAssetPayload(row);
      const { data: inserted, error } = await supabase
        .from('brandkit_assets')
        .insert([dbRow])
        .select()
        .single();
      if (error) {
        console.error('[AppContext.addBrandkitAsset] No se pudo guardar asset Brand Kit:', error.message || error);
        throw error;
      }
      setData(d => ({ ...d, brandkitAssets: [inserted, ...(d.brandkitAssets || [])] }));
      archiveBrandkitAssetForInternalArchive(inserted, 'addBrandkitAsset');
      return inserted;
    }

    setData(d => ({ ...d, brandkitAssets: [row, ...(d.brandkitAssets || [])] }));
    return row;
  }, [archiveBrandkitAssetForInternalArchive, demoMode]);

  const updateBrandkitAsset = useCallback(async (id, changes = {}) => {
    const now = new Date().toISOString();
    const patch = { ...changes, updated_at: now };
    if (patch.status === 'published' && !patch.published_at) patch.published_at = now;
    if (isConfigured && !demoMode) {
      const dbPatch = filterBrandkitAssetPayload(patch);
      const { data: updated, error } = await supabase
        .from('brandkit_assets')
        .update(dbPatch)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        console.error('[AppContext.updateBrandkitAsset] No se pudo actualizar asset Brand Kit:', error.message || error);
        throw error;
      }
      setData(d => ({ ...d, brandkitAssets: (d.brandkitAssets || []).map(asset => String(asset.id) === String(id) ? updated : asset) }));
      if (updated?.storage_path && ['storage_path', 'storage_bucket', 'status', 'published_at'].some(key => Object.prototype.hasOwnProperty.call(changes, key))) {
        archiveBrandkitAssetForInternalArchive(updated, 'updateBrandkitAsset');
      }
      return updated;
    }

    setData(d => ({ ...d, brandkitAssets: (d.brandkitAssets || []).map(asset => String(asset.id) === String(id) ? { ...asset, ...patch } : asset) }));
    return { id, ...patch };
  }, [archiveBrandkitAssetForInternalArchive, demoMode]);

  const publishBrandkitAsset = useCallback(async (id) => updateBrandkitAsset(id, { status: 'published', published_at: new Date().toISOString() }), [updateBrandkitAsset]);

  const submitClientPresentationDecision = useCallback(async ({ project_update_id, decision, notes = '', adjustments = [], signature_name = '', client_context = {} }) => {
    const now = new Date().toISOString();
    const updateId = project_update_id;
    const currentUpdate = (data.projectUpdates || []).find(u => String(u.id) === String(updateId));
    if (!currentUpdate) throw new Error('No se encontro la presentacion publicada.');
    const projectId = currentUpdate.proyecto_id || currentUpdate.proyectoId;
    const currentProject = (data.proyectos || []).find(p => String(p.id) === String(projectId));
    const clienteId = currentProject?.cliente_id || currentProject?.clienteId || client_context?.cliente_id || null;
    const version = currentUpdate.presentation_version || currentUpdate.version_label || 'V1';
    const normalizedDecision = String(decision || '').toLowerCase();

    if (!['approved', 'adjustments_requested'].includes(normalizedDecision)) {
      throw new Error('Decision invalida.');
    }
    if (normalizedDecision === 'adjustments_requested' && !String(notes || '').trim() && !adjustments.length) {
      throw new Error('Describe los ajustes solicitados antes de enviar.');
    }

    if (!isConfigured || USE_SEED || demoMode || currentProject?.is_demo || currentUpdate?.is_demo) {
      const actorKey = client_context?.decision_actor_key || (clienteId ? `primary:${clienteId}` : 'primary:demo');
      const actorType = client_context?.decision_actor_type || 'primary_contact';
      const record = {
        id: crypto.randomUUID?.() || `${Date.now()}-approval-record`,
        cliente_id: clienteId,
        proyecto_id: projectId,
        project_update_id: updateId,
        presentation_version: version,
        decision: normalizedDecision,
        status: 'active',
        approved_at: normalizedDecision === 'approved' ? now : null,
        adjustments_requested_at: normalizedDecision === 'adjustments_requested' ? now : null,
        client_name: signature_name || client_context?.client_name || '',
        client_email: client_context?.client_email || '',
        client_notes: notes,
        decision_actor_key: actorKey,
        decision_actor_type: actorType,
        decision_user_id: client_context?.decision_user_id || null,
        portal_member_id: client_context?.portal_member_id || null,
        decision_actor_name: client_context?.client_name || signature_name || '',
        decision_actor_email: client_context?.client_email || '',
        audit_hash: `demo-${Date.now()}`,
        is_demo: true,
        created_at: now,
        updated_at: now,
      };
      const adjustmentRows = normalizedDecision === 'adjustments_requested'
        ? (adjustments.length ? adjustments : [{ body: notes }]).map((item, index) => ({
            id: crypto.randomUUID?.() || `${Date.now()}-adjustment-${index}`,
            approval_record_id: record.id,
            cliente_id: clienteId,
            proyecto_id: projectId,
            project_update_id: updateId,
            presentation_version: version,
            body: typeof item === 'string' ? item : (item.body || item.text || notes),
            page_ref: typeof item === 'object' ? item.page_ref || item.page || '' : '',
            priority: typeof item === 'object' ? item.priority || 'normal' : 'normal',
            status: 'open',
            is_demo: true,
            created_at: now,
            updated_at: now,
          }))
        : [];

      setData(d => ({
        ...d,
        projectUpdates: (d.projectUpdates || []).map(u => String(u.id) === String(updateId) ? { ...u, client_decision_status: normalizedDecision, client_decision_at: now } : u),
        proyectos: (d.proyectos || []).map(p => String(p.id) === String(projectId) ? { ...p, production_status: advanceProductionStatus(p.production_status, normalizedDecision === 'approved' ? 'aprobado_cliente' : 'cambios_solicitados'), updated_at: now } : p),
        projectApprovalRecords: [record, ...(d.projectApprovalRecords || [])],
        projectAdjustmentRequests: [...adjustmentRows, ...(d.projectAdjustmentRequests || [])],
      }));
      return { ok: true, decision: normalizedDecision, approval_record: record, adjustments: adjustmentRows };
    }

    const { data: result, error } = await supabase.functions.invoke('client-project-approval', {
      body: {
        project_update_id: updateId,
        decision: normalizedDecision,
        notes,
        adjustments,
        signature_name,
        client_context,
      },
    });
    if (error || result?.ok === false) {
      const message = result?.error || error?.message || 'No se pudo registrar la decision.';
      console.error('[AppContext.submitClientPresentationDecision] Error registrando decision:', error || result);
      throw new Error(message);
    }

    const record = result?.approval_record;
    const adjustmentRows = Array.isArray(result?.adjustments) ? result.adjustments : [];
    const updatedProjectUpdate = result?.project_update;
    const approvalSummary = result?.approval_summary || null;
    const shouldAdvanceProject = normalizedDecision === 'adjustments_requested' || approvalSummary?.all_approved !== false;
    const nextProductionEvent = normalizedDecision === 'approved' ? 'aprobado_cliente' : 'cambios_solicitados';

    setData(d => ({
      ...d,
      projectUpdates: (d.projectUpdates || []).map(u => String(u.id) === String(updateId) ? { ...u, ...(updatedProjectUpdate || {}), client_decision_status: updatedProjectUpdate?.client_decision_status || normalizedDecision, client_decision_at: updatedProjectUpdate?.client_decision_at || now } : u),
      proyectos: (d.proyectos || []).map(p => String(p.id) === String(projectId) && shouldAdvanceProject ? { ...p, production_status: advanceProductionStatus(p.production_status, nextProductionEvent), updated_at: now } : p),
      projectApprovalRecords: record ? [record, ...(d.projectApprovalRecords || []).filter(x => String(x.id) !== String(record.id))] : d.projectApprovalRecords || [],
      projectAdjustmentRequests: adjustmentRows.length ? [...adjustmentRows, ...(d.projectAdjustmentRequests || []).filter(x => !adjustmentRows.some(row => String(row.id) === String(x.id)))] : d.projectAdjustmentRequests || [],
    }));

    return result;
  }, [data.projectUpdates, data.proyectos, demoMode]);

  const signProjectApprovalAct = useCallback(async ({ approval_record_id, signature_name = '', accepted = false, client_context = {} }) => {
    const now = new Date().toISOString();
    const record = (data.projectApprovalRecords || []).find(r => String(r.id) === String(approval_record_id));
    if (!record) throw new Error('No se encontro la aprobacion de presentacion.');
    if (record.decision !== 'approved') throw new Error('Solo se puede firmar acta sobre una presentacion aprobada.');
    if (!accepted) throw new Error('Debes aceptar los terminos del acta.');
    if (!String(signature_name || '').trim()) throw new Error('Escribe tu nombre/firma para completar el acta.');

    const projectId = record.proyecto_id || record.proyectoId;
    const updateId = record.project_update_id || record.projectUpdateId;
    const currentProject = (data.proyectos || []).find(p => String(p.id) === String(projectId));
    const currentUpdate = (data.projectUpdates || []).find(u => String(u.id) === String(updateId));
    const clienteId = record.cliente_id || record.clienteId || currentProject?.cliente_id || currentProject?.clienteId || client_context?.cliente_id || null;
    const currentCliente = (data.clientes || []).find(c => String(c.id) === String(clienteId));
    const version = record.presentation_version || currentUpdate?.presentation_version || currentUpdate?.version_label || 'V1';
    const alreadySigned = (data.projectApprovalActs || []).some(act => (
      String(act.approval_record_id || act.approvalRecordId) === String(approval_record_id) &&
      String(act.status || 'signed') === 'signed'
    ));
    if (alreadySigned) throw new Error('Esta acta ya fue firmada.');

    if (!isConfigured || USE_SEED || demoMode || currentProject?.is_demo || currentCliente?.is_demo) {
      const deliverables = (data.projectApplications || [])
        .filter(app => String(app.proyecto_id || app.proyectoId) === String(projectId))
        .map((app, index) => ({
          application_id: app.application_id || app.id,
          name: app.name || app.nombre || `Entregable ${index + 1}`,
          category: app.category || app.categoria || '',
          status: app.status || 'pendiente',
          is_extra: Boolean(app.is_extra),
          extra_status: app.extra_status || (app.is_extra ? 'pagada' : 'incluida'),
          sort_order: app.sort_order ?? index + 1,
        }));
      const amountBase = Number(currentProject?.package_price || currentProject?.monto || currentCliente?.monto || 0);
      const act = {
        id: crypto.randomUUID?.() || `${Date.now()}-approval-act`,
        approval_record_id,
        cliente_id: clienteId,
        proyecto_id: projectId,
        project_update_id: updateId,
        presentation_version: version,
        status: 'signed',
        statement: APPROVAL_ACT_STATEMENT,
        approved_deliverables: deliverables,
        client_name: currentCliente?.nombre || client_context?.client_name || '',
        client_email: currentCliente?.email || client_context?.client_email || '',
        signature_name,
        signed_at: now,
        document_payload: { approval_record_id, proyecto_id: projectId, project_update_id: updateId, presentation_version: version, signed_at: now, signature_name, approved_deliverables: deliverables },
        document_hash: `demo-document-${Date.now()}`,
        audit_hash: `demo-audit-${Date.now()}`,
        is_demo: true,
        created_at: now,
        updated_at: now,
      };
      const cobro = {
        id: crypto.randomUUID?.() || `${Date.now()}-final-40`,
        clienteId: clienteId,
        cliente_id: clienteId,
        proyecto_id: projectId,
        approval_act_id: act.id,
        nombre: 'Saldo final 40%',
        tipo: 'Saldo final 40%',
        monto: Math.round(Math.max(amountBase, 0) * 0.4 * 100) / 100,
        status: 'pending',
        via: 'Stripe',
        payment_stage: 'final_40',
        payment_source: 'approval_act',
        payment_reference: act.id,
        is_demo: true,
        created_at: now,
        updated_at: now,
      };
      const actWithCobro = { ...act, final_payment_cobro_id: cobro.id };
      setData(d => ({
        ...d,
        projectApprovalActs: [actWithCobro, ...(d.projectApprovalActs || [])],
        cobros: [cobro, ...(d.cobros || [])],
        proyectos: (d.proyectos || []).map(p => String(p.id) === String(projectId) ? { ...p, approval_act_signed_at: now, approval_act_id: act.id, production_status: resolveProductionStatus({ ...p, approval_act_signed_at: now }, 'pago_final_pendiente'), updated_at: now } : p),
      }));
      return { ok: true, approval_act: actWithCobro, final_payment_cobro: cobro };
    }

    const { data: result, error } = await supabase.functions.invoke('sign-project-approval-act', {
      body: {
        approval_record_id,
        signature_name,
        accepted,
        client_context,
      },
    });
    if (error || result?.ok === false) {
      const message = result?.error || error?.message || 'No se pudo firmar el acta de aprobacion.';
      console.error('[AppContext.signProjectApprovalAct] Error firmando acta:', error || result);
      throw new Error(message);
    }

    const act = result?.approval_act;
    const cobro = result?.final_payment_cobro;
    const updatedProject = result?.project;

    setData(d => ({
      ...d,
      projectApprovalActs: act ? [act, ...(d.projectApprovalActs || []).filter(x => String(x.id) !== String(act.id))] : d.projectApprovalActs || [],
      cobros: cobro
        ? [{ ...cobro, clienteId: cobro.cliente_id }, ...(d.cobros || []).filter(x => String(x.id) !== String(cobro.id))]
        : d.cobros || [],
      proyectos: updatedProject
        ? (d.proyectos || []).map(p => String(p.id) === String(updatedProject.id) ? { ...p, ...updatedProject, clienteId: updatedProject.cliente_id || p.clienteId, creativo: updatedProject.creativo_id || p.creativo } : p)
        : d.proyectos || [],
    }));

    return result;
  }, [data.projectApprovalRecords, data.projectApprovalActs, data.projectUpdates, data.proyectos, data.clientes, data.projectApplications, demoMode]);

  const signProjectDeliveryAct = useCallback(async ({ proyecto_id, signature_name = '', accepted = false }) => {
    const now = new Date().toISOString();
    const projectId = proyecto_id;
    const currentProject = (data.proyectos || []).find(p => String(p.id) === String(projectId));
    if (!currentProject) throw new Error('No se encontro el proyecto para entrega.');
    if (!accepted) throw new Error('Debes aceptar el documento de entrega.');
    if (!String(signature_name || '').trim()) throw new Error('Escribe tu nombre/firma para aceptar la entrega.');

    const clienteId = currentProject.cliente_id || currentProject.clienteId;
    const currentCliente = (data.clientes || []).find(c => String(c.id) === String(clienteId));
    const alreadySigned = (data.projectDeliveryActs || []).some(act => (
      String(act.proyecto_id || act.proyectoId) === String(projectId) &&
      String(act.status || 'signed') === 'signed'
    ));
    if (alreadySigned || currentProject.delivery_act_signed_at) throw new Error('El documento de entrega ya fue aceptado.');

    const publishedAssets = (data.brandkitAssets || []).filter(asset => (
      String(asset.proyecto_id || asset.proyectoId) === String(projectId) &&
      String(asset.cliente_id || asset.clienteId) === String(clienteId) &&
      asset.status === 'published'
    ));
    // SOLO-DRIVE: el enlace externo de entrega (Drive) permite firmar sin assets de bucket.
    const deliveryUrl = String(currentProject?.brandkit_delivery_url || '').trim();
    if (!publishedAssets.length && !deliveryUrl) throw new Error('No hay assets ni enlace de entrega');

    if (!isConfigured || USE_SEED || demoMode || currentProject?.is_demo || currentCliente?.is_demo) {
      const deliveredAssets = publishedAssets.map(asset => ({
        id: asset.id,
        title: asset.title,
        asset_type: asset.asset_type,
        storage_bucket: asset.storage_bucket,
        storage_path: asset.storage_path,
        mime_type: asset.mime_type,
        file_size: asset.file_size,
        published_at: asset.published_at,
      }));
      // Paridad con la edge function: constancia del enlace externo (entrada sintética + statement).
      if (deliveryUrl) {
        deliveredAssets.push({
          kind: 'external_link', asset_type: 'delivery_link', title: 'Carpeta de entrega (enlace externo)',
          delivery_url: deliveryUrl, delivered_via: 'external_url', storage_bucket: null, storage_path: null, mime_type: 'text/uri-list',
        });
      }
      const externalStatement = 'El CLIENTE declara haber recibido la entrega final del proyecto a través de un enlace de acceso externo proporcionado por el Estudio (por ejemplo, una carpeta compartida de Google Drive) y acepta dicho medio de entrega como válido y suficiente. El CLIENTE reconoce y acepta que el alojamiento, el contenido y la disponibilidad de dicho enlace son administrados por el Estudio y pueden actualizarse, modificarse o dejar de estar disponibles con posterioridad a la firma de la presente acta, sin que ello afecte la validez de la entrega aquí aceptada. La presente acta deja constancia del enlace de entrega vigente al momento de la firma, el cual queda registrado en este documento.';
      const actStatement = deliveryUrl
        ? (publishedAssets.length ? `${externalStatement.slice(0, -1)}, junto con los archivos publicados en el sistema del Estudio, cuyo detalle se registra igualmente en esta acta.` : externalStatement)
        : 'El CLIENTE acepta el documento de entrega final del Brand Kit.';
      const act = {
        id: crypto.randomUUID?.() || `${Date.now()}-delivery-act`,
        cliente_id: clienteId,
        proyecto_id: projectId,
        status: 'signed',
        statement: actStatement,
        delivered_assets: deliveredAssets,
        client_name: currentCliente?.nombre || '',
        client_email: currentCliente?.email || '',
        signature_name,
        signed_at: now,
        document_payload: { proyecto_id: projectId, signed_at: now, signature_name, delivered_assets: deliveredAssets, ...(deliveryUrl ? { delivery: { kind: 'external_url', url: deliveryUrl, mutable: true } } : {}) },
        document_hash: `demo-delivery-document-${Date.now()}`,
        audit_hash: `demo-delivery-audit-${Date.now()}`,
        is_demo: true,
        created_at: now,
        updated_at: now,
      };
      setData(d => ({
        ...d,
        projectDeliveryActs: [act, ...(d.projectDeliveryActs || [])],
        proyectos: (d.proyectos || []).map(p => String(p.id) === String(projectId) ? { ...p, delivery_act_id: act.id, delivery_act_signed_at: now, production_status: resolveProductionStatus({ ...p, delivery_act_signed_at: now }, 'entregado'), updated_at: now } : p),
        brandkitAssets: (d.brandkitAssets || []).map(asset => String(asset.proyecto_id || asset.proyectoId) === String(projectId) && asset.status === 'published' ? { ...asset, delivered_at: now, updated_at: now } : asset),
      }));
      return { ok: true, deliveryAct: act, proyecto: { ...currentProject, delivery_act_id: act.id, delivery_act_signed_at: now, production_status: resolveProductionStatus({ ...currentProject, delivery_act_signed_at: now }, 'entregado') }, assets: publishedAssets.map(asset => ({ ...asset, delivered_at: now })) };
    }

    const { data: result, error } = await supabase.functions.invoke('sign-project-delivery-act', {
      body: { proyecto_id: projectId, signature_name, accepted },
    });
    if (error || result?.ok === false) {
      const message = result?.error || error?.message || 'No se pudo firmar el documento de entrega.';
      console.error('[AppContext.signProjectDeliveryAct] Error firmando entrega:', error || result);
      throw new Error(message);
    }

    const act = result?.deliveryAct;
    const updatedProject = result?.proyecto;
    const updatedAssets = Array.isArray(result?.assets) ? result.assets : [];
    setData(d => ({
      ...d,
      projectDeliveryActs: act ? [act, ...(d.projectDeliveryActs || []).filter(x => String(x.id) !== String(act.id))] : d.projectDeliveryActs || [],
      proyectos: updatedProject
        ? (d.proyectos || []).map(p => String(p.id) === String(updatedProject.id) ? { ...p, ...updatedProject, clienteId: updatedProject.cliente_id || p.clienteId, creativo: updatedProject.creativo_id || p.creativo } : p)
        : d.proyectos || [],
      brandkitAssets: updatedAssets.length
        ? (d.brandkitAssets || []).map(asset => {
            const updated = updatedAssets.find(row => String(row.id) === String(asset.id));
            return updated ? { ...asset, ...updated } : asset;
          })
        : d.brandkitAssets || [],
    }));

    return result;
  }, [data.proyectos, data.clientes, data.projectDeliveryActs, data.brandkitAssets, demoMode]);

  const getBrandkitDownloadUrl = useCallback(async ({ brandkit_asset_id }) => {
    const assetId = brandkit_asset_id;
    const asset = (data.brandkitAssets || []).find(item => String(item.id) === String(assetId));
    if (!asset) throw new Error('No se encontro el asset del Brand Kit.');
    const projectId = asset.proyecto_id || asset.proyectoId;
    const currentProject = (data.proyectos || []).find(p => String(p.id) === String(projectId));
    const currentCliente = (data.clientes || []).find(c => String(c.id) === String(asset.cliente_id || asset.clienteId));

    if (!isConfigured || USE_SEED || demoMode || asset?.is_demo || currentProject?.is_demo || currentCliente?.is_demo) {
      const now = new Date().toISOString();
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000).toISOString();
      const log = {
        id: crypto.randomUUID?.() || `${Date.now()}-brandkit-download`,
        cliente_id: asset.cliente_id || asset.clienteId,
        proyecto_id: projectId,
        brandkit_asset_id: asset.id,
        delivery_act_id: (data.projectDeliveryActs || []).find(act => String(act.proyecto_id || act.proyectoId) === String(projectId))?.id || null,
        storage_bucket: asset.storage_bucket || 'brandkit-assets',
        storage_path: asset.storage_path || '',
        access_granted_at: now,
        signed_url_expires_at: expiresAt,
        is_demo: true,
        created_at: now,
      };
      const updatedAsset = {
        ...asset,
        access_grant_count: Number(asset.access_grant_count || 0) + 1,
        last_access_granted_at: now,
      };
      setData(d => ({
        ...d,
        brandkitDownloadLogs: [log, ...(d.brandkitDownloadLogs || [])],
        brandkitAssets: (d.brandkitAssets || []).map(item => String(item.id) === String(asset.id) ? updatedAsset : item),
        proyectos: (d.proyectos || []).map(p => String(p.id) === String(projectId) ? { ...p, production_status: resolveProductionStatus(p, 'entregado'), updated_at: now } : p),
      }));
      return { ok: true, signedUrl: asset.storage_path || '#', expiresAt, downloadLog: log, asset: updatedAsset, project: { ...currentProject, production_status: resolveProductionStatus(currentProject, 'entregado'), updated_at: now } };
    }

    const { data: result, error } = await supabase.functions.invoke('get-brandkit-download-url', {
      body: { brandkit_asset_id: assetId },
    });
    if (error || result?.ok === false) {
      const message = result?.error || error?.message || 'No se pudo preparar la descarga segura.';
      console.error('[AppContext.getBrandkitDownloadUrl] Error creando signed URL:', error || result);
      throw new Error(message);
    }

    // Conceder acceso a un archivo ya no mueve el ciclo de vida del proyecto:
    // quien entrega es la firma del acta. Por eso aqui ya no llega `project`.
    const log = result?.accessLog;
    const updatedAsset = result?.asset;
    setData(d => ({
      ...d,
      brandkitDownloadLogs: log ? [log, ...(d.brandkitDownloadLogs || [])] : d.brandkitDownloadLogs || [],
      brandkitAssets: updatedAsset
        ? (d.brandkitAssets || []).map(item => String(item.id) === String(updatedAsset.id) ? { ...item, ...updatedAsset } : item)
        : d.brandkitAssets || [],
    }));

    return result;
  }, [data.brandkitAssets, data.proyectos, data.clientes, data.projectDeliveryActs, demoMode]);

  const addProjectApplication = useCallback(async ({ proyectoId, applicationId, isExtra = false, extraPrice = 0, selectedBy = 'admin' }) => {
    const app = applicationById(applicationId);
    const row = {
      id: crypto.randomUUID?.() || Date.now().toString(),
      proyecto_id: proyectoId,
      application_id: applicationId,
      name: app.name,
      category: app.category,
      status: 'pendiente',
      is_extra: Boolean(isExtra),
      // Antes el importe caia en cascada hasta un CERO literal: un extra cuyo precio no
      // resolvia quedaba gratis, y marcado 'pendiente_pago' como si hubiera algo que
      // cobrar. NULL es «no se sabe»; 0 es «no cuesta nada», y no son lo mismo.
      ...(() => {
        const e = precioDeExtraParaPersistir({
          esExtra: Boolean(isExtra),
          precio: precioDeLaFila(
            { ...app, base_price: extraPrice ?? app.base_price ?? app.basePrice },
            { applicationId: applicationId, mercado: 'nacional' },
          ),
        });
        return { extra_price: e.valor, extra_status: e.estado };
      })(),
      selected_by: selectedBy,
      sort_order: (data.projectApplications || []).filter(x => String(x.proyecto_id) === String(proyectoId)).length + 1,
      designer_id: null,
      internal_due_date: null,
      assignment_comments: '',
      assigned_at: null,
      created_at: new Date().toISOString(),
    };
    setData(d => ({ ...d, projectApplications: [row, ...(d.projectApplications || [])] }));
    if (isConfigured) {
      const { data: inserted } = await supabase.from('project_applications').insert([row]).select().single();
      if (inserted) setData(d => ({ ...d, projectApplications: (d.projectApplications || []).map(x => x.id === row.id ? inserted : x) }));
      return inserted || row;
    }
    return row;
  }, [data.projectApplications]);

  const updateProjectApplication = useCallback(async (id, changes) => {
    const normalizedChanges = { ...changes };
    if (Object.prototype.hasOwnProperty.call(changes, 'designer_id')) {
      normalizedChanges.assigned_at = changes.designer_id ? new Date().toISOString() : null;
    }
    // La UI se pinta optimista ANTES de saber si el guardado ocurrió. Se retiene la fila
    // previa para poder devolverla: si el UPDATE toca 0 filas (RLS o registro inexistente),
    // dejar el cambio en pantalla sería exactamente la mentira que este frente corrige.
    // El updater se mantiene idempotente — asigna siempre el mismo valor.
    let filaPrevia = null;
    setData(d => {
      filaPrevia = (d.projectApplications || []).find(x => x.id === id) || null;
      return { ...d, projectApplications: (d.projectApplications || []).map(x => x.id === id ? { ...x, ...normalizedChanges } : x) };
    });

    if (!isConfigured) return { ok: true };

    const resultado = await updatePorId(
      supabase,
      'project_applications',
      id,
      { ...normalizedChanges, updated_at: new Date().toISOString() },
      { queSeGuardaba: 'el entregable' },
    );

    if (!resultado.ok) {
      if (filaPrevia) {
        setData(d => ({ ...d, projectApplications: (d.projectApplications || []).map(x => x.id === id ? filaPrevia : x) }));
      }
      showToast(resultado.error.message, '!');
    }
    return resultado;
  }, [showToast]);

  const updateReunion = useCallback(async (id, changes = {}) => {
    const patch = {
      ...changes,
      title: changes.title ?? changes.titulo,
      duration_minutes: changes.duration_minutes ?? changes.duracion,
      zoom_join_url: changes.zoom_join_url ?? changes.link ?? changes.zoom_link,
      notes: changes.notes ?? changes.notas,
      platform: changes.platform,
      meeting_mode: changes.meeting_mode,
      updated_at: new Date().toISOString(),
    };
    Object.keys(patch).forEach((key) => patch[key] === undefined && delete patch[key]);
    setData(d => ({
      ...d,
      reuniones: (d.reuniones || []).map(r => String(r.id) === String(id) ? { ...r, ...patch } : r),
    }));
    if (isConfigured && id && !String(id).startsWith('demo-')) {
      try {
        const { data: updated, error } = await supabase.from('reuniones').update(patch).eq('id', id).select('*').single();
        if (error) throw error;
        if (updated) {
          setData(d => ({
            ...d,
            reuniones: (d.reuniones || []).map(r => String(r.id) === String(id) ? updated : r),
          }));
        }
      } catch (error) {
        console.warn('[AppContext.updateReunion] No se pudo actualizar reunion:', error?.message || error);
        throw error;
      }
    }
  }, []);

  const upsertReunionLocal = useCallback((reunion) => {
    if (!reunion?.id) return;
    setData(d => ({
      ...d,
      reuniones: (d.reuniones || []).some(r => String(r.id) === String(reunion.id))
        ? (d.reuniones || []).map(r => String(r.id) === String(reunion.id) ? { ...r, ...reunion } : r)
        : [reunion, ...(d.reuniones || [])],
    }));
  }, []);

  const removeReunionLocal = useCallback((id) => {
    setData(d => ({ ...d, reuniones: (d.reuniones || []).filter(r => String(r.id) !== String(id)) }));
  }, []);

  const addProjectChatMessage = useCallback(async ({ proyectoId, message, senderName, senderId, attachmentUrl = '', attachmentBucket = '', attachmentPath = '', attachmentMime = '', clienteId = null, senderEmail = '', source = 'project', etiqueta = 'interno', attachmentName = '', attachmentSize = 0, isDemo = false, conversationId = '', conversationType = '', targetMemberId = null, replyToId = null, reactions = null, pinned = false }) => {
    const mentions = Array.from(String(message || '').matchAll(/@([\wáéíóúÁÉÍÓÚñÑ.-]+)/g)).map(m => m[1]);
    const row = {
      id: crypto.randomUUID?.() || Date.now().toString(),
      proyecto_id: proyectoId || null,
      cliente_id: clienteId || null,
      sender_name: senderName || 'Equipo',
      sender_id: senderId || null,
      sender_email: senderEmail || '',
      message,
      mentions,
      attachment_url: attachmentUrl,
      attachment_name: attachmentName,
      attachment_size: attachmentSize,
      attachment_bucket: attachmentBucket,
      attachment_path: attachmentPath,
      attachment_mime: attachmentMime,
      source,
      etiqueta,
      conversation_id: conversationId || (source === 'general' ? 'general' : proyectoId ? `project:${proyectoId}` : null),
      conversation_type: conversationType || (source === 'direct' ? 'direct' : source === 'general' ? 'general' : 'project'),
      target_member_id: targetMemberId || null,
      reply_to_id: replyToId || null,
      read_by: senderId ? [String(senderId)] : [],
      reactions: reactions || {},
      pinned: Boolean(pinned),
      is_demo: Boolean(isDemo),
      created_at: new Date().toISOString(),
    };
    const isPortalClientMessage = source === 'portal_cliente';
    const isPortalDirectMessage = isPortalClientMessage || source === 'portal_staff';
    const notif = source === 'portal_cliente'
      ? { id: crypto.randomUUID?.() || String(Date.now()) + '-portal-message', tipo:'mensaje_cliente', titulo:'Nuevo mensaje del portal cliente', desc: row.sender_name + ' escribió desde su portal personal.', descripcion: row.sender_name + ' escribió desde su portal personal.', cliente_id: clienteId || null, proyecto_id: proyectoId || null, leida:false, created_at:new Date().toISOString() }
      : (mentions.length ? { id: crypto.randomUUID?.() || String(Date.now()) + '-mention', tipo:'mention', titulo:'Mención en proyecto', desc: row.sender_name + ' mencionó a ' + mentions.join(', '), descripcion: row.sender_name + ' mencionó a ' + mentions.join(', '), leida:false, created_at:new Date().toISOString() } : null);
    setData(d => ({ ...d, projectChatMessages: upsertRowById(d.projectChatMessages, row, { position: 'end', sort: sortOldestFirst }), notificaciones: notif && !isPortalClientMessage ? [notif, ...d.notificaciones] : d.notificaciones }));
    if (isConfigured) {
      try {
        const { data: inserted, error } = await supabase.from('project_chat_messages').insert([row]).select().single();
        if (error) throw error;
        if (isPortalDirectMessage && !inserted?.id) throw new Error('Supabase no confirmo el mensaje del portal.');
        if (inserted?.id) {
          setData(d => ({
            ...d,
            projectChatMessages: upsertRowById(d.projectChatMessages, inserted, { position: 'end', sort: sortOldestFirst }),
          }));
          // 🔴 EL AVISO AL ESTUDIO LO EMITE EL SERVIDOR, NO EL FRONT.
          // El trigger trg_notify_client_portal_message (migracion 20260802205600, ACTIVA en
          // produccion desde el 2026-08-02) inserta la notificacion 'mensaje_cliente' con
          // SECURITY DEFINER al insertarse la fila. Verificado 6/6 contra la BD: destinatario
          // crm/CRM, dedupe_key portal_client_message:<id>, y excluye los mensajes de protocolo
          // que empiezan con doble corchete, los demo y los del staff.
          //
          // Aqui habia un SEGUNDO intento desde el navegador del CLIENTE. Sobraba, y ademas
          // habria necesitado darle permiso de INSERT sobre `notificaciones`: ampliar los
          // permisos del cliente para hacer algo que el servidor ya hace es una regresion de
          // seguridad, no una solucion. **Un hecho, un solo productor causal.**
          //
          // Por eso este metodo tampoco afirma que el equipo quedo avisado: solo responde por
          // haber PERSISTIDO el mensaje, que es lo unico que puede demostrar.
          if (inserted.proyecto_id || proyectoId) refreshProjectReviewData(inserted.proyecto_id || proyectoId);
        }
        // El estado del aviso viaja CON el mensaje: guardar y notificar son dos hechos.
        return { ...(inserted || row), persistido: true };
      } catch (error) {
        if (isPortalDirectMessage) {
          setData(d => ({ ...d, projectChatMessages: removeRowById(d.projectChatMessages, row.id) }));
          console.error('[AppContext.addProjectChatMessage] No se pudo guardar mensaje del portal:', error);
          throw error;
        }
        // Camino INTERNO: no se lanza —un chat interno caido no debe tumbar la pantalla—,
        // pero el fallo deja de ser invisible. Antes este catch estaba vacio y se devolvia
        // la fila optimista, asi que el llamador no tenia forma de saber que no se guardo.
        console.error('[AppContext.addProjectChatMessage] No se pudo guardar mensaje interno:', error);
        return { ...row, persistido: false, persistError: error?.message || String(error) };
      }
    }
    // Sin Supabase no hay persistencia que afirmar.
    return { ...row, persistido: false };
    // `addNotification` sale de las dependencias: el aviso al estudio lo emite el trigger
    // server-side, así que este método ya no notifica nada.
  }, [refreshProjectReviewData]);

  // Pestaña Portal (Mensajes): marca leídos del lado ESTUDIO los mensajes del cliente
  // (source 'portal_cliente') al abrir su hilo. Alimenta el conteo exacto de no-leídos.
  // Optimista siempre; persiste en BD solo si hay Supabase (columna read_by_staff ya
  // existe — migración 012, sin migración nueva).
  const markPortalReadByStaff = useCallback(async (clienteId) => {
    if (!clienteId) return;
    const cid = String(clienteId);
    setData(d => ({
      ...d,
      projectChatMessages: (d.projectChatMessages || []).map(m =>
        (String(m.cliente_id || m.clienteId || '') === cid && m.source === 'portal_cliente' && !m.read_by_staff)
          ? { ...m, read_by_staff: true }
          : m
      ),
    }));
    if (isConfigured) {
      try {
        await supabase.from('project_chat_messages')
          .update({ read_by_staff: true })
          .eq('cliente_id', clienteId)
          .eq('source', 'portal_cliente')
          .eq('read_by_staff', false);
      } catch (e) {
        console.warn('[AppContext.markPortalReadByStaff] no se pudo marcar leido:', e?.message || e);
      }
    }
  }, []);

  const addProjectUpdateComment = useCallback(async ({ updateId, proyectoId, pageRef = '', body }) => {
    const currentProject = (data.proyectos || []).find(p => String(p.id) === String(proyectoId));
    const designerId = currentProject?.creativo_id || currentProject?.creativo || currentProject?.designer_id || null;
    const row = { id: crypto.randomUUID?.() || Date.now().toString(), update_id: updateId, proyecto_id: proyectoId, page_ref: pageRef, body, created_at: new Date().toISOString() };
    setData(d => ({ ...d, projectUpdateComments: upsertRowById(d.projectUpdateComments, row, { position: 'end', sort: sortOldestFirst }) }));
    let persisted = !isConfigured;
    if (isConfigured) {
      try {
        const { data: inserted } = await supabase.from('project_update_comments').insert([row]).select().single();
        if (inserted?.id) {
          setData(d => ({
            ...d,
            projectUpdateComments: upsertRowById(d.projectUpdateComments, inserted, { position: 'end', sort: sortOldestFirst }),
          }));
          refreshProjectReviewData(proyectoId);
        }
        persisted = true;
      } catch (error) {
        // El catch se tragaba el error sin dejar rastro ni avisar en pantalla.
        console.error('[AppContext.addProjectUpdateComment] No se pudo guardar el comentario:', error);
      }
    }
    showToast(
      persisted ? 'Comentario agregado' : 'No se pudo guardar el comentario. Intenta de nuevo.',
      persisted ? '✓' : '!',
    );
    if (persisted) {
      try {
        const notificar = designerId
          ? payload => notifyTeamMember(designerId, payload, 'el comentario del avance')
          : payload => addNotification(payload);
        await notificar({
          tipo: 'project_update_comment',
          titulo: 'Comentario del revisor',
          descripcion: `${currentProject?.nombre || 'Proyecto'} recibio un comentario de revision. Proxima accion: revisar cambios solicitados.`,
          priority: 'medium',
          module: 'proyectos',
          route: '/kanban',
          owner: 'creativo',
          recipient_role: designerId ? null : 'creativo',
          entity_type: 'project_update_comment',
          entity_id: row.id,
          proyecto_id: proyectoId,
          dedupe_key: `project_update_comment:${row.id}`,
          metadata: {
            project_id: proyectoId,
            update_id: updateId,
            next_action: 'Revisar cambios solicitados',
          },
        });
      } catch (error) {
        console.warn('[AppContext.addProjectUpdateComment] No se pudo crear notificacion de comentario:', error?.message || error);
      }
    }
    return row;
  }, [addNotification, notifyTeamMember, data.proyectos, refreshProjectReviewData, showToast]);


  const upsertMarketingPerformance = useCallback(async (perf) => {
    const now = new Date();
    const periodStart = perf.period_start || new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const row = {
      id: perf.id || crypto.randomUUID?.() || Date.now().toString(),
      period_start: periodStart,
      owner_name: perf.owner_name || 'CRM Demo',
      channel: perf.channel || 'Meta Ads',
      brand: perf.brand || 'feria',
      invested_amount: Number(perf.invested_amount || 0),
      converted_amount: Number(perf.converted_amount || 0),
      leads_count: Number(perf.leads_count || 0),
      clients_count: Number(perf.clients_count || 0),
      notes: perf.notes || '',
      is_demo: Boolean(perf.is_demo),
      updated_at: new Date().toISOString(),
    };
    setData(d => {
      const exists = (d.marketingPerformance || []).some(x => String(x.id) === String(row.id));
      return { ...d, marketingPerformance: exists ? d.marketingPerformance.map(x => String(x.id) === String(row.id) ? { ...x, ...row } : x) : [row, ...(d.marketingPerformance || [])] };
    });
    if (isConfigured) {
      try {
        const { data: saved } = await supabase.from('marketing_performance').upsert(row, { onConflict: 'id' }).select().single();
        if (saved) setData(d => ({ ...d, marketingPerformance: (d.marketingPerformance || []).map(x => String(x.id) === String(row.id) ? saved : x) }));
        return saved || row;
      } catch (_) {}
    }
    return row;
  }, []);

  const addMarketingSource = useCallback(async (source = {}) => {
    const now = new Date().toISOString();
    const row = calculateMarketingSourceLocally({
      ...source,
      id: source.id || crypto.randomUUID?.() || Date.now().toString(),
      created_at: source.created_at || now,
      updated_at: now,
      created_by: source.created_by || user?.id || null,
    });

    if (!row.name) throw new Error('Nombre de fuente requerido');

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, marketingSources: [row, ...(d.marketingSources || [])] }));
      return row;
    }

    const payload = filterMarketingSourcePayload(row);
    const { data: inserted, error } = await supabase.from('marketing_sources').insert([payload]).select().single();
    if (error) {
      console.error('[AppContext.addMarketingSource] No se pudo guardar fuente:', { error, payload });
      throw error;
    }

    const saved = inserted || row;
    setData(d => ({ ...d, marketingSources: [saved, ...(d.marketingSources || [])] }));
    return saved;
  }, [demoMode, user?.id]);

  const updateMarketingSource = useCallback(async (id, changes = {}) => {
    const current = (data.marketingSources || []).find(row => String(row.id) === String(id));
    const normalizedChanges = calculateMarketingSourceLocally({
      ...(current || {}),
      ...changes,
      updated_at: new Date().toISOString(),
    });

    if (!normalizedChanges.name) throw new Error('Nombre de fuente requerido');

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, marketingSources: (d.marketingSources || []).map(row => String(row.id) === String(id) ? normalizedChanges : row) }));
      return normalizedChanges;
    }

    const payload = filterMarketingSourcePayload(normalizedChanges);
    delete payload.id;
    delete payload.created_at;
    delete payload.created_by;

    const { data: updated, error } = await supabase.from('marketing_sources').update(payload).eq('id', id).select().single();
    if (error) {
      console.error('[AppContext.updateMarketingSource] No se pudo actualizar fuente:', { error, id, payload });
      throw error;
    }

    const saved = updated || normalizedChanges;
    setData(d => ({ ...d, marketingSources: (d.marketingSources || []).map(row => String(row.id) === String(id) ? saved : row) }));
    return saved;
  }, [data.marketingSources, demoMode]);

  const deleteMarketingSource = useCallback(async (id) => {
    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, marketingSources: (d.marketingSources || []).filter(row => String(row.id) !== String(id)) }));
      return;
    }

    const { error } = await supabase.from('marketing_sources').delete().eq('id', id);
    if (error) {
      console.error('[AppContext.deleteMarketingSource] No se pudo eliminar fuente:', { error, id });
      throw error;
    }

    setData(d => ({ ...d, marketingSources: (d.marketingSources || []).filter(row => String(row.id) !== String(id)) }));
  }, [demoMode]);

  const addMarketingCampaign = useCallback(async (campaign = {}) => {
    const now = new Date().toISOString();
    const row = calculateMarketingCampaignLocally({
      ...campaign,
      id: campaign.id || crypto.randomUUID?.() || Date.now().toString(),
      created_at: campaign.created_at || now,
      updated_at: now,
      created_by: campaign.created_by || user?.id || null,
    });

    if (!row.name) throw new Error('Nombre de campana requerido');

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, marketingCampaigns: [row, ...(d.marketingCampaigns || [])] }));
      return row;
    }

    const payload = filterMarketingCampaignPayload(row);
    const { data: inserted, error } = await supabase.from('marketing_campaigns').insert([payload]).select().single();
    if (error) {
      console.error('[AppContext.addMarketingCampaign] No se pudo guardar campana:', { error, payload });
      throw error;
    }

    const saved = inserted || row;
    setData(d => ({ ...d, marketingCampaigns: [saved, ...(d.marketingCampaigns || [])] }));
    return saved;
  }, [demoMode, user?.id]);

  const updateMarketingCampaign = useCallback(async (id, changes = {}) => {
    const current = (data.marketingCampaigns || []).find(row => String(row.id) === String(id));
    const normalizedChanges = calculateMarketingCampaignLocally({
      ...(current || {}),
      ...changes,
      updated_at: new Date().toISOString(),
    });

    if (!normalizedChanges.name) throw new Error('Nombre de campana requerido');

    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, marketingCampaigns: (d.marketingCampaigns || []).map(row => String(row.id) === String(id) ? normalizedChanges : row) }));
      return normalizedChanges;
    }

    const payload = filterMarketingCampaignPayload(normalizedChanges);
    delete payload.id;
    delete payload.created_at;
    delete payload.created_by;

    const { data: updated, error } = await supabase.from('marketing_campaigns').update(payload).eq('id', id).select().single();
    if (error) {
      console.error('[AppContext.updateMarketingCampaign] No se pudo actualizar campana:', { error, id, payload });
      throw error;
    }

    const saved = updated || normalizedChanges;
    setData(d => ({ ...d, marketingCampaigns: (d.marketingCampaigns || []).map(row => String(row.id) === String(id) ? saved : row) }));
    return saved;
  }, [data.marketingCampaigns, demoMode]);

  const deleteMarketingCampaign = useCallback(async (id) => {
    if (!isConfigured || USE_SEED || demoMode) {
      setData(d => ({ ...d, marketingCampaigns: (d.marketingCampaigns || []).filter(row => String(row.id) !== String(id)) }));
      return;
    }

    const { error } = await supabase.from('marketing_campaigns').delete().eq('id', id);
    if (error) {
      console.error('[AppContext.deleteMarketingCampaign] No se pudo eliminar campana:', { error, id });
      throw error;
    }

    setData(d => ({ ...d, marketingCampaigns: (d.marketingCampaigns || []).filter(row => String(row.id) !== String(id)) }));
  }, [demoMode]);

  const updateMeta = useCallback(async (objetivo) => {
    const previo = data.studio?.meta?.objetivo;
    setData(d => ({ ...d, studio: { ...d.studio, meta: { ...d.studio.meta, objetivo } } }));
    if (!isConfigured) return { ok: true };

    let studioId = studioIdRef.current;
    if (!studioId) {
      // Respaldo: actualizar la única fila existente sin depender del ID.
      const { data: rows } = await supabase.from('studio').select('id').limit(1).single();
      studioId = rows?.id || null;
      if (studioId) studioIdRef.current = studioId;
    }
    if (!studioId) {
      setData(d => ({ ...d, studio: { ...d.studio, meta: { ...d.studio.meta, objetivo: previo } } }));
      return { ok: false, error: { message: 'No se guardó la meta: no se encontró la ficha del estudio.' } };
    }

    // Usa el UUID real capturado al cargar — nunca el literal 1.
    const resultado = await updatePorId(supabase, 'studio', studioId, { meta_objetivo: objetivo }, { queSeGuardaba: 'la meta' });
    if (!resultado.ok) {
      setData(d => ({ ...d, studio: { ...d.studio, meta: { ...d.studio.meta, objetivo: previo } } }));
    }
    return resultado;
  }, [data.studio]);

  const marcarNotifLeida = useCallback(async (id) => {
    const readAt = new Date().toISOString();
    setData(d => ({ ...d, notificaciones: d.notificaciones.map(n => n.id === id ? { ...n, leida: true, read_at: n.read_at || readAt } : n) }));
    if (isConfigured) {
      await supabase.from('notificaciones').update({ leida: true, read_at: readAt }).eq('id', id);
    }
  }, []);

  // Derived
  const getClientId = (row = {}) => row.clienteId || row.cliente_id || row.clientId || row.client_id;
  const getProjectId = (row = {}) => row.proyectoId || row.proyecto_id || row.projectId || row.project_id;
  const matchesBrand = (row = {}) => activeBrand === 'all' || row.brand === activeBrand || row.marca === activeBrand;

  const clientesFiltrados = activeBrand === 'all'
    ? data.clientes
    : data.clientes.filter(c => c.brand === activeBrand);
  const scopedClientIds = new Set(clientesFiltrados.map(c => String(c.id)));

  const proyectosFiltrados = activeBrand === 'all'
    ? data.proyectos
    : data.proyectos.filter(p => matchesBrand(p) || scopedClientIds.has(String(getClientId(p))));
  const scopedProjectIds = new Set(proyectosFiltrados.map(p => String(p.id)));

  const cobrosFiltrados = activeBrand === 'all'
    ? data.cobros
    : data.cobros.filter(c => matchesBrand(c) || scopedClientIds.has(String(getClientId(c))));
  const scopedCobroIds = new Set(cobrosFiltrados.map(c => String(c.id)));
  const ingresosRegistradosFiltrados = activeBrand === 'all'
    ? (data.ingresos || [])
    : (data.ingresos || []).filter(i => scopedClientIds.has(String(getClientId(i))) || scopedProjectIds.has(String(getProjectId(i))) || scopedCobroIds.has(String(i.cobro_id || i.cobroId || '')));
  const suscripcionesFiltradas = data.suscripciones || [];
  const gastosFijosFiltrados = data.gastosFijos || [];
  const flujoConfigFiltrado = data.flujoConfig || [];
  const gastosFiltrados = activeBrand === 'all'
    ? data.gastos
    : data.gastos.filter(g => !g.brand || matchesBrand(g));
  const leadsFiltrados = activeBrand === 'all'
    ? data.leads
    : data.leads.filter(l => matchesBrand(l) || scopedClientIds.has(String(getClientId(l))));
  const reunionesFiltradas = activeBrand === 'all'
    ? (data.reuniones || [])
    : (data.reuniones || []).filter(r => matchesBrand(r) || scopedClientIds.has(String(getClientId(r))));
  const deudasFiltradas = activeBrand === 'all'
    ? (data.deudas || [])
    : (data.deudas || []).filter(d => matchesBrand(d) || scopedClientIds.has(String(getClientId(d))));
  const projectUpdatesFiltrados = activeBrand === 'all'
    ? (data.projectUpdates || [])
    : (data.projectUpdates || []).filter(u => scopedProjectIds.has(String(getProjectId(u))));
  const scopedUpdateIds = new Set(projectUpdatesFiltrados.map(u => String(u.id)));
  const projectApplicationsFiltradas = activeBrand === 'all'
    ? (data.projectApplications || [])
    : (data.projectApplications || []).filter(a => scopedProjectIds.has(String(getProjectId(a))));
  const cotizacionesFiltradas = activeBrand === 'all'
    ? (data.cotizacionesEnviadas || [])
    : (data.cotizacionesEnviadas || []).filter(q => scopedClientIds.has(String(getClientId(q))) || matchesBrand(q));
  const briefsFiltrados = activeBrand === 'all'
    ? (data.briefs || [])
    : (data.briefs || []).filter(brief => scopedClientIds.has(String(getClientId(brief))) || scopedProjectIds.has(String(getProjectId(brief))));
  const scopedContractIds = new Set((data.contratos || []).filter(contract => scopedClientIds.has(String(getClientId(contract)))).map(contract => String(contract.id)));
  const contractParticipantsFiltrados = activeBrand === 'all'
    ? (data.contractParticipants || [])
    : (data.contractParticipants || []).filter(participant => scopedClientIds.has(String(getClientId(participant))) || scopedContractIds.has(String(participant.contrato_id || '')));
  const scopedContractParticipantIds = new Set(contractParticipantsFiltrados.map(participant => String(participant.id)));
  const contractSignaturesFiltradas = activeBrand === 'all'
    ? (data.contractSignatures || [])
    : (data.contractSignatures || []).filter(signature => scopedContractIds.has(String(signature.contrato_id || '')) || scopedContractParticipantIds.has(String(signature.contract_participant_id || '')));
  const scopedBriefIds = new Set(briefsFiltrados.map(brief => String(brief.id)));
  const briefParticipationFiltrada = activeBrand === 'all'
    ? (data.briefParticipation || [])
    : (data.briefParticipation || []).filter(participant => scopedClientIds.has(String(getClientId(participant))) || scopedBriefIds.has(String(participant.brief_id || '')) || scopedProjectIds.has(String(getProjectId(participant))));
  const clientPortalMembersFiltrados = activeBrand === 'all'
    ? (data.clientPortalMembers || [])
    : (data.clientPortalMembers || []).filter(member => scopedClientIds.has(String(getClientId(member))) || scopedProjectIds.has(String(getProjectId(member))));
  const clientResponsibilitiesFiltradas = activeBrand === 'all'
    ? (data.clientResponsibilities || [])
    : (data.clientResponsibilities || []).filter(r => scopedClientIds.has(String(getClientId(r))) || scopedProjectIds.has(String(getProjectId(r))));
  const projectApprovalRecordsFiltrados = activeBrand === 'all'
    ? (data.projectApprovalRecords || [])
    : (data.projectApprovalRecords || []).filter(r => scopedClientIds.has(String(getClientId(r))) || scopedProjectIds.has(String(getProjectId(r))) || scopedUpdateIds.has(String(r.project_update_id || r.projectUpdateId)));
  const projectApprovalActsFiltrados = activeBrand === 'all'
    ? (data.projectApprovalActs || [])
    : (data.projectApprovalActs || []).filter(r => scopedClientIds.has(String(getClientId(r))) || scopedProjectIds.has(String(getProjectId(r))) || scopedUpdateIds.has(String(r.project_update_id || r.projectUpdateId)));
  const projectAdjustmentRequestsFiltrados = activeBrand === 'all'
    ? (data.projectAdjustmentRequests || [])
    : (data.projectAdjustmentRequests || []).filter(r => scopedClientIds.has(String(getClientId(r))) || scopedProjectIds.has(String(getProjectId(r))) || scopedUpdateIds.has(String(r.project_update_id || r.projectUpdateId)));
  const brandkitAssetsFiltrados = activeBrand === 'all'
    ? (data.brandkitAssets || [])
    : (data.brandkitAssets || []).filter(asset => scopedClientIds.has(String(getClientId(asset))) || scopedProjectIds.has(String(getProjectId(asset))));
  const internalClientDocumentsFiltrados = activeBrand === 'all'
    ? (data.internalClientDocuments || [])
    : (data.internalClientDocuments || []).filter(doc => scopedClientIds.has(String(getClientId(doc))) || scopedProjectIds.has(String(getProjectId(doc))));
  const projectDeliveryActsFiltrados = activeBrand === 'all'
    ? (data.projectDeliveryActs || [])
    : (data.projectDeliveryActs || []).filter(act => scopedClientIds.has(String(getClientId(act))) || scopedProjectIds.has(String(getProjectId(act))));
  const brandkitDownloadLogsFiltrados = activeBrand === 'all'
    ? (data.brandkitDownloadLogs || [])
    : (data.brandkitDownloadLogs || []).filter(log => scopedClientIds.has(String(getClientId(log))) || scopedProjectIds.has(String(getProjectId(log))));
  const projectChatMessagesFiltrados = activeBrand === 'all'
    ? (data.projectChatMessages || [])
    : (data.projectChatMessages || []).filter(m => scopedProjectIds.has(String(getProjectId(m))) || scopedClientIds.has(String(getClientId(m))));
  const projectUpdateCommentsFiltrados = activeBrand === 'all'
    ? (data.projectUpdateComments || [])
    : (data.projectUpdateComments || []).filter(c => scopedProjectIds.has(String(getProjectId(c))) || scopedUpdateIds.has(String(c.update_id || c.updateId)));
  const supportTicketsFiltrados = activeBrand === 'all'
    ? (data.supportTickets || [])
    : (data.supportTickets || []).filter(ticket => scopedClientIds.has(String(getClientId(ticket))) || scopedProjectIds.has(String(getProjectId(ticket))));
  const scopedSupportTicketIds = new Set(supportTicketsFiltrados.map(ticket => String(ticket.id)));
  const supportTicketMessagesFiltrados = activeBrand === 'all'
    ? (data.supportTicketMessages || [])
    : (data.supportTicketMessages || []).filter(message => scopedSupportTicketIds.has(String(message.ticket_id || message.ticketId)));
  const clientSatisfactionSurveysFiltradas = activeBrand === 'all'
    ? (data.clientSatisfactionSurveys || [])
    : (data.clientSatisfactionSurveys || []).filter(survey => scopedClientIds.has(String(getClientId(survey))) || scopedProjectIds.has(String(getProjectId(survey))));
  const testimonialRequestsFiltradas = activeBrand === 'all'
    ? (data.testimonialRequests || [])
    : (data.testimonialRequests || []).filter(request => scopedClientIds.has(String(getClientId(request))) || scopedProjectIds.has(String(getProjectId(request))));
  const marketingPerformanceFiltrado = activeBrand === 'all'
    ? (data.marketingPerformance || [])
    : (data.marketingPerformance || []).filter(m => matchesBrand(m));
  const marketingMetaInsightsFiltrados = activeBrand === 'all'
    ? (data.marketingMetaInsights || [])
    : (data.marketingMetaInsights || []).filter(row => {
        const campaign = (data.marketingCampaigns || []).find(c => String(c.id) === String(row.campaign_id) || String(c.meta_campaign_id || '') === String(row.meta_campaign_id || ''));
        return campaign ? matchesBrand(campaign) : matchesBrand(row);
      });
  const marketingSourcesFiltradas = data.marketingSources || [];
  const marketingCampaignsFiltradas = data.marketingCampaigns || [];
  const metaAdsSyncLogsFiltrados = data.metaAdsSyncLogs || [];
  const landingAssetsFiltrados = data.landingAssets || [];
  const portalAssetsFiltrados = data.portalAssets || [];
  const communicationEventsFiltrados = activeBrand === 'all'
    ? (data.communicationEvents || [])
    : (data.communicationEvents || []).filter(event => scopedClientIds.has(String(getClientId(event))) || scopedProjectIds.has(String(getProjectId(event))) || scopedCobroIds.has(String(event.cobro_id || event.cobroId || '')));

  const scopedData = {
    ...data,
    clientes: clientesFiltrados,
    proyectos: proyectosFiltrados,
    cobros: cobrosFiltrados,
    ingresos: ingresosRegistradosFiltrados,
    suscripciones: suscripcionesFiltradas,
    gastosFijos: gastosFijosFiltrados,
    flujoConfig: flujoConfigFiltrado,
    gastos: gastosFiltrados,
    leads: leadsFiltrados,
    reuniones: reunionesFiltradas,
    deudas: deudasFiltradas,
    sueldosProgramados: data.sueldosProgramados || [],
    projectUpdates: projectUpdatesFiltrados,
    projectApplications: projectApplicationsFiltradas,
    cotizacionesEnviadas: cotizacionesFiltradas,
    contractParticipants: contractParticipantsFiltrados,
    contractSignatures: contractSignaturesFiltradas,
    briefs: briefsFiltrados,
    briefParticipation: briefParticipationFiltrada,
    clientPortalMembers: clientPortalMembersFiltrados,
    clientResponsibilities: clientResponsibilitiesFiltradas,
    projectApprovalRecords: projectApprovalRecordsFiltrados,
    projectApprovalActs: projectApprovalActsFiltrados,
    projectAdjustmentRequests: projectAdjustmentRequestsFiltrados,
    brandkitAssets: brandkitAssetsFiltrados,
    internalClientDocuments: internalClientDocumentsFiltrados,
    projectDeliveryActs: projectDeliveryActsFiltrados,
    brandkitDownloadLogs: brandkitDownloadLogsFiltrados,
    projectChatMessages: projectChatMessagesFiltrados,
    projectUpdateComments: projectUpdateCommentsFiltrados,
    supportTickets: supportTicketsFiltrados,
    supportTicketMessages: supportTicketMessagesFiltrados,
    clientSatisfactionSurveys: clientSatisfactionSurveysFiltradas,
    testimonialRequests: testimonialRequestsFiltradas,
    marketingPerformance: marketingPerformanceFiltrado,
    marketingMetaInsights: marketingMetaInsightsFiltrados,
    marketingSources: marketingSourcesFiltradas,
    marketingCampaigns: marketingCampaignsFiltradas,
    metaAdsSyncLogs: metaAdsSyncLogsFiltrados,
    landingAssets: landingAssetsFiltrados,
    studioNews: data.studioNews || [],
    portalAssets: portalAssetsFiltrados,
    communicationEvents: communicationEventsFiltrados,
    allClientes: data.clientes,
    allProyectos: data.proyectos,
    allCobros: data.cobros,
    allIngresos: data.ingresos,
    allSuscripciones: data.suscripciones,
    allGastosFijos: data.gastosFijos,
    allFlujoConfig: data.flujoConfig,
    allCotizacionesEnviadas: data.cotizacionesEnviadas,
    allContratos: data.contratos,
    allContractParticipants: data.contractParticipants,
    allContractSignatures: data.contractSignatures,
    allBriefs: data.briefs,
    allBriefParticipation: data.briefParticipation,
    allClientPortalMembers: data.clientPortalMembers,
    allProjectApplications: data.projectApplications,
    allClientResponsibilities: data.clientResponsibilities,
    allLeads: data.leads,
    allMarketingSources: data.marketingSources,
    allMarketingCampaigns: data.marketingCampaigns,
    allMarketingMetaInsights: data.marketingMetaInsights,
    allLandingAssets: data.landingAssets,
    allPortalAssets: data.portalAssets,
    allSupportTickets: data.supportTickets,
    allSupportTicketMessages: data.supportTicketMessages,
    allClientSatisfactionSurveys: data.clientSatisfactionSurveys,
    allTestimonialRequests: data.testimonialRequests,
  };

  const notifsNoLeidas = data.notificaciones.filter(isNotificationPending).length;
  // Panel de notificaciones: estado lifteado aqui para que la campana (franja del header) dispare
  // el MISMO panel que renderiza Layout.
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  // Backlink de la franja superior persistente del staff: lo registra <BackLink> desde la vista-detalle activa.
  const [stripBackLink, setStripBackLink] = useState(null);

  // ── EL DINERO QUE SE MUESTRA ES EL REAL ─────────────────────────────────
  //
  // MEDIDO el 2026-09-23: 3.933,25 USD de cobros nacidos en pruebas, los cinco
  // pagados, contra 4.071,60 reales. Sumar `cobros` crudos enseñaba ~8.000
  // donde hay ~4.072.
  //
  // El filtro NO se aplica a la carga: `data.cobros` sigue completo porque el
  // portal de un cliente de QA tiene que seguir viendo LO SUYO. Se aplica aquí,
  // que es donde el estudio cuenta dinero, leyendo la etiqueta que calculó la
  // base (399) — no una clasificación hecha en el navegador.
  const cobrosReales = soloOperacionReal(cobrosFiltrados);
  // `ingresos` = cobrado REAL en la plataforma, TODO EL HISTORIAL (no es el ingreso del mes ni el ingreso registrado
  // de Financial Truth; las superficies que hablan de un periodo lo calculan con su periodo).
  const ingresos    = cobrosReales.filter(c => c.status === 'paid').reduce((a, c) => a + (c.monto || 0), 0);
  // POR COBRAR = RECOVER: saldo contractual de ventas REALES (proyecto de cliente real − pagos reales atribuidos).
  // Antes era «todo cobro no pagado», y un checkout CANCELADO (Jorge, 655,50) se presentaba como cuenta por cobrar.
  const porCobrar   = recoverReal(ventasReales({ proyectos: proyectosFiltrados, clientes: clientesFiltrados, cobros: cobrosFiltrados }).ventas).total;
  // GASTO = egreso EJECUTADO. Las 27 líneas de presupuesto importadas el 2026-06-08 (status «recurrente») no son gastos.
  const totalGastos = separarGastos(gastosFiltrados).totalEjecutado;

  // meta.actual siempre refleja los ingresos reales — una sola fuente de verdad
  const metaConIngresos = {
    ...data.studio.meta,
    actual: ingresos,
  };

  return (
    <AppContext.Provider value={{
      data: { ...scopedData, studio: { ...data.studio, meta: metaConIngresos } },
      activeBrand, setActiveBrand,
      toast, showToast,
      theme, toggleTheme,
      accentColor, setAccentColor,
      studioLogo, setStudioLogo,
      studioBrandAssets, setStudioBrandAsset, saveStudioBrandSettings, refreshStudioBrandFromDB, saveStudioPortalSettings, saveStudioArchiveSettings,
      dbReady, isConfigured, demoMode, setDemoMode, loadDemoFlow, clearDemoFlow,
      // Equipo
      addTeamMember, updateTeamMember, deleteTeamMember,
      // Clientes
      updateCliente, advanceClientPhase, addCliente, createClientWithAccess, archiveCliente, restoreCliente, deleteClienteSafely, previewClientDelete, purgeTestClient, deleteCliente,
      // Proyectos
      updateProyecto, addProyecto,
      // Leads
      addLead, updateLead, deleteLead,
      // Gastos
      addGasto, updateGasto, deleteGasto,
      // Deudas
      addDeuda, updateDeuda, deleteDeuda,
      // Sueldos programados
      addSueldoProgramado, updateSueldoProgramado, deleteSueldoProgramado,
      // Cobros
      addCobro, updateCobro, sincronizarCobroTrasAprobacion,
      // Registro de ingresos
      addIngreso, updateIngreso, deleteIngreso,
      // Suscripciones digitales
      addSuscripcion, updateSuscripcion, deleteSuscripcion,
      // Gastos fijos
      addGastoFijo, updateGastoFijo, deleteGastoFijo,
      // Flujo de efectivo
      saveFlujoConfig,
      // Contratos
      firmarContrato, prepareContractForPortal,
      addContractParticipant, updateContractParticipant,
      addBriefParticipation, updateBriefParticipation,
      sendParticipantAccess,
      setupPortalParticipants,
      // Portal cliente
      saveBrief, saveAprobacion,
      // Gestión de producción
      addProjectUpdate, updateProjectUpdate, deleteProjectUpdate, reviewProjectUpdate, publishProjectUpdateToClient, addProjectUpdateComment, refreshProjectReviewData,
      addBrandkitAsset, updateBrandkitAsset, publishBrandkitAsset,
      submitClientPresentationDecision, signProjectApprovalAct, signProjectDeliveryAct, getBrandkitDownloadUrl,
      addProjectApplication, updateProjectApplication, addProjectChatMessage, markPortalReadByStaff,
      // Archivo interno
      uploadArchiveDocument, refreshArchiveDocuments, getArchiveSignedUrl, archiveInternalDocument,
      // Marketing interno
      upsertMarketingPerformance, addMarketingSource, updateMarketingSource, deleteMarketingSource, addMarketingCampaign, updateMarketingCampaign, deleteMarketingCampaign,
      // Landing / cotizacion assets
      addLandingAsset, updateLandingAsset, deleteLandingAsset,
      // Novedades del portal (studio_news)
      addStudioNews, updateStudioNews, deleteStudioNews, reorderStudioNews, reloadStudioNews, studioNewsLoading, studioNewsError, writesAreLocal,
      // Soporte postventa
      refreshSupportTickets, addSupportTicket, updateSupportTicket, addSupportTicketMessage,
      refreshQuotesPipeline,
      addClientSatisfactionSurvey, updateClientSatisfactionSurvey, addTestimonialRequest, updateTestimonialRequest,
      // Reuniones
      updateReunion, upsertReunionLocal, removeReunionLocal,
      // Studio
      updateMeta,
      // Notificaciones
      addNotification, marcarNotifLeida,
      // Derived
      clientesFiltrados,
      notifsNoLeidas, notificationsOpen, setNotificationsOpen, stripBackLink, setStripBackLink,
      ingresos, porCobrar, totalGastos,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
